import React, { useState, useEffect, useCallback } from "react";

// ============================================================
// API CLIENT — all calls go through here
// ============================================================
const API_BASE    = "https://coldfact.up.railway.app";
const SUPABASE_URL  = "https://wmdyapgjerjhrtdvbuxq.supabase.co";
const SUPABASE_ANON = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6IndtZHlhcGdqZXJqaHJ0ZHZidXhxIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzI0ODU5NjIsImV4cCI6MjA4ODA2MTk2Mn0.qSW5P7jrgxZg2WTD3Xc3QdarZz_FOPlOvr3usQAkIp8";

const supabase = {
  auth: {
    signInWithOAuth(provider) {
      const redirectTo = window.location.origin + "/?auth=callback";
      window.location.href = `${SUPABASE_URL}/auth/v1/authorize?provider=${provider}&redirect_to=${encodeURIComponent(redirectTo)}`;
    },
    async signInWithPassword(email, password) {
      const res = await fetch(`${SUPABASE_URL}/auth/v1/token?grant_type=password`, {
        method: "POST",
        headers: { "apikey": SUPABASE_ANON, "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      return res.json();
    },
    async signUp(email, password) {
      const res = await fetch(`${SUPABASE_URL}/auth/v1/signup`, {
        method: "POST",
        headers: { "apikey": SUPABASE_ANON, "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      return res.json();
    },
    async signOut() {
      const token = localStorage.getItem("vs_token");
      await fetch(`${SUPABASE_URL}/auth/v1/logout`, {
        method: "POST",
        headers: { "apikey": SUPABASE_ANON, "Authorization": `Bearer ${token}` },
      }).catch(() => {});
      localStorage.removeItem("vs_token");
      localStorage.removeItem("vs_user");
      window.location.reload();
    },
  }
};

async function apiFetch(path, options = {}) {
  const token = localStorage.getItem("vs_token");
  const res = await fetch(`${API_BASE}${path}`, {
    headers: {
      "Content-Type": "application/json",
      ...(token ? { "Authorization": `Bearer ${token}` } : {}),
      ...options.headers,
    },
    ...options,
  });
  if (res.status === 401) {
    localStorage.removeItem("vs_token");
    localStorage.removeItem("vs_user");
    window.location.href = "/";
    throw new Error("Session expired — please sign in again.");
  }
  if (!res.ok) throw new Error(`API error ${res.status} on ${path}`);
  return res.json();
}

// ============================================================
// HOOKS
// ============================================================
function useSubscription(userId) {
  const [sub, setSub] = useState(null);
  const [subLoading, setSubLoading] = useState(true);
  useEffect(() => {
    if (!userId) return;
    setSubLoading(true);
    apiFetch("/subscription")
      .then(d => { setSub(d); setSubLoading(false); })
      .catch(() => { setSub({ tier: "free", features: {} }); setSubLoading(false); });
  }, [userId]);
  return { sub, subLoading };
}

function UpgradeModal({ tier, onClose }) {
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);
  const [cycle, setCycle]     = useState("monthly"); // monthly | annual

  const tiers = [
    {
      id:    "reader",
      label: "READER",
      monthly_price: "$10/mo",
      annual_price:  "$90/yr",
      saving:        "2 months free",
      desc:  "20 scans/day, full VCI breakdown, author & outlet scoring, 3 gifts/week",
    },
    {
      id:    "analyst",
      label: "ANALYST",
      monthly_price: "$29/mo",
      annual_price:  "$290/yr",
      saving:        "2 months free",
      desc:  "100 scans/day, full Layer 4 detail, Narrative Intelligence Reports, PDF export",
      highlight: true,
    },
    {
      id:    "organization",
      label: "ORGANIZATION",
      monthly_price: "$299/mo",
      annual_price:  "Contact us",
      saving:        "Annual via invoice",
      desc:  "10 seats, API access, branded PDFs, unlimited NIRs, priority support",
    },
  ];

  const checkout = async (tierId) => {
    // Org annual — redirect to email
    if (tierId === "organization" && cycle === "annual") {
      window.location.href = "mailto:founders@verdadscore.com?subject=Organization Annual Plan&body=Hi, I'd like to arrange annual billing for the Organization plan.";
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch("/subscription/checkout", {
        method: "POST",
        body: JSON.stringify({ tier: tierId, cycle }),
      });
      if (res.manual) {
        window.location.href = "mailto:founders@verdadscore.com?subject=Organization Annual Plan&body=Hi, I'd like to arrange annual billing for the Organization plan.";
        return;
      }
      if (res.checkout_url) {
        window.location.href = res.checkout_url;
      } else {
        setError("Could not create checkout. Please try again.");
      }
    } catch(e) {
      setError("Checkout failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.8)",
      display: "flex", alignItems: "center", justifyContent: "center",
      zIndex: 1000, padding: 16,
    }}>
      <div style={{
        background: "#0D1521", border: "1px solid #1E293B", borderRadius: 10,
        padding: 28, width: "100%", maxWidth: 480, position: "relative",
      }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center",
          justifyContent: "space-between", marginBottom: 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <img src="/verdadscore_icon.svg" alt="" style={{ height: 28 }} />
            <div>
              <Mono style={{ fontSize: 9, color: "#3B82F6", letterSpacing: "0.15em",
                display: "block", marginBottom: 2 }}>VERDAD SCORE</Mono>
              <div style={{ fontSize: 20, fontWeight: 700, color: "#E2E8F0" }}>
                Upgrade Your Plan
              </div>
            </div>
          </div>
          <button onClick={onClose} style={{ background: "transparent",
            border: "none", color: "#64748B", fontSize: 20, cursor: "pointer" }}>✕</button>
        </div>

        {/* Monthly / Annual toggle */}
        <div style={{ display: "flex", alignItems: "center", gap: 8,
          marginBottom: 18, background: "#111827", borderRadius: 6,
          padding: "4px", width: "fit-content" }}>
          {["monthly", "annual"].map(c => (
            <button key={c} onClick={() => setCycle(c)} style={{
              background: cycle === c ? "#3B82F6" : "transparent",
              border: "none", borderRadius: 4, padding: "5px 14px",
              color: cycle === c ? "#fff" : "#64748B",
              fontFamily: "'DM Mono', monospace", fontSize: 10,
              cursor: "pointer", textTransform: "uppercase",
              letterSpacing: "0.08em", transition: "all 0.15s",
            }}>{c}</button>
          ))}
          {cycle === "annual" && (
            <Mono style={{ fontSize: 9, color: "#22C55E", marginLeft: 4 }}>
              2 MONTHS FREE
            </Mono>
          )}
        </div>

        {/* Tier cards */}
        {tiers.map(t => (
          <div key={t.id} style={{
            padding: "16px 20px", marginBottom: 10, borderRadius: 6,
            background: t.highlight ? "#0F1E35" : "#111827",
            border: `1px solid ${t.highlight ? "#3B82F6" : "#1E293B"}`,
            display: "flex", justifyContent: "space-between", alignItems: "center",
          }}>
            <div style={{ flex: 1 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                <Mono style={{ fontSize: 9, color: "#3B82F6", letterSpacing: "0.1em" }}>
                  {t.label}
                </Mono>
                {t.highlight && (
                  <Mono style={{ fontSize: 8, color: "#22C55E", background: "#052e16",
                    padding: "2px 6px", borderRadius: 3 }}>MOST POPULAR</Mono>
                )}
              </div>
              <div style={{ fontSize: 12, color: "#CBD5E1", lineHeight: 1.4 }}>{t.desc}</div>
            </div>
            <div style={{ textAlign: "right", flexShrink: 0, marginLeft: 16 }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: "#E2E8F0", marginBottom: 2 }}>
                {cycle === "annual" ? t.annual_price : t.monthly_price}
              </div>
              {cycle === "annual" && t.id !== "organization" && (
                <Mono style={{ fontSize: 8, color: "#22C55E", display: "block", marginBottom: 6 }}>
                  {t.saving}
                </Mono>
              )}
              <button
                onClick={() => checkout(t.id)}
                disabled={loading || tier === t.id}
                style={{
                  background: tier === t.id ? "#1E293B" : "#3B82F6",
                  border: "none", color: tier === t.id ? "#475569" : "#fff",
                  borderRadius: 4, padding: "6px 14px", cursor: tier === t.id ? "default" : "pointer",
                  fontFamily: "'DM Mono', monospace", fontSize: 9,
                  opacity: loading ? 0.6 : 1,
                }}>
                {tier === t.id ? "CURRENT" : t.id === "organization" && cycle === "annual" ? "CONTACT US" : "SUBSCRIBE"}
              </button>
            </div>
          </div>
        ))}

        {error && (
          <div style={{ color: "#EF4444", fontSize: 12, marginTop: 8, textAlign: "center" }}>
            {error}
          </div>
        )}

        {/* Footer */}
        <Mono style={{ fontSize: 9, color: "#475569", display: "block",
          marginTop: 16, textAlign: "center" }}>
          Powered by Mercado Pago · Cancel anytime · verdadscore.com
        </Mono>
        <Mono style={{ fontSize: 8, color: "#334155", display: "block",
          marginTop: 4, textAlign: "center" }}>
          Prices in USD · Charged in ARS · Your bank rate may vary slightly
        </Mono>
      </div>
    </div>
  );
}

function useAPI(path, deps = []) {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  const fetch_ = useCallback(async () => {
    if (!path) return;
    setLoading(true);
    setError(null);
    try {
      const d = await apiFetch(path);
      setData(d);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [path, ...deps]);

  useEffect(() => { fetch_(); }, [fetch_]);
  return { data, loading, error, refetch: fetch_ };
}

// ============================================================
// DESIGN TOKENS
// ============================================================
const T = {
  bg:       "#060C17",
  surface:  "#0D1521",
  surfaceAlt:"#080E1A",
  border:   "#1E293B",
  borderHi: "#334155",
  text:     "#E2E8F0",
  muted:    "#475569",
  faint:    "#1E293B",
  blue:     "#3B82F6",
  green:    "#22C55E",
  yellow:   "#F59E0B",
  red:      "#EF4444",
  purple:   "#8B5CF6",
};

const SOURCE_TYPE_LABELS = {
  "GOVERNMENT":        "GOVT OFFICIAL",
  "OFFICIAL":          "GOVT OFFICIAL",
  "EXPERT":            "NAMED EXPERT",
  "ANONYMOUS":         "ANONYMOUS SOURCE",
  "STATE_MEDIA":       "STATE MEDIA",
  "SOCIAL_MEDIA":      "SOCIAL MEDIA",
  "INTERNATIONAL_ORG": "INTL BODY",
  "MILITARY":          "MILITARY",
  "ADVOCACY_ORG":      "ADVOCACY GROUP",
  "OTHER":             "OTHER SOURCE",
};

const CRED_LABELS = {
  "HIGH":    "VERIFIED",
  "MEDIUM":  "UNVERIFIED",
  "LOW":     "DISPUTED",
  "UNKNOWN": "UNKNOWN",
};

const statusColor = (s) =>
  s === "Reliable" ? T.green : s === "At Risk" ? T.yellow : T.red;
const riskColor   = (r) =>
  r === "HIGH" ? T.red : r === "MEDIUM" ? T.yellow : T.green;
const vciColor    = (v) =>
  v >= 85 ? T.green : v >= 65 ? T.yellow : T.red;

// ============================================================
// SHARED UI COMPONENTS
// ============================================================
function Card({ children, style = {}, onClick }) {
  return (
    <div onClick={onClick} style={{
      background: T.surface, border: `1px solid ${T.border}`,
      borderRadius: 8, transition: "border-color 0.2s",
      maxWidth: "100%", boxSizing: "border-box",
      cursor: onClick ? "pointer" : "default", ...style,
    }}
    onMouseEnter={e => onClick && (e.currentTarget.style.borderColor = T.borderHi)}
    onMouseLeave={e => onClick && (e.currentTarget.style.borderColor = T.border)}>
      {children}
    </div>
  );
}

function ShareModal({ shareUrl, onClose }) {
  if (!shareUrl) return null;
  const [copied, setCopied] = React.useState(false);

  const copyLink = () => {
    const doCopy = () => {
      try {
        if (navigator.clipboard && window.isSecureContext) {
          navigator.clipboard.writeText(shareUrl).catch(() => {
            const el = document.createElement("textarea");
            el.value = shareUrl; document.body.appendChild(el);
            el.select(); document.execCommand("copy");
            document.body.removeChild(el);
          });
        } else {
          const el = document.createElement("textarea");
          el.value = shareUrl; document.body.appendChild(el);
          el.select(); document.execCommand("copy");
          document.body.removeChild(el);
        }
      } catch(e) {}
    };
    doCopy();
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)",
      display: "flex", alignItems: "center", justifyContent: "center",
      zIndex: 1000,
    }} onClick={onClose}>
      <div style={{
        background: T.surface, border: `1px solid ${T.border}`,
        borderRadius: 10, padding: 24, width: 360, maxWidth: "90vw",
      }} onClick={e => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between",
          alignItems: "center", marginBottom: 20 }}>
          <Mono style={{ fontSize: 11, color: T.text, letterSpacing: "0.1em" }}>SHARE</Mono>
          <button onClick={onClose} style={{ background: "transparent",
            border: "none", color: T.muted, cursor: "pointer", fontSize: 18 }}>×</button>
        </div>
        <div style={{ display: "flex", gap: 12, justifyContent: "center",
          marginBottom: 20, flexWrap: "wrap" }}>
          {[
            { label: "WhatsApp", color: "#25D366", icon: "💬",
              url: `https://wa.me/?text=${encodeURIComponent("Check this credibility report: " + shareUrl)}` },
            { label: "X", color: "#000000", icon: "𝕏",
              url: `https://x.com/intent/tweet?text=${encodeURIComponent("Credibility report via @VerdadScore")}&url=${encodeURIComponent(shareUrl)}` },
            { label: "Facebook", color: "#1877F2", icon: "f",
              url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}` },
            { label: "LinkedIn", color: "#0A66C2", icon: "in",
              url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}` },
            { label: "Reddit", color: "#FF4500", icon: "👽",
              url: `https://www.reddit.com/submit?url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent("Credibility Report — Verdad Score")}` },
            { label: "Email", color: "#64748B", icon: "✉",
              url: `mailto:?subject=${encodeURIComponent("Credibility Report — Verdad Score")}&body=${encodeURIComponent("Check this credibility report: " + shareUrl)}` },
          ].map(({ label, color, icon, url }) => (
            <a key={label} href={url} target="_blank" rel="noopener noreferrer"
              style={{ display: "flex", flexDirection: "column",
                alignItems: "center", gap: 6, textDecoration: "none" }}>
              <div style={{ width: 48, height: 48, borderRadius: 24,
                background: color, display: "flex", alignItems: "center",
                justifyContent: "center", fontSize: 18, color: "#fff",
                fontWeight: 700 }}>{icon}</div>
              <Mono style={{ fontSize: 8, color: T.muted }}>{label.toUpperCase()}</Mono>
            </a>
          ))}
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center",
          background: T.surfaceAlt, border: `1px solid ${T.border}`,
          borderRadius: 6, padding: "8px 12px" }}>
          <Mono style={{ fontSize: 10, color: T.muted, flex: 1,
            overflow: "hidden", textOverflow: "ellipsis",
            whiteSpace: "nowrap" }}>{shareUrl}</Mono>
          <button onClick={copyLink}
            style={{ background: copied ? T.green : T.blue, border: "none", color: "#fff",
              borderRadius: 4, padding: "4px 12px", cursor: "pointer",
              fontFamily: "'DM Mono'", fontSize: 9, transition: "background 0.2s",
              whiteSpace: "nowrap" }}>{copied ? "✓ COPIED" : "COPY"}</button>
        </div>
      </div>
    </div>
  );
}

function useShare() {
  const [shareUrl, setShareUrl] = React.useState(null);
  const [sharing, setSharing]   = React.useState(false);

  const share = async ({ vci_score, verdict, outlet, title, summary, result_json, name, score, type }) => {
    setSharing(true);
    try {
      const res = await fetch(`${API_BASE}/share`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          vci_score:   Math.round(vci_score || score || 0),
          verdict:     verdict || null,
          outlet:      outlet || null,
          title:       title || name || null,
          summary:     summary || null,
          result_json: result_json || null,
        }),
      });
      const data = await res.json();
      setShareUrl(data.url);
      navigator.clipboard.writeText(data.url).catch(() => {});
    } catch(e) { console.error("Share failed", e); }
    setSharing(false);
  };

  return { shareUrl, setShareUrl, sharing, share };
}

function Mono({ children, style = {} }) {
  return <span style={{ fontFamily: "'DM Mono', monospace", ...style }}>{children}</span>;
}

function Badge({ status }) {
  const c = statusColor(status);
  return (
    <Mono style={{
      fontSize: 9, fontWeight: 700, letterSpacing: "0.08em",
      color: c, background: `${c}18`, border: `1px solid ${c}40`,
      borderRadius: 4, padding: "3px 8px", whiteSpace: "nowrap",
    }}>{status?.toUpperCase()}</Mono>
  );
}

function ScoreRing({ score, size = 72 }) {
  const r = size / 2 - 7;
  const circ = 2 * Math.PI * r;
  const offset = circ - ((score || 0) / 100) * circ;
  const c = vciColor(score || 0);
  return (
    <svg width={size} height={size} style={{ transform: "rotate(-90deg)", flexShrink: 0 }}>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#ffffff08" strokeWidth={5} />
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={c} strokeWidth={5}
        strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round"
        style={{ transition: "stroke-dashoffset 1s ease" }} />
      <text x={size/2} y={size/2} textAnchor="middle" dominantBaseline="central"
        fill={c} fontSize={size / 4.5} fontWeight="800"
        fontFamily="'DM Mono', monospace"
        style={{ transform: "rotate(90deg)", transformOrigin: `${size/2}px ${size/2}px` }}>
        {score ?? "—"}
      </text>
    </svg>
  );
}

function Spinner() {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center",
      gap: 8, padding: "40px 0", color: T.muted }}>
      <div style={{
        width: 16, height: 16, borderRadius: "50%",
        border: `2px solid ${T.border}`, borderTopColor: T.blue,
        animation: "spin 0.8s linear infinite",
      }} />
      <Mono style={{ fontSize: 11, color: T.muted }}>LOADING…</Mono>
    </div>
  );
}

function ErrorBox({ message, onRetry }) {
  return (
    <div style={{ background: `${T.red}10`, border: `1px solid ${T.red}30`,
      borderRadius: 6, padding: "14px 18px", margin: "12px 0" }}>
      <Mono style={{ fontSize: 10, color: T.red, display: "block", marginBottom: 6 }}>
        API ERROR
      </Mono>
      <p style={{ margin: "0 0 10px", fontSize: 12, color: "#94a3b8" }}>{message}</p>
      {onRetry && (
        <button onClick={onRetry} style={{
          background: `${T.red}20`, border: `1px solid ${T.red}40`,
          color: T.red, borderRadius: 4, padding: "4px 12px",
          cursor: "pointer", fontSize: 11, fontFamily: "'DM Mono', monospace",
        }}>RETRY</button>
      )}
    </div>
  );
}


// ============================================================
// TOOLTIP SYSTEM
// ============================================================
const TOOLTIPS = {
  VCI:      "Veracity Credibility Index: overall credibility score (0–100). Green ≥82 Reliable · Amber ≥60 At Risk · Red <60 Blacklisted.",
  FI:       "Factual Integrity: how factually accurate the reporting appears based on source quality and corroboration (weight: 60%).",
  EI:       "Editorial Independence: how free the article is from bias, emotional framing, and one-sided narratives (weight: 40%).",
  OCI:      "Outlet Credibility Index: the outlet's track record score, built from all their article scores over time.",
  ACI:      "Author Credibility Index: this author's credibility relative to their outlet's average. Rewards consistency, penalises anomalies.",
  SCI:      "Source Credibility Index: how credible a cited entity is, based on how reliably it is referenced across outlets.",
  BASE_VCI: "The outlet's OCI score used as the starting point before article-level adjustments are applied.",
  DIVERGENCE: "How much outlets disagree on this story. High divergence means outlets are telling very different versions.",
  PCI:      "Publication Credibility Index: per-article score combining Factual Integrity (60%) and Editorial Independence (40%).",
};

function InfoIcon({ term, style = {} }) {
  const [show, setShow] = useState(false);
  const text = TOOLTIPS[term] || term;
  return (
    <span style={{ position: "relative", display: "inline-flex",
      alignItems: "center", ...style }}>
      <span
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
        style={{
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          width: 13, height: 13, borderRadius: "50%",
          border: "1px solid #334155", color: "#64748B",
          fontSize: 8, cursor: "help", marginLeft: 4,
          fontFamily: "'DM Mono', monospace", lineHeight: 1,
          userSelect: "none", flexShrink: 0,
        }}>?</span>
      {show && (
        <span style={{
          position: "absolute", bottom: "calc(100% + 6px)", left: "50%",
          transform: "translateX(-50%)",
          background: "#0f172a", border: "1px solid #334155",
          borderRadius: 5, padding: "8px 10px",
          fontSize: 10, color: "#CBD5E1", lineHeight: 1.5,
          whiteSpace: "normal", width: 220, zIndex: 500,
          boxShadow: "0 4px 20px rgba(0,0,0,0.5)",
          fontFamily: "'DM Sans', sans-serif", fontWeight: 400,
          pointerEvents: "none",
        }}>{text}</span>
      )}
    </span>
  );
}

function SectionLabel({ children }) {
  return (
    <Mono style={{ fontSize: 9, color: T.muted, letterSpacing: "0.12em",
      display: "block", marginBottom: 14 }}>{children}</Mono>
  );
}

// ============================================================
// WORLD MAP
// ============================================================
function WorldMap({ hotspots = [] }) {
  const [tooltip, setTooltip] = useState(null);
  const project = (lat, lon) => ({
    x: ((lon + 180) / 360) * 800,
    y: ((90 - lat)  / 180) * 400,
  });

  return (
    <div style={{ background: T.surfaceAlt, borderRadius: 8,
      overflow: "hidden", border: `1px solid ${T.border}` }}>
      <svg viewBox="0 0 800 400" style={{ width: "100%", display: "block" }}>
        <rect width="800" height="400" fill="#0d1521" />
        {[0,1,2,3,4].map(i => (
          <line key={`v${i}`} x1={i*200} y1={0} x2={i*200} y2={400}
            stroke={T.border} strokeWidth={0.5} />
        ))}
        {[0,1,2,3].map(i => (
          <line key={`h${i}`} x1={0} y1={i*100} x2={800} y2={i*100}
            stroke={T.border} strokeWidth={0.5} />
        ))}
        {/* Continent shapes */}
        <path d="M60 120 Q80 100 120 110 Q140 115 145 130 Q150 150 130 160 Q100 170 70 155 Z" fill="#1a2744" />
        <path d="M150 150 Q180 130 210 140 Q240 148 250 165 Q255 185 230 195 Q200 200 175 190 Q155 175 150 150 Z" fill="#1a2744" />
        <path d="M310 80 Q360 60 420 65 Q460 68 480 90 Q500 115 490 145 Q475 170 440 178 Q400 182 365 170 Q330 155 315 130 Q305 108 310 80 Z" fill="#1a2744" />
        <path d="M490 65 Q530 50 580 55 Q640 58 680 75 Q720 95 730 130 Q738 160 715 180 Q685 195 640 195 Q590 193 555 175 Q515 155 500 125 Q488 98 490 65 Z" fill="#1a2744" />
        <path d="M220 240 Q260 225 295 235 Q320 243 330 265 Q338 285 320 300 Q295 315 265 310 Q235 302 222 280 Q215 260 220 240 Z" fill="#1a2744" />
        <path d="M155 220 Q175 205 200 210 Q218 215 225 232 Q230 250 215 262 Q195 272 175 265 Q157 255 153 238 Z" fill="#1a2744" />
        <path d="M640 215 Q680 210 710 225 Q730 236 735 255 Q738 272 720 280 Q695 288 668 282 Q642 273 635 253 Q630 232 640 215 Z" fill="#1a2744" />

        {hotspots.map((h, i) => {
          const pos = project(h.lat, h.lon);
          const c   = vciColor(h.avg_vci);
          const r   = Math.max(6, Math.min(18, h.severity || 8));
          return (
            <g key={i}
              onMouseEnter={() => setTooltip({ ...h, px: pos.x, py: pos.y })}
              onMouseLeave={() => setTooltip(null)}
              style={{ cursor: "pointer" }}>
              <circle cx={pos.x} cy={pos.y} r={r + 6} fill={c} opacity={0.08} />
              <circle cx={pos.x} cy={pos.y} r={r} fill={c} opacity={0.25} />
              <circle cx={pos.x} cy={pos.y} r={r * 0.55} fill={c} opacity={0.9} />
            </g>
          );
        })}

        {tooltip && (() => {
          const px = Math.min(tooltip.px, 620);
          const py = Math.max(tooltip.py - 70, 5);
          const c  = vciColor(tooltip.avg_vci);
          return (
            <g>
              <rect x={px-4} y={py} width={170} height={60} rx={4}
                fill="#0f172a" stroke={T.border} strokeWidth={1} />
              <text x={px+4} y={py+14} fill={T.text} fontSize={10}
                fontWeight="700" fontFamily="DM Mono,monospace">{tooltip.country_name}</text>
              <text x={px+4} y={py+27} fill={T.muted} fontSize={8}
                fontFamily="DM Mono,monospace">{tooltip.top_incident}</text>
              <text x={px+4} y={py+42} fill={c} fontSize={12}
                fontWeight="800" fontFamily="DM Mono,monospace">VCI {tooltip.avg_vci}</text>
              <text x={px+80} y={py+42} fill={T.red} fontSize={9}
                fontFamily="DM Mono,monospace">
                {tooltip.total_fines > 0 ? `-$${tooltip.total_fines.toLocaleString()}` : ""}
              </text>
            </g>
          );
        })()}
      </svg>
      <div style={{ display: "flex", gap: 16, padding: "8px 16px",
        borderTop: `1px solid ${T.border}` }}>
        {[["Reliable (85+)", T.green], ["At Risk (60–84)", T.yellow],
          ["Blacklisted (<60)", T.red]].map(([l, c]) => (
          <div key={l} style={{ display: "flex", alignItems: "center", gap: 5 }}>
            <div style={{ width: 7, height: 7, borderRadius: "50%", background: c }} />
            <Mono style={{ fontSize: 9, color: T.muted }}>{l}</Mono>
          </div>
        ))}
        <Mono style={{ fontSize: 9, color: T.muted, marginLeft: "auto" }}>
          {hotspots.length} countries monitored
        </Mono>
      </div>
    </div>
  );
}


// ============================================================
// REVIEW QUEUE TAB
// ============================================================

function WatchlistTab() {
  const [watchlist, setWatchlist] = useState([]);
  const [alerts, setAlerts]       = useState([]);
  const [unread, setUnread]       = useState(0);
  const [loading, setLoading]     = useState(true);
  const [adding, setAdding]       = useState(false);
  const [form, setForm]           = useState({
    watch_type: "outlet", watch_value: "", threshold: 65, critical_only: false
  });
  const [activeTab, setActiveTab] = useState("alerts");

  const load = () => {
    setLoading(true);
    Promise.all([
      apiFetch("/watchlist"),
      apiFetch("/alerts?limit=100"),
    ]).then(([wl, al]) => {
      setWatchlist(wl.watchlist || []);
      setAlerts(al.alerts || []);
      setUnread(al.unread_count || 0);
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const addWatch = async () => {
    if (!form.watch_value.trim()) return;
    setAdding(true);
    try {
      await apiFetch("/watchlist", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      setForm({ watch_type: "outlet", watch_value: "", threshold: 65, critical_only: false });
      load();
    } catch(e) {}
    setAdding(false);
  };

  const removeWatch = async (watch_type, watch_value) => {
    await apiFetch(`/watchlist?watch_type=${watch_type}&watch_value=${encodeURIComponent(watch_value)}`, {
      method: "DELETE"
    });
    load();
  };

  const markAllRead = async () => {
    await apiFetch("/alerts/mark-read", { method: "POST" });
    load();
  };

  const typeColor = t => ({
    outlet: T.blue, author: T.green, keyword: T.yellow, entity: T.red
  })[t] || T.muted;

  const vciColor = s => s >= 82 ? T.green : s >= 60 ? T.yellow : T.red;

  return (
    <div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 12, marginBottom: 20 }}>
        <SectionLabel>WATCHLIST & ALERTS</SectionLabel>
        {unread > 0 && (
          <div style={{
            background: T.red, color: "#fff", borderRadius: 10,
            padding: "2px 8px", fontSize: 10,
            fontFamily: "'DM Mono', monospace", fontWeight: 700,
          }}>{unread} NEW</div>
        )}
      </div>

      {/* Sub-tabs */}
      <div style={{ display: "flex", gap: 0, borderBottom: `1px solid ${T.border}`,
        marginBottom: 20 }}>
        {[["alerts", `ALERTS${unread > 0 ? ` (${unread})` : ""}`], ["watchlist", "WATCHLIST"], ["add", "+ ADD"]].map(([key, label]) => (
          <button key={key} onClick={() => setActiveTab(key)} style={{
            background: "transparent",
            borderBottom: activeTab === key ? `2px solid ${T.blue}` : "2px solid transparent",
            border: "none",
            borderBottom: activeTab === key ? `2px solid ${T.blue}` : "2px solid transparent",
            color: activeTab === key ? T.text : T.muted,
            padding: "10px 18px", cursor: "pointer",
            fontFamily: "'DM Mono', monospace", fontSize: 9,
            letterSpacing: "0.08em",
          }}>{label}</button>
        ))}
      </div>

      {loading ? <Spinner /> : (
        <>
          {/* ALERTS TAB */}
          {activeTab === "alerts" && (
            <div>
              {alerts.length > 0 && unread > 0 && (
                <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
                  <button onClick={markAllRead} style={{
                    background: T.surface, border: `1px solid ${T.border}`,
                    color: T.muted, borderRadius: 4, padding: "6px 12px",
                    cursor: "pointer", fontFamily: "'DM Mono', monospace", fontSize: 9,
                  }}>MARK ALL READ</button>
                </div>
              )}
              {alerts.length === 0 ? (
                <Card style={{ padding: 40, textAlign: "center" }}>
                  <Mono style={{ fontSize: 11, color: T.muted }}>NO ALERTS YET</Mono>
                  <Mono style={{ fontSize: 10, color: T.muted, display: "block", marginTop: 8 }}>
                    Alerts are generated after each scraper run (~every 6 hours).
                  </Mono>
                </Card>
              ) : alerts.map((a, i) => (
                <div key={a.id} style={{
                  padding: "14px 18px", marginBottom: 8, borderRadius: 6,
                  background: a.is_read ? T.surface : `${T.blue}08`,
                  border: `1px solid ${a.is_critical ? T.red : a.is_read ? T.border : T.blue}`,
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between",
                    alignItems: "flex-start", gap: 12 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: "flex", gap: 8, alignItems: "center",
                        marginBottom: 4, flexWrap: "wrap" }}>
                        {a.is_critical && (
                          <Mono style={{ fontSize: 8, color: T.red,
                            background: `${T.red}15`, padding: "2px 6px",
                            borderRadius: 2 }}>CRITICAL</Mono>
                        )}
                        <Mono style={{ fontSize: 8,
                          color: typeColor(a.watch_type),
                          background: `${typeColor(a.watch_type)}15`,
                          padding: "2px 6px", borderRadius: 2 }}>
                          {a.watch_type.toUpperCase()}
                        </Mono>
                        <Mono style={{ fontSize: 8, color: T.muted }}>
                          {a.watch_value}
                        </Mono>
                        <Mono style={{ fontSize: 8, color: T.muted }}>
                          {a.created_at?.slice(0,16).replace("T"," ")} UTC
                        </Mono>
                      </div>
                      <div style={{ fontSize: 13, fontWeight: 500, color: T.text,
                        marginBottom: 4 }}>{a.title}</div>
                      <Mono style={{ fontSize: 9, color: T.muted }}>{a.summary}</Mono>
                    </div>
                    {a.vci_score && (
                      <div style={{ textAlign: "right", flexShrink: 0 }}>
                        <div style={{ fontFamily: "'Bebas Neue', sans-serif",
                          fontSize: 24, color: vciColor(a.vci_score) }}>
                          {Math.round(a.vci_score)}
                        </div>
                        <Mono style={{ fontSize: 7, color: T.muted }}>VCI</Mono>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* WATCHLIST TAB */}
          {activeTab === "watchlist" && (
            <div>
              {watchlist.length === 0 ? (
                <Card style={{ padding: 40, textAlign: "center" }}>
                  <Mono style={{ fontSize: 11, color: T.muted }}>NO ITEMS WATCHED</Mono>
                  <Mono style={{ fontSize: 10, color: T.muted, display: "block", marginTop: 8 }}>
                    Click "+ ADD" to start tracking outlets, authors, keywords or entities.
                  </Mono>
                </Card>
              ) : watchlist.map((w, i) => (
                <div key={w.id} style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  padding: "12px 18px", marginBottom: 8, borderRadius: 6,
                  background: T.surface, border: `1px solid ${T.border}`,
                }}>
                  <div>
                    <div style={{ display: "flex", gap: 8, alignItems: "center",
                      marginBottom: 4 }}>
                      <Mono style={{ fontSize: 9,
                        color: typeColor(w.watch_type),
                        background: `${typeColor(w.watch_type)}15`,
                        border: `1px solid ${typeColor(w.watch_type)}30`,
                        padding: "2px 8px", borderRadius: 3 }}>
                        {w.watch_type.toUpperCase()}
                      </Mono>
                      <div style={{ fontSize: 14, fontWeight: 600 }}>{w.watch_value}</div>
                    </div>
                    <Mono style={{ fontSize: 9, color: T.muted }}>
                      Alert threshold: VCI below {w.threshold}
                      {w.critical_only ? "  ·  Critical only" : "  ·  All articles"}
                    </Mono>
                  </div>
                  <button onClick={() => removeWatch(w.watch_type, w.watch_value)} style={{
                    background: "transparent", border: `1px solid ${T.border}`,
                    color: T.muted, borderRadius: 4, padding: "5px 10px",
                    cursor: "pointer", fontFamily: "'DM Mono', monospace", fontSize: 9,
                  }}>REMOVE</button>
                </div>
              ))}
            </div>
          )}

          {/* ADD TAB */}
          {activeTab === "add" && (
            <Card style={{ padding: 24, maxWidth: 480 }}>
              <Mono style={{ fontSize: 9, color: T.muted, letterSpacing: "0.1em",
                display: "block", marginBottom: 16 }}>ADD TO WATCHLIST</Mono>

              <div style={{ marginBottom: 14 }}>
                <Mono style={{ fontSize: 8, color: T.muted, display: "block",
                  marginBottom: 6 }}>TRACK TYPE</Mono>
                <div style={{ display: "flex", gap: 6 }}>
                  {["outlet","author","keyword","entity"].map(t => (
                    <button key={t} onClick={() => setForm({...form, watch_type: t})} style={{
                      background: form.watch_type === t ? typeColor(t) : T.surface,
                      border: `1px solid ${form.watch_type === t ? typeColor(t) : T.border}`,
                      color: form.watch_type === t ? "#fff" : T.muted,
                      borderRadius: 4, padding: "6px 12px", cursor: "pointer",
                      fontFamily: "'DM Mono', monospace", fontSize: 9,
                    }}>{t.toUpperCase()}</button>
                  ))}
                </div>
              </div>

              <div style={{ marginBottom: 14 }}>
                <Mono style={{ fontSize: 8, color: T.muted, display: "block",
                  marginBottom: 6 }}>
                  {form.watch_type === "outlet" ? "OUTLET NAME" :
                   form.watch_type === "author" ? "AUTHOR NAME" :
                   form.watch_type === "keyword" ? "KEYWORD OR PHRASE" :
                   "ENTITY NAME (e.g. IRGC, Donald Trump)"}
                </Mono>
                <input
                  value={form.watch_value}
                  onChange={e => setForm({...form, watch_value: e.target.value})}
                  placeholder={
                    form.watch_type === "outlet" ? "e.g. BBC News" :
                    form.watch_type === "author" ? "e.g. John Smith" :
                    form.watch_type === "keyword" ? "e.g. Iran nuclear" :
                    "e.g. IRGC"
                  }
                  style={{ width: "100%", background: T.surface,
                    border: `1px solid ${T.border}`, color: T.text,
                    borderRadius: 4, padding: "8px 12px",
                    fontFamily: "'DM Mono', monospace", fontSize: 11,
                    outline: "none", boxSizing: "border-box" }}
                />
              </div>

              <div style={{ marginBottom: 14 }}>
                <Mono style={{ fontSize: 8, color: T.muted, display: "block",
                  marginBottom: 6 }}>VCI ALERT THRESHOLD ({form.threshold})</Mono>
                <input type="range" min="0" max="100" step="5"
                  value={form.threshold}
                  onChange={e => setForm({...form, threshold: parseInt(e.target.value)})}
                  style={{ width: "100%", accentColor: T.blue }}
                />
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <Mono style={{ fontSize: 8, color: T.red }}>0 — Alert always</Mono>
                  <Mono style={{ fontSize: 8, color: T.green }}>100 — Never alert</Mono>
                </div>
              </div>

              <div style={{ marginBottom: 20, display: "flex", alignItems: "center", gap: 10 }}>
                <input type="checkbox" id="crit_only"
                  checked={form.critical_only}
                  onChange={e => setForm({...form, critical_only: e.target.checked})}
                  style={{ accentColor: T.red, width: 14, height: 14 }}
                />
                <label htmlFor="crit_only" style={{ cursor: "pointer" }}>
                  <Mono style={{ fontSize: 9, color: T.muted }}>
                    Critical alerts only (VCI below threshold)
                  </Mono>
                </label>
              </div>

              <button onClick={addWatch} disabled={adding || !form.watch_value.trim()} style={{
                background: T.blue, border: "none", color: "#fff",
                borderRadius: 4, padding: "10px 20px", cursor: "pointer",
                fontFamily: "'DM Mono', monospace", fontSize: 10,
                width: "100%", opacity: adding ? 0.6 : 1,
              }}>{adding ? "ADDING..." : "ADD TO WATCHLIST"}</button>
            </Card>
          )}
        </>
      )}
    </div>
  );
}


function AccountTab({ user, sub, onUpgrade }) {
  const tier = sub?.tier || "free";
  const tierColor = tier === "free" ? T.muted :
                    tier === "reader" ? T.blue :
                    tier === "analyst" ? T.green : T.yellow;
  const tierPrice = { free: "Free", reader: "$12/mo", analyst: "$49/mo", professional: "$199/mo" };

  const [editingName, setEditingName] = useState(false);
  const [displayName, setDisplayName] = useState(
    user?.user_metadata?.full_name || user?.email?.split("@")[0] || ""
  );
  const [nameInput, setNameInput] = useState(displayName);
  const [nameSaving, setNameSaving] = useState(false);
  const [nameMsg, setNameMsg] = useState("");

  const [notifs, setNotifs] = useState({
    critical_alerts: true,
    daily_digest:    true,
    weekly_summary:  false,
    product_updates: true,
  });
  const [notifSaved, setNotifSaved] = useState(false);

  const saveName = async () => {
    setNameSaving(true);
    try {
      const token = localStorage.getItem("vs_token");
      const res = await fetch(`${SUPABASE_URL}/auth/v1/user`, {
        method: "PUT",
        headers: {
          "apikey": SUPABASE_ANON,
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ data: { full_name: nameInput } }),
      });
      if (res.ok) {
        setDisplayName(nameInput);
        setEditingName(false);
        setNameMsg("Name updated");
        setTimeout(() => setNameMsg(""), 3000);
      }
    } catch(e) {}
    setNameSaving(false);
  };

  const saveNotifs = () => {
    localStorage.setItem("vs_notifs", JSON.stringify(notifs));
    setNotifSaved(true);
    setTimeout(() => setNotifSaved(false), 2000);
  };

  const openBilling = () => window.open("https://app.lemonsqueezy.com/my-orders", "_blank");
  const manageSub  = () => window.open("https://app.lemonsqueezy.com/my-orders", "_blank");

  return (
    <div style={{ maxWidth: 600 }}>
      <SectionLabel>ACCOUNT</SectionLabel>

      {/* Profile */}
      <Card style={{ padding: 24, marginBottom: 16 }}>
        <Mono style={{ fontSize: 9, color: T.muted, letterSpacing: "0.1em",
          display: "block", marginBottom: 16 }}>PROFILE</Mono>
        <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 20 }}>
          <div style={{ width: 52, height: 52, borderRadius: "50%",
            background: `linear-gradient(135deg, ${T.blue}, #6366f1)`,
            display: "flex", alignItems: "center",
            justifyContent: "center", fontSize: 20, fontWeight: 700, color: "white",
            flexShrink: 0 }}>
            {(displayName || "?")[0].toUpperCase()}
          </div>
          <div style={{ flex: 1 }}>
            {editingName ? (
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input value={nameInput} onChange={e => setNameInput(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && saveName()}
                  style={{ background: T.surface, border: `1px solid ${T.blue}`,
                    borderRadius: 4, padding: "6px 10px", color: T.text,
                    fontFamily: "'DM Sans'", fontSize: 14, outline: "none", flex: 1 }} />
                <button onClick={saveName} disabled={nameSaving} style={{
                  background: T.blue, border: "none", color: "white",
                  borderRadius: 4, padding: "6px 12px", cursor: "pointer",
                  fontFamily: "'DM Mono'", fontSize: 9 }}>
                  {nameSaving ? "..." : "SAVE"}
                </button>
                <button onClick={() => setEditingName(false)} style={{
                  background: "transparent", border: `1px solid ${T.border}`,
                  color: T.muted, borderRadius: 4, padding: "6px 10px",
                  cursor: "pointer", fontFamily: "'DM Mono'", fontSize: 9 }}>
                  CANCEL
                </button>
              </div>
            ) : (
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ fontSize: 16, fontWeight: 600, color: T.text }}>
                  {displayName}
                </div>
                <button onClick={() => { setNameInput(displayName); setEditingName(true); }}
                  style={{ background: "transparent", border: `1px solid ${T.border}`,
                    color: T.muted, borderRadius: 3, padding: "3px 8px",
                    cursor: "pointer", fontFamily: "'DM Mono'", fontSize: 8 }}>
                  EDIT
                </button>
              </div>
            )}
            <div style={{ fontSize: 13, color: T.muted, marginTop: 4 }}>{user?.email}</div>
            {nameMsg && <Mono style={{ fontSize: 9, color: T.green, display: "block",
              marginTop: 4 }}>{nameMsg}</Mono>}
          </div>
        </div>

        {/* Tier badge */}
        <div style={{ display: "flex", alignItems: "center",
          justifyContent: "space-between", padding: "14px 18px",
          background: T.surface, borderRadius: 8,
          border: `1px solid ${tierColor}30` }}>
          <div>
            <Mono style={{ fontSize: 8, color: T.muted, display: "block", marginBottom: 4 }}>
              CURRENT PLAN
            </Mono>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <Mono style={{ fontSize: 12, color: tierColor, fontWeight: 600 }}>
                {tier.toUpperCase()}
              </Mono>
              <Mono style={{ fontSize: 11, color: T.muted }}>{tierPrice[tier]}</Mono>
            </div>
          </div>
          {tier !== "professional" && (
            <button onClick={onUpgrade} style={{
              background: T.blue, border: "none", color: "white",
              borderRadius: 4, padding: "8px 16px", cursor: "pointer",
              fontFamily: "'DM Mono'", fontSize: 9, letterSpacing: "0.08em",
            }}>UPGRADE</button>
          )}
        </div>
      </Card>

      {/* Features */}
      <Card style={{ padding: 24, marginBottom: 16 }}>
        <Mono style={{ fontSize: 9, color: T.muted, letterSpacing: "0.1em",
          display: "block", marginBottom: 16 }}>YOUR FEATURES</Mono>
        {[
          { label: "URL Scanner",        value: sub?.features?.scan ? `${sub?.features?.scans_per_day === -1 ? "Unlimited" : sub?.features?.scans_per_day} scans/day` : "Locked" },
          { label: "Watchlist & Alerts", value: sub?.features?.watchlist ? "Enabled" : "Locked" },
          { label: "Daily PDF Digest",   value: sub?.features?.digest ? "Enabled" : "Locked" },
          { label: "Narratives",         value: sub?.features?.narratives ? "Enabled" : "Locked" },
          { label: "Source Profiles",    value: sub?.features?.sources ? "Enabled" : "Locked" },
          { label: "API Access",         value: sub?.features?.api ? "Enabled" : "Locked" },
          { label: "Custom Outlets",     value: sub?.features?.custom_outlets ? "Enabled" : "Locked" },
          { label: "Priority Support",   value: sub?.features?.priority_support ? "Enabled" : "Locked" },
        ].map(({ label, value }) => (
          <div key={label} style={{ display: "flex", justifyContent: "space-between",
            alignItems: "center", padding: "10px 0", borderBottom: `1px solid ${T.border}` }}>
            <div style={{ fontSize: 13, color: T.text }}>{label}</div>
            <Mono style={{ fontSize: 10, color: value === "Locked" ? T.muted : T.green }}>
              {value}
            </Mono>
          </div>
        ))}
      </Card>

      {/* Notifications */}
      <Card style={{ padding: 24, marginBottom: 16 }}>
        <Mono style={{ fontSize: 9, color: T.muted, letterSpacing: "0.1em",
          display: "block", marginBottom: 16 }}>NOTIFICATIONS</Mono>
        {[
          { key: "critical_alerts", label: "Critical alerts",    desc: "Immediate email when a watched story is contested" },
          { key: "daily_digest",    label: "Daily digest email", desc: "Morning intelligence digest delivered at 07:00 UTC" },
          { key: "weekly_summary",  label: "Weekly summary",     desc: "Weekly credibility trends and top stories" },
          { key: "product_updates", label: "Product updates",    desc: "New features and improvements to Verdad Score" },
        ].map(({ key, label, desc }) => (
          <div key={key} style={{ display: "flex", justifyContent: "space-between",
            alignItems: "center", padding: "12px 0", borderBottom: `1px solid ${T.border}` }}>
            <div>
              <div style={{ fontSize: 13, color: T.text, marginBottom: 2 }}>{label}</div>
              <div style={{ fontSize: 11, color: T.muted }}>{desc}</div>
            </div>
            <div onClick={() => setNotifs(n => ({ ...n, [key]: !n[key] }))}
              style={{ width: 36, height: 20, borderRadius: 10, cursor: "pointer",
                background: notifs[key] ? T.blue : T.border,
                position: "relative", transition: "background 0.2s", flexShrink: 0 }}>
              <div style={{ width: 14, height: 14, borderRadius: "50%",
                background: "white", position: "absolute",
                top: 3, transition: "left 0.2s",
                left: notifs[key] ? 19 : 3 }} />
            </div>
          </div>
        ))}
        <button onClick={saveNotifs} style={{
          marginTop: 16, background: T.surface, border: `1px solid ${T.border}`,
          color: notifSaved ? T.green : T.text, borderRadius: 4,
          padding: "8px 16px", cursor: "pointer",
          fontFamily: "'DM Mono'", fontSize: 9, letterSpacing: "0.08em",
        }}>{notifSaved ? "SAVED ✓" : "SAVE PREFERENCES"}</button>
      </Card>

      {/* Billing */}
      <Card style={{ padding: 24, marginBottom: 16 }}>
        <Mono style={{ fontSize: 9, color: T.muted, letterSpacing: "0.1em",
          display: "block", marginBottom: 16 }}>BILLING</Mono>
        {tier === "free" ? (
          <div>
            <div style={{ fontSize: 13, color: T.muted, marginBottom: 16 }}>
              You are on the free plan. Upgrade to access premium features.
            </div>
            <button onClick={onUpgrade} style={{
              background: T.blue, border: "none", color: "white",
              borderRadius: 4, padding: "10px 20px", cursor: "pointer",
              fontFamily: "'DM Mono'", fontSize: 9, letterSpacing: "0.08em",
            }}>VIEW PLANS</button>
          </div>
        ) : (
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button onClick={openBilling} style={{
              background: T.surface, border: `1px solid ${T.border}`,
              color: T.text, borderRadius: 4, padding: "10px 16px",
              cursor: "pointer", fontFamily: "'DM Mono'", fontSize: 9,
              letterSpacing: "0.08em",
            }}>VIEW INVOICES</button>
            <button onClick={manageSub} style={{
              background: T.surface, border: `1px solid ${T.border}`,
              color: T.text, borderRadius: 4, padding: "10px 16px",
              cursor: "pointer", fontFamily: "'DM Mono'", fontSize: 9,
              letterSpacing: "0.08em",
            }}>MANAGE SUBSCRIPTION</button>
            <button onClick={manageSub} style={{
              background: "transparent", border: `1px solid ${T.red}30`,
              color: T.red, borderRadius: 4, padding: "10px 16px",
              cursor: "pointer", fontFamily: "'DM Mono'", fontSize: 9,
              letterSpacing: "0.08em",
            }}>CANCEL PLAN</button>
          </div>
        )}
      </Card>

      {/* Account Actions */}
      <Card style={{ padding: 24 }}>
        <Mono style={{ fontSize: 9, color: T.muted, letterSpacing: "0.1em",
          display: "block", marginBottom: 16 }}>ACCOUNT ACTIONS</Mono>
        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={() => supabase.auth.signOut()} style={{
            background: "transparent", border: `1px solid ${T.red}`,
            color: T.red, borderRadius: 4, padding: "10px 16px",
            cursor: "pointer", fontFamily: "'DM Mono'", fontSize: 9,
            letterSpacing: "0.08em",
          }}>SIGN OUT</button>
        </div>
      </Card>
    </div>
  );
}

// ============================================================
// BLOG TAB
// ============================================================
function BlogTab() {
  const [posts, setPosts]         = React.useState([]);
  const [loading, setLoading]     = React.useState(true);
  const [generating, setGenerating] = React.useState(false);
  const [subject, setSubject]     = React.useState("");
  const [editing, setEditing]     = React.useState(null);
  const [editEs, setEditEs]       = React.useState("");
  const [editEn, setEditEn]       = React.useState("");
  const [msg, setMsg]             = React.useState(null);

  const loadPosts = async () => {
    setLoading(true);
    const token = localStorage.getItem("vs_token");
    const headers = { "Content-Type": "application/json", ...(token ? { "Authorization": `Bearer ${token}` } : {}) };
    try {
      const [r1, r2] = await Promise.all([
        fetch(`${API_BASE}/blog/posts?status=draft&per_page=50&language=es`, { headers }),
        fetch(`${API_BASE}/blog/posts?status=published&per_page=50&language=es`, { headers }),
      ]);
      const drafts    = await r1.json();
      const published = await r2.json();
      const all = [...(drafts.posts||[]), ...(published.posts||[])];
      setPosts(all);
      if (all.length === 0) setMsg("No posts found — check API");
    } catch(e) { setMsg("Error: " + e.message); }
    setLoading(false);
  };

  React.useEffect(() => { loadPosts(); }, []);

  const generate = async () => {
    if (!subject.trim()) return;
    setGenerating(true); setMsg(null);
    try {
      const data = await apiFetch(`/blog/generate?subject=${encodeURIComponent(subject)}`, { method: "POST" });
      setMsg(`✅ Generated: ${data.title_es}`);
      loadPosts();
    } catch(e) { setMsg("Failed to generate"); }
    setGenerating(false);
  };

  const publish = async (slug) => {
    try {
      await apiFetch(`/blog/posts/${slug}/publish`, { method: "PATCH" });
      setMsg("✅ Published");
      loadPosts();
    } catch(e) { setMsg("Failed to publish"); }
  };

  const saveEdit = async (slug) => {
    const token = localStorage.getItem("vs_token");
    if (!token) { setMsg("❌ Session expired — please refresh the page and try again"); return; }
    try {
      const resp = await fetch(`${API_BASE}/blog/posts/${slug}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify({ content_es: editEs, content_en: editEn })
      });
      if (resp.status === 401) {
        setMsg("❌ Session expired — your edits are preserved below. Refresh the page and save again.");
        return;
      }
      setEditing(null);
      setMsg("✅ Saved");
      loadPosts();
    } catch(e) { setMsg("❌ Save failed: " + e.message); }
  };

  return (
    <div style={{ padding: "24px 32px" }}>
      <SectionLabel>BLOG MANAGER</SectionLabel>
      <div style={{ fontSize: 13, color: T.muted, marginBottom: 24 }}>
        Auto-generate bilingual blog posts from report data.
      </div>

      {/* Generate */}
      <Card style={{ padding: 20, marginBottom: 24 }}>
        <Mono style={{ fontSize: 9, color: T.muted, letterSpacing: "0.1em", display: "block", marginBottom: 12 }}>
          GENERATE NEW POST
        </Mono>
        <div style={{ display: "flex", gap: 10 }}>
          <input
            value={subject}
            onChange={e => setSubject(e.target.value)}
            placeholder="Subject name (e.g. Manuel Adorni)"
            style={{
              flex: 1, background: T.surface, border: `1px solid ${T.border}`,
              color: T.text, borderRadius: 6, padding: "10px 14px",
              fontFamily: "'DM Mono'", fontSize: 12, outline: "none"
            }}
          />
          <button onClick={generate} disabled={generating || !subject.trim()} style={{
            background: generating ? T.surfaceAlt : T.blue,
            border: "none", color: "#fff", borderRadius: 6,
            padding: "10px 20px", cursor: "pointer",
            fontFamily: "'DM Mono'", fontSize: 10, letterSpacing: "0.08em"
          }}>
            {generating ? "GENERATING..." : "GENERATE"}
          </button>
        </div>
        {msg && <div style={{ marginTop: 10, fontSize: 12, color: T.green }}>{msg}</div>}
      </Card>

      {/* Posts list */}
      {loading ? <Spinner /> : (
        <Card>
          <div style={{ padding: "12px 18px", borderBottom: `1px solid ${T.border}`, background: T.surfaceAlt }}>
            <Mono style={{ fontSize: 9, color: T.muted, letterSpacing: "0.1em" }}>
              ALL POSTS ({posts.length})
            </Mono>
          </div>
          {posts.length === 0 && (
            <div style={{ padding: 24, color: T.muted, fontSize: 13 }}>No posts yet. Generate one above.</div>
          )}
          {posts.map((post, i) => (
            <div key={i} style={{
              padding: "14px 18px",
              borderBottom: i < posts.length - 1 ? `1px solid ${T.border}` : "none",
              background: i % 2 === 0 ? T.surface : T.surfaceAlt
            }}>
              {editing === post.slug ? (
                <div>
                  <Mono style={{ fontSize: 9, color: T.muted, marginBottom: 6 }}>SPANISH</Mono>
                  <textarea
                    value={editEs}
                    onChange={e => setEditEs(e.target.value)}
                    style={{
                      width: "100%", height: 300, background: T.surface,
                      border: `1px solid ${T.border}`, color: T.text,
                      borderRadius: 6, padding: 10, fontFamily: "monospace",
                      fontSize: 11, marginBottom: 10
                    }}
                  />
                  <Mono style={{ fontSize: 9, color: T.muted, marginBottom: 6 }}>ENGLISH</Mono>
                  <textarea
                    value={editEn}
                    onChange={e => setEditEn(e.target.value)}
                    style={{
                      width: "100%", height: 300, background: T.surface,
                      border: `1px solid ${T.border}`, color: T.text,
                      borderRadius: 6, padding: 10, fontFamily: "monospace",
                      fontSize: 11, marginBottom: 10
                    }}
                  />
                  <div style={{ display: "flex", gap: 8 }}>
                    <button onClick={() => saveEdit(post.slug)} style={{
                      background: T.green, border: "none", color: "#fff",
                      borderRadius: 4, padding: "8px 16px", cursor: "pointer",
                      fontFamily: "'DM Mono'", fontSize: 10
                    }}>SAVE</button>
                    <button onClick={() => setEditing(null)} style={{
                      background: T.surfaceAlt, border: `1px solid ${T.border}`,
                      color: T.muted, borderRadius: 4, padding: "8px 16px",
                      cursor: "pointer", fontFamily: "'DM Mono'", fontSize: 10
                    }}>CANCEL</button>
                  </div>
                </div>
              ) : (
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: T.text, marginBottom: 4 }}>
                      {post.title}
                    </div>
                    <Mono style={{ fontSize: 9, color: T.muted }}>
                      {post.slug} · {post.published_at ? "PUBLISHED" : "DRAFT"}
                    </Mono>
                    <div style={{ fontSize: 11, color: T.muted, marginTop: 4 }}>
                      {post.excerpt?.slice(0, 120)}...
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 8, flexShrink: 0, marginLeft: 16 }}>
                    <button onClick={async () => {
                      const es = await apiFetch(`/blog/posts/${post.slug}?language=es`);
                      const en = await apiFetch(`/blog/posts/${post.slug}?language=en`);
                      setEditEs(es.content || "");
                      setEditEn(en.content || "");
                      setEditing(post.slug);
                    }} style={{
                      background: T.surfaceAlt, border: `1px solid ${T.border}`,
                      color: T.muted, borderRadius: 4, padding: "6px 12px",
                      cursor: "pointer", fontFamily: "'DM Mono'", fontSize: 9
                    }}>EDIT</button>
                    {!post.published_at && (
                      <button onClick={() => publish(post.slug)} style={{
                        background: T.blue, border: "none", color: "#fff",
                        borderRadius: 4, padding: "6px 12px", cursor: "pointer",
                        fontFamily: "'DM Mono'", fontSize: 9
                      }}>PUBLISH</button>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </Card>
      )}
    </div>
  );
}

function ReviewTab() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [selected, setSelected] = useState(null);
  const [editorialNote, setEditorialNote] = useState("");
  const [page, setPage]         = useState(1);
  const [total, setTotal]       = useState(0);
  const PER_PAGE = 15;

  const [decisionFilter, setDecisionFilter] = useState("");

  const load = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const d = await apiFetch(`/reviews?per_page=${PER_PAGE}&page=${p}${decisionFilter ? `&decision=${decisionFilter}` : ""}`);
      setArticles(d.articles || []);
      setTotal(d.total || 0);
      setPage(p);
    } finally { setLoading(false); }
  }, [decisionFilter]);

  useEffect(() => { load(1); }, [load]);

  const priorityColor = p => p === "HIGH" ? "#ef4444" : p === "MEDIUM" ? "#f59e0b" : "#64748b";
  const flagColor = f => f === "REQUIRES_REVIEW" ? "#ef4444" : f === "UNVERIFIABLE" ? "#f59e0b" : "#22c55e";

  const saveDecision = async (decision) => {
    try {
      const note = editorialNote ? `&note=${encodeURIComponent(editorialNote)}` : "";
      await fetch(`https://coldfact.up.railway.app/articles/${selected._id}/review?decision=${decision}${note}`, { method: "PATCH" });
      setSelected({ ...selected, review_decision: decision });
      setEditorialNote("");
      load(page);
    } catch(e) { console.error(e); }
  };

  if (selected) return (
    <div style={{ animation: "fadeIn 0.2s ease" }}>
      <button onClick={() => setSelected(null)} style={{
        background: "none", border: "1px solid #1e293b", color: "#64748b",
        padding: "6px 14px", borderRadius: 4, cursor: "pointer",
        fontFamily: "'DM Mono', monospace", fontSize: 11, marginBottom: 20,
      }}>← BACK TO QUEUE</button>

      <div style={{ background: "#0d1521", border: "1px solid #1e293b",
        borderRadius: 8, padding: 24, marginBottom: 16 }}>
        <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
          <div style={{ textAlign: "center", minWidth: 64 }}>
            <div style={{ fontSize: 36, fontWeight: 700,
              color: selected.vci_score >= 85 ? "#22c55e" : selected.vci_score >= 65 ? "#f59e0b" : "#ef4444"
            }}>{selected.vci_score}</div>
            <div style={{ fontSize: 10, color: "#64748b", fontFamily: "'DM Mono', monospace" }}>VCI</div>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 6 }}>{selected.title}</div>
            <div style={{ fontSize: 12, color: "#64748b", fontFamily: "'DM Mono', monospace" }}>
              {selected.outlet} · {selected.country} · {selected.category}
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
              <span style={{ background: flagColor(selected.layer4_review_flag) + "20",
                color: flagColor(selected.layer4_review_flag),
                border: `1px solid ${flagColor(selected.layer4_review_flag)}`,
                padding: "3px 10px", borderRadius: 3, fontSize: 10,
                fontFamily: "'DM Mono', monospace" }}>{selected.layer4_review_flag}</span>
              <span style={{ background: priorityColor(selected.layer4_review_priority) + "20",
                color: priorityColor(selected.layer4_review_priority),
                border: `1px solid ${priorityColor(selected.layer4_review_priority)}`,
                padding: "3px 10px", borderRadius: 3, fontSize: 10,
                fontFamily: "'DM Mono', monospace" }}>{selected.layer4_review_priority} PRIORITY</span>
            </div>
          </div>
          {selected.url && (
            <a href={selected.url} target="_blank" rel="noreferrer" style={{
              color: "#3b82f6", fontSize: 11, fontFamily: "'DM Mono', monospace",
              textDecoration: "none", border: "1px solid #3b82f6",
              padding: "6px 12px", borderRadius: 4, whiteSpace: "nowrap",
            }}>VIEW ORIGINAL →</a>
          )}
        </div>
      </div>

      {selected.layer4_fact_checks?.length > 0 && (
        <div style={{ background: "#0d1521", border: "1px solid #1e293b",
          borderRadius: 8, padding: 20, marginBottom: 16 }}>
          <div style={{ fontSize: 9, color: "#64748b", fontFamily: "'DM Mono', monospace",
            letterSpacing: "0.1em", marginBottom: 12 }}>FACT-CHECK EVIDENCE</div>
          {selected.layer4_fact_checks.map((fc, i) => {
            const vc = fc.verdict_norm === "FALSE" ? "#ef4444" :
                       fc.verdict_norm === "MISLEADING" ? "#f59e0b" :
                       fc.verdict_norm === "TRUE" ? "#22c55e" : "#64748b";
            return (
              <div key={i} style={{ borderLeft: `3px solid ${vc}`, paddingLeft: 14, marginBottom: 14 }}>
                <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 4 }}>
                  <span style={{ background: vc + "20", color: vc, border: `1px solid ${vc}`,
                    padding: "2px 8px", borderRadius: 3, fontSize: 10,
                    fontFamily: "'DM Mono', monospace" }}>{fc.verdict_norm}</span>
                  <span style={{ fontSize: 11, fontWeight: 600, color: "#94a3b8" }}>{fc.publisher}</span>
                </div>
                <div style={{ fontSize: 12, color: "#cbd5e1", fontStyle: "italic", marginBottom: 4 }}>"{fc.claim}"</div>
                <div style={{ fontSize: 10, color: "#64748b" }}>Claimant: {fc.claimant}</div>
                {fc.url && <a href={fc.url} target="_blank" rel="noreferrer"
                  style={{ fontSize: 10, color: "#3b82f6", textDecoration: "none",
                    fontFamily: "'DM Mono', monospace" }}>READ FULL FACT-CHECK →</a>}
              </div>
            );
          })}
        </div>
      )}

      <div style={{ background: "#0d1521", border: "1px solid #1e293b",
        borderRadius: 8, padding: 20, marginBottom: 16 }}>
        <div style={{ fontSize: 9, color: "#64748b", fontFamily: "'DM Mono', monospace",
          letterSpacing: "0.1em", marginBottom: 14 }}>REVIEWER DECISION</div>
        {selected.editorial_note && (
          <div style={{ background: "#060e18", border: "1px solid #334155",
            borderRadius: 6, padding: "10px 14px", marginBottom: 12,
            fontSize: 11, color: "#f59e0b", fontFamily: "'DM Mono', monospace" }}>
            ✎ {selected.editorial_note}
          </div>
        )}
        <textarea
          placeholder="Editorial note (optional) — explain your decision..."
          value={editorialNote}
          onChange={e => setEditorialNote(e.target.value)}
          style={{ width: "100%", minHeight: 68, background: "#060e18",
            border: "1px solid #1e293b", borderRadius: 6,
            color: "#e2e8f0", fontFamily: "'DM Mono', monospace",
            fontSize: 11, padding: "10px 12px", marginBottom: 12,
            resize: "vertical", boxSizing: "border-box", outline: "none" }}
        />
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          {[["APPROVED","#22c55e"],["DISMISSED","#64748b"],["UNVERIFIED","#f59e0b"],["INACCURATE","#ef4444"]].map(([d,c]) => (
            <button key={d} onClick={() => saveDecision(d)} style={{
              background: selected.review_decision === d ? c + "30" : "#0d1521",
              border: `1px solid ${selected.review_decision === d ? c : "#1e293b"}`,
              color: selected.review_decision === d ? c : "#64748b",
              padding: "8px 18px", borderRadius: 4, cursor: "pointer",
              fontFamily: "'DM Mono', monospace", fontSize: 11,
            }}>{d}</button>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between",
        alignItems: "center", marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 9, color: "#64748b", fontFamily: "'DM Mono', monospace",
            letterSpacing: "0.1em", marginBottom: 4 }}>HUMAN REVIEW QUEUE</div>
          <div style={{ fontSize: 13, color: "#94a3b8" }}>
            Articles flagged by Layer 4 external verification
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {[["", "All"], ["", "Pending"], ["APPROVED", "Approved"],
            ["DISMISSED", "Dismissed"], ["UNVERIFIED", "Unverified"],
            ["INACCURATE", "Inaccurate"]].map(([val, label], idx) => {
            const isActive = idx === 1
              ? decisionFilter === ""
              : decisionFilter === val;
            const handleClick = () => {
              setDecisionFilter(idx === 1 ? "" : val);
            };
            return (
              <button key={label} onClick={handleClick} style={{
                background: isActive ? T.blue : "none",
                border: `1px solid ${isActive ? T.blue : T.border}`,
                color: isActive ? "#fff" : T.muted,
                padding: "5px 12px", borderRadius: 4, cursor: "pointer",
                fontFamily: "'DM Mono', monospace", fontSize: 10,
              }}>{label}</button>
            );
          })}
          <button onClick={() => load(1)} style={{
            background: "none", border: `1px solid ${T.border}`, color: T.muted,
            padding: "5px 12px", borderRadius: 4, cursor: "pointer",
            fontFamily: "'DM Mono', monospace", fontSize: 10,
          }}>↺</button>
        </div>
      </div>

      {loading ? <Spinner /> : articles.length === 0 ? (
        <div style={{ textAlign: "center", padding: 60 }}>
          <Mono style={{ fontSize: 13, color: T.muted, letterSpacing: "0.1em",
            display: "block", marginBottom: 8 }}>NO ARTICLES IN REVIEW QUEUE</Mono>
          <Mono style={{ fontSize: 10, color: "#334155" }}>
            Articles scoring below 85 VCI will appear here after Layer 4 verification
          </Mono>
        </div>
      ) : (
        <Card>
          {articles.map((a, i) => (
            <div key={a._id} onClick={() => setSelected(a)}
              style={{ display: "grid", gridTemplateColumns: "1fr 80px 120px 120px 110px",
                gap: 14, padding: "12px 18px", alignItems: "center", cursor: "pointer",
                borderBottom: "1px solid #0a1120",
                background: i % 2 === 0 ? T.surface : T.surfaceAlt }}
              onMouseEnter={e => e.currentTarget.style.background = "#111827"}
              onMouseLeave={e => e.currentTarget.style.background = i%2===0 ? T.surface : T.surfaceAlt}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 3,
                  whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                  maxWidth: 500 }}>{a.title}</div>
                <Mono style={{ fontSize: 9, color: T.muted }}>
                  {a.outlet} · {a.category} · {a.country}
                </Mono>
              </div>
              <ScoreRing score={a.vci_score} size={40} />
              <Mono style={{ fontSize: 9, color: flagColor(a.layer4_review_flag),
                background: flagColor(a.layer4_review_flag) + "15",
                border: `1px solid ${flagColor(a.layer4_review_flag)}30`,
                borderRadius: 3, padding: "2px 6px", textAlign: "center" }}>
                {a.layer4_review_flag}
              </Mono>
              <Mono style={{ fontSize: 9, color: priorityColor(a.layer4_review_priority),
                background: priorityColor(a.layer4_review_priority) + "15",
                border: `1px solid ${priorityColor(a.layer4_review_priority)}30`,
                borderRadius: 3, padding: "2px 6px", textAlign: "center" }}>
                {a.layer4_review_priority}
              </Mono>
              <Mono style={{ fontSize: 9,
                color: a.review_decision ? "#22c55e" : "#64748b" }}>
                {a.review_decision || "PENDING"}
              </Mono>
            </div>
          ))}
        </Card>
      )}

      <div style={{ display: "flex", gap: 6, marginTop: 14, justifyContent: "center" }}>
        {[
          { label: "← PREV", disabled: page <= 1, action: () => load(page-1) },
          { label: `${page}`, disabled: true, action: null },
          { label: "NEXT →", disabled: articles.length < PER_PAGE, action: () => load(page+1) },
        ].map(btn => (
          <button key={btn.label} onClick={btn.action} disabled={btn.disabled} style={{
            background: btn.disabled ? T.border : `${T.blue}20`,
            border: `1px solid ${btn.disabled ? T.border : T.blue}`,
            color: btn.disabled ? T.muted : T.blue, borderRadius: 4,
            padding: "6px 14px", cursor: btn.disabled ? "default" : "pointer",
            fontFamily: "'DM Mono', monospace", fontSize: 10,
          }}>{btn.label}</button>
        ))}
      </div>
    </div>
  );
}


// ============================================================
// AUTHORS TAB
// ============================================================
function BiasReportTab({ authorId, authorName }) {
  const [topic, setTopic]     = useState("");
  const [days, setDays]       = useState(90);
  const [report, setReport]   = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);

  const run = async () => {
    setLoading(true);
    setError(null);
    setReport(null);
    try {
      let path = `/authors/${authorId}/bias-report?days=${days}`;
      if (topic.trim()) path += `&topic=${encodeURIComponent(topic.trim())}`;
      const data = await apiFetch(path);
      setReport(data);
    } catch(e) {
      setError("Failed to load bias report. Please try again.");
    }
    setLoading(false);
  };

  const biasColor = (rating) => ({
    "HIGH BIAS":     T.red,
    "MODERATE BIAS": T.yellow,
    "LOW BIAS":      T.blue,
    "NEUTRAL":       T.green,
  }[rating] || T.muted);

  return (
    <div>
      {/* Controls */}
      <Card style={{ padding: 20, marginBottom: 16 }}>
        <SectionLabel>BIAS ANALYSIS — {authorName.toUpperCase()}</SectionLabel>
        <div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap", marginTop: 12 }}>
          <div style={{ flex: 2, minWidth: 180 }}>
            <Mono style={{ fontSize: 9, color: T.muted, display: "block", marginBottom: 6 }}>
              TOPIC / KEYWORD (optional)
            </Mono>
            <input
              value={topic}
              onChange={e => setTopic(e.target.value)}
              placeholder="e.g. Adorni, Milei, economía..."
              style={{
                width: "100%", background: T.surface, border: `1px solid ${T.border}`,
                borderRadius: 4, padding: "8px 12px", color: T.text,
                fontFamily: "'DM Mono'", fontSize: 11, boxSizing: "border-box",
              }}
            />
          </div>
          <div style={{ minWidth: 120 }}>
            <Mono style={{ fontSize: 9, color: T.muted, display: "block", marginBottom: 6 }}>
              LOOKBACK PERIOD
            </Mono>
            <select
              value={days}
              onChange={e => setDays(Number(e.target.value))}
              style={{
                width: "100%", background: T.surface, border: `1px solid ${T.border}`,
                borderRadius: 4, padding: "8px 12px", color: T.text,
                fontFamily: "'DM Mono'", fontSize: 11,
              }}
            >
              <option value={30}>30 days</option>
              <option value={90}>90 days</option>
              <option value={180}>180 days</option>
              <option value={365}>365 days</option>
            </select>
          </div>
          <button onClick={run} disabled={loading} style={{
            background: T.blue, border: "none", borderRadius: 4,
            color: "#fff", padding: "9px 20px", cursor: "pointer",
            fontFamily: "'DM Mono'", fontSize: 10, letterSpacing: "0.08em",
            opacity: loading ? 0.6 : 1,
          }}>
            {loading ? "ANALYSING..." : "RUN ANALYSIS →"}
          </button>
        </div>
      </Card>

      {error && (
        <Card style={{ padding: 16, marginBottom: 16, border: `1px solid ${T.red}30` }}>
          <Mono style={{ fontSize: 11, color: T.red }}>{error}</Mono>
        </Card>
      )}

      {report && report.article_count === 0 && (
        <Card style={{ padding: 24, textAlign: "center" }}>
          <Mono style={{ fontSize: 11, color: T.muted }}>
            No articles found for this author{topic ? ` matching "${topic}"` : ""} in the last {days} days.
          </Mono>
        </Card>
      )}

      {report && report.article_count > 0 && (
        <div>
          {/* Bias Rating */}
          <Card style={{ padding: 24, marginBottom: 16, textAlign: "center",
            border: `1px solid ${biasColor(report.bias_rating)}30` }}>
            <Mono style={{ fontSize: 10, color: T.muted, display: "block", marginBottom: 8 }}>
              BIAS RATING — {report.article_count} ARTICLES ANALYSED
              {topic ? ` — TOPIC: "${topic.toUpperCase()}"` : " — ALL TOPICS"}
            </Mono>
            <div style={{ fontFamily: "'Bebas Neue'", fontSize: 52,
              color: biasColor(report.bias_rating), lineHeight: 1, marginBottom: 8 }}>
              {report.bias_rating}
            </div>
            <Mono style={{ fontSize: 10, color: T.muted }}>
              Bias Score: {report.bias_score}/100 · Avg VCI: {report.avg_vci}
            </Mono>
          </Card>

          {/* Signal breakdown */}
          <Card style={{ padding: 20, marginBottom: 16 }}>
            <SectionLabel>BIAS SIGNALS</SectionLabel>
            {Object.entries(report.signals).map(([key, sig]) => {
              const isPositive = key === "confirmed";
              const color = isPositive ? T.green : sig.pct >= 60 ? T.red : sig.pct >= 30 ? T.yellow : T.muted;
              return (
                <div key={key} style={{ marginBottom: 16 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                    <Mono style={{ fontSize: 10, color: T.text }}>{sig.label}</Mono>
                    <Mono style={{ fontSize: 10, color }}>
                      {sig.count} articles ({sig.pct}%)
                    </Mono>
                  </div>
                  <div style={{ height: 5, background: T.border, borderRadius: 3 }}>
                    <div style={{
                      width: `${Math.min(sig.pct, 100)}%`, height: "100%",
                      background: color, borderRadius: 3, transition: "width 0.8s ease",
                    }} />
                  </div>
                </div>
              );
            })}
          </Card>

          {/* VCI distribution */}
          <Card style={{ padding: 20, marginBottom: 16 }}>
            <SectionLabel>VCI DISTRIBUTION</SectionLabel>
            {[
              { label: "Reliable (82+)",   value: report.vci_distribution.reliable,    color: T.green },
              { label: "At Risk (65-81)",   value: report.vci_distribution.at_risk,     color: T.yellow },
              { label: "Blacklisted (<65)", value: report.vci_distribution.blacklisted, color: T.red },
            ].map(row => {
              const pct = Math.round(100 * row.value / report.article_count);
              return (
                <div key={row.label} style={{ marginBottom: 14 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
                    <Mono style={{ fontSize: 10, color: T.text }}>{row.label}</Mono>
                    <Mono style={{ fontSize: 10, color: row.color }}>{row.value} ({pct}%)</Mono>
                  </div>
                  <div style={{ height: 5, background: T.border, borderRadius: 3 }}>
                    <div style={{ width: `${pct}%`, height: "100%",
                      background: row.color, borderRadius: 3, transition: "width 0.8s ease" }} />
                  </div>
                </div>
              );
            })}
          </Card>

          {/* Article list */}
          <Card style={{ padding: 20 }}>
            <SectionLabel>ARTICLES ANALYSED</SectionLabel>
            {report.articles.map(art => (
              <div key={art.id} style={{ padding: "10px 0",
                borderBottom: `1px solid ${T.border}`, display: "flex",
                justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <a href={art.url} target="_blank" rel="noopener noreferrer"
                    style={{ color: T.text, textDecoration: "none", fontSize: 12,
                      lineHeight: 1.4, display: "block", marginBottom: 4 }}>
                    {art.title}
                  </a>
                  <div style={{ display: "flex", gap: 8 }}>
                    {art.layer4_contradictions && (
                      <Mono style={{ fontSize: 8, color: T.red,
                        background: `${T.red}15`, padding: "2px 6px", borderRadius: 3 }}>
                        CONTRADICTED
                      </Mono>
                    )}
                    {art.layer4_confirmations && (
                      <Mono style={{ fontSize: 8, color: T.green,
                        background: `${T.green}15`, padding: "2px 6px", borderRadius: 3 }}>
                        CONFIRMED
                      </Mono>
                    )}
                    {art.has_balance === false && (
                      <Mono style={{ fontSize: 8, color: T.yellow,
                        background: `${T.yellow}15`, padding: "2px 6px", borderRadius: 3 }}>
                        ONE-SIDED
                      </Mono>
                    )}
                  </div>
                </div>
                <div style={{ fontFamily: "'Bebas Neue'", fontSize: 22,
                  color: art.vci_score >= 82 ? T.green : art.vci_score >= 65 ? T.yellow : T.red,
                  flexShrink: 0 }}>
                  {Math.round(art.vci_score)}
                </div>
              </div>
            ))}
          </Card>
        </div>
      )}
    </div>
  );
}

function AuthorsTab() {
  const [authors, setAuthors]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [selected, setSelected] = useState(null);
  const [page, setPage]         = useState(1);
  const [total, setTotal]       = useState(0);
  const [stats, setStats]       = useState({});
  const [search, setSearch]     = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [sortBy, setSortBy]     = useState("aci_score");
  const [order, setOrder]       = useState("desc");
  const PER_PAGE = 50;

  const load = async (p = 1, s = search, sf = statusFilter, sb = sortBy, o = order) => {
    setLoading(true);
    try {
      let path = `/authors?per_page=${PER_PAGE}&page=${p}&sort_by=${sb}&order=${o}`;
      if (sf) path += `&status=${encodeURIComponent(sf)}`;
      if (s.trim()) path += `&search=${encodeURIComponent(s.trim())}`;
      const data = await apiFetch(path);
      setAuthors(data.authors || []);
      setTotal(data.count || 0);
      setPage(p);
      setStats({ reliable: data.reliable, at_risk: data.at_risk, blacklisted: data.blacklisted });
    } catch(e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleSort = (col) => {
    const newOrder = sortBy === col && order === "desc" ? "asc" : "desc";
    setSortBy(col);
    setOrder(newOrder);
    load(1, search, statusFilter, col, newOrder);
  };

  const SortArrow = ({ col }) => (
    <span style={{ color: sortBy === col ? T.blue : T.muted, marginLeft: 3 }}>
      {sortBy === col ? (order === "desc" ? "↓" : "↑") : "↕"}
    </span>
  );

  const aciColor = (status) =>
    status === "Reliable" ? T.green : status === "At Risk" ? T.yellow : T.red;

  const [authorTab, setAuthorTab] = useState("OVERVIEW");

  const { shareUrl: authShareUrl, setShareUrl: setAuthShareUrl, sharing: authSharing, share: authShare } = useShare();

  if (selected) {
    const a = selected;
    const authorTabs = ["OVERVIEW", "ARTICLES", "OUTLETS", "CREDIBILITY", "BIAS REPORT"];
    const aColor = aciColor(a.aci_status);

    // Group articles by outlet
    const outletMap = {};
    (a.articles || []).forEach(art => {
      if (!outletMap[art.outlet]) outletMap[art.outlet] = { count: 0, scores: [] };
      outletMap[art.outlet].count++;
      outletMap[art.outlet].scores.push(art.vci_score);
    });
    const outletStats = Object.entries(outletMap).map(([name, d]) => ({
      name, count: d.count,
      avg: Math.round(d.scores.reduce((s,v)=>s+v,0)/d.scores.length)
    })).sort((x,y) => y.count - x.count);

    // Credibility distribution
    const reliable   = (a.articles||[]).filter(x=>x.vci_score>=82).length;
    const atRisk     = (a.articles||[]).filter(x=>x.vci_score>=65&&x.vci_score<82).length;
    const blacklisted= (a.articles||[]).filter(x=>x.vci_score<65).length;
    const total      = (a.articles||[]).length || 1;

    return (
      <div style={{ animation: "fadeIn 0.2s ease" }}>
        <button onClick={() => { setSelected(null); setAuthorTab("OVERVIEW"); }} style={{
          background: "none", border: `1px solid ${T.border}`, color: T.muted,
          borderRadius: 4, padding: "6px 14px", cursor: "pointer", marginBottom: 20,
          fontFamily: "'DM Mono'", fontSize: 10,
        }}>← BACK TO AUTHORS</button>

        {/* Header */}
        <Card style={{ padding: 24, marginBottom: 16 }}>
          <div style={{ display: "flex", gap: 20, alignItems: "flex-start" }}>
            <div style={{ width: 72, height: 72, borderRadius: "50%", flexShrink: 0,
              background: `${aColor}20`, border: `2px solid ${aColor}`,
              display: "flex", alignItems: "center", justifyContent: "center" }}>
              <div style={{ fontFamily: "'Bebas Neue'", fontSize: 28,
                color: aColor, lineHeight: 1 }}>{a.aci_score}</div>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", gap: 8, alignItems: "center",
                marginBottom: 8, flexWrap: "wrap" }}>
                <span style={{ background: `${aColor}20`, color: aColor,
                  border: `1px solid ${aColor}30`, padding: "3px 10px",
                  borderRadius: 4, fontSize: 10,
                  fontFamily: "'DM Mono'" }}>{a.aci_status?.toUpperCase()}</span>
                <Mono style={{ fontSize: 9, color: T.muted,
                  background: T.surface, padding: "2px 8px", borderRadius: 3 }}>
                  ACI SCORE
                </Mono>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                <div style={{ fontSize: 22, fontWeight: 700, color: T.text }}>{a.name}</div>
                <button onClick={() => authShare({ vci_score: a.aci_score, verdict: a.aci_status, title: a.name, summary: `${a.name} — ${a.outlet} — ACI Score ${a.aci_score}` })}
                  disabled={authSharing} style={{
                  background: `${T.blue}20`, border: `1px solid ${T.blue}`,
                  color: T.blue, borderRadius: 4, padding: "4px 10px",
                  cursor: "pointer", fontFamily: "'DM Mono'", fontSize: 8,
                  letterSpacing: "0.08em",
                }}>{authSharing ? "..." : "↗ SHARE"}</button>
              </div>
              <ShareModal shareUrl={authShareUrl} onClose={() => setAuthShareUrl(null)} />
              <Mono style={{ fontSize: 10, color: T.blue, display: "block",
                marginBottom: 4 }}>{a.outlet}</Mono>
              <Mono style={{ fontSize: 10, color: T.muted }}>
                {a.article_count} articles tracked
              </Mono>
            </div>
          </div>

          {/* KPI row */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)",
            gap: 8, marginTop: 20, paddingTop: 20,
            borderTop: `1px solid ${T.border}` }}>
            {[
              { label: "ACI SCORE",  value: a.aci_score,     color: aColor,           tip: "ACI" },
              { label: "AVG VCI",    value: a.avg_vci,       color: vciColor(a.avg_vci), tip: "VCI" },
              { label: "ARTICLES",   value: a.article_count, color: T.muted },
              { label: "OUTLETS",    value: outletStats.length, color: T.cyan },
            ].map(k => (
              <div key={k.label} style={{ textAlign: "center" }}>
                <Mono style={{ fontSize: 8, color: T.muted, display: "block",
                  marginBottom: 4 }}>{k.label}{k.tip && <InfoIcon term={k.tip} />}</Mono>
                <div style={{ fontFamily: "'Bebas Neue'", fontSize: 28,
                  color: k.color, lineHeight: 1 }}>{k.value ?? "—"}</div>
              </div>
            ))}
          </div>
        </Card>

        {/* Tabs */}
        <div style={{ display: "flex", gap: 0, marginBottom: 16,
          borderBottom: `1px solid ${T.border}` }}>
          {authorTabs.map(t => (
            <button key={t} onClick={() => setAuthorTab(t)} style={{
              background: "none", border: "none",
              borderBottom: authorTab === t ? `2px solid ${T.blue}` : "2px solid transparent",
              color: authorTab === t ? T.blue : T.muted,
              padding: "8px 16px", cursor: "pointer",
              fontFamily: "'DM Mono'", fontSize: 10, letterSpacing: "0.06em",
            }}>{t}</button>
          ))}
        </div>

        {/* OVERVIEW TAB */}
        {authorTab === "OVERVIEW" && (
          <div className="two-col-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {a.narrative && (
                <Card style={{ padding: 20, borderLeft: `3px solid ${T.blue}` }}>
                  <SectionLabel>INTELLIGENCE OVERVIEW</SectionLabel>
                  <p style={{ fontSize: 13, color: T.text, lineHeight: 1.7, margin: 0 }}>
                    {typeof a.narrative === "string" ? a.narrative :
                     a.narrative?.summary || JSON.stringify(a.narrative)}
                  </p>
                </Card>
              )}
              <Card style={{ padding: 20 }}>
                <SectionLabel>CREDIBILITY DISTRIBUTION</SectionLabel>
                {[
                  { label: "Reliable (82+)",  count: reliable,    color: T.green },
                  { label: "At Risk (60-81)",  count: atRisk,      color: T.yellow },
                  { label: "Blacklisted (<60)", count: blacklisted, color: T.red },
                ].map(row => (
                  <div key={row.label} style={{ marginBottom: 14 }}>
                    <div style={{ display: "flex", justifyContent: "space-between",
                      marginBottom: 5 }}>
                      <Mono style={{ fontSize: 10, color: T.text }}>{row.label}</Mono>
                      <Mono style={{ fontSize: 10, color: row.color }}>
                        {row.count} ({Math.round(row.count/total*100)}%)
                      </Mono>
                    </div>
                    <div style={{ height: 4, background: T.border, borderRadius: 2 }}>
                      <div style={{ width: `${Math.round(row.count/total*100)}%`,
                        height: "100%", background: row.color,
                        borderRadius: 2, transition: "width 0.8s ease" }} />
                    </div>
                  </div>
                ))}
              </Card>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <Card style={{ padding: 20 }}>
                <SectionLabel>RECENT ARTICLES</SectionLabel>
                {(a.articles || []).slice(0,5).map((art, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center",
                    gap: 10, padding: "8px 0",
                    borderBottom: i < 4 ? `1px solid ${T.border}` : "none" }}>
                    <div style={{ width: 36, height: 36, borderRadius: 4, flexShrink: 0,
                      background: `${vciColor(art.vci_score)}15`,
                      display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <Mono style={{ fontSize: 11, color: vciColor(art.vci_score),
                        fontWeight: 700 }}>{Math.round(art.vci_score)}</Mono>
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, color: T.text, lineHeight: 1.4,
                        overflow: "hidden", textOverflow: "ellipsis",
                        display: "-webkit-box", WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical" }}>{art.title}</div>
                      <Mono style={{ fontSize: 9, color: T.muted }}>{art.outlet}</Mono>
                    </div>
                  </div>
                ))}
              </Card>
              <Card style={{ padding: 20 }}>
                <SectionLabel>TOP OUTLETS</SectionLabel>
                {outletStats.slice(0,5).map((o, i) => (
                  <div key={o.name} style={{ display: "flex", alignItems: "center",
                    gap: 10, padding: "8px 0",
                    borderBottom: i < 4 ? `1px solid ${T.border}` : "none" }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, color: T.text,
                        fontWeight: 500 }}>{o.name}</div>
                      <Mono style={{ fontSize: 9, color: T.muted }}>
                        {o.count} articles
                      </Mono>
                    </div>
                    <Mono style={{ fontSize: 11, color: vciColor(o.avg),
                      fontWeight: 700 }}>{o.avg}</Mono>
                  </div>
                ))}
              </Card>
            </div>
          </div>
        )}

        {/* ARTICLES TAB */}
        {authorTab === "ARTICLES" && (
          <Card style={{ padding: 20 }}>
            <SectionLabel>ALL ARTICLES ({(a.articles||[]).length})</SectionLabel>
            {(a.articles || []).map((art, i) => (
              <div key={i}
                onClick={() => art.url && window.open(art.url, "_blank")}
                style={{ display: "flex", alignItems: "center", gap: 12,
                  padding: "10px 0",
                  borderBottom: i < a.articles.length-1 ? `1px solid ${T.border}` : "none",
                  cursor: art.url ? "pointer" : "default",
                  transition: "opacity 0.15s" }}
                onMouseOver={e => e.currentTarget.style.opacity = "0.75"}
                onMouseOut={e => e.currentTarget.style.opacity = "1"}>
                <div style={{ width: 44, height: 44, borderRadius: 6, flexShrink: 0,
                  background: `${vciColor(art.vci_score)}15`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  border: `1px solid ${vciColor(art.vci_score)}30` }}>
                  <div style={{ fontFamily: "'Bebas Neue'", fontSize: 20,
                    color: vciColor(art.vci_score), lineHeight: 1 }}>
                    {Math.round(art.vci_score)}
                  </div>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, color: T.text, lineHeight: 1.4,
                    marginBottom: 3 }}>{art.title}</div>
                  <div style={{ display: "flex", gap: 10 }}>
                    <Mono style={{ fontSize: 9, color: T.blue }}>{art.outlet}</Mono>
                    <Mono style={{ fontSize: 9, color: T.muted }}>
                      {art.scraped_at ? new Date(art.scraped_at).toLocaleDateString() : ""}
                    </Mono>
                  </div>
                </div>
                <Badge status={art.vci_status || (
                  art.vci_score >= 82 ? "Reliable" :
                  art.vci_score >= 60 ? "At Risk" : "Blacklisted"
                )} />
              </div>
            ))}
          </Card>
        )}

        {/* OUTLETS TAB */}
        {authorTab === "OUTLETS" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <Card style={{ padding: 20 }}>
              <SectionLabel>OUTLETS THIS AUTHOR WRITES FOR ({outletStats.length})</SectionLabel>
              {outletStats.map((o, i) => (
                <div key={o.name} style={{ display: "flex", alignItems: "center",
                  gap: 12, padding: "12px 0",
                  borderBottom: i < outletStats.length-1 ? `1px solid ${T.border}` : "none" }}>
                  <ScoreRing score={o.avg} size={44} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: T.text,
                      marginBottom: 4 }}>{o.name}</div>
                    <div style={{ height: 3, background: T.border, borderRadius: 2,
                      marginBottom: 4 }}>
                      <div style={{ width: `${o.avg}%`, height: "100%",
                        background: vciColor(o.avg), borderRadius: 2 }} />
                    </div>
                    <Mono style={{ fontSize: 9, color: T.muted }}>
                      {o.count} articles · avg VCI {o.avg}
                    </Mono>
                  </div>
                  <Badge status={o.avg >= 82 ? "Reliable" :
                    o.avg >= 60 ? "At Risk" : "Blacklisted"} />
                </div>
              ))}
            </Card>
          </div>
        )}

        {/* CREDIBILITY TAB */}
        {authorTab === "BIAS REPORT" && (
          <BiasReportTab authorId={a.id} authorName={a.name} />
        )}

        {authorTab === "CREDIBILITY" && (
          <div className="two-col-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            <Card style={{ padding: 20 }}>
              <SectionLabel>CREDIBILITY BREAKDOWN</SectionLabel>
              {[
                { label: "Reliable articles",    value: reliable,    pct: Math.round(reliable/total*100),    color: T.green },
                { label: "At Risk articles",      value: atRisk,      pct: Math.round(atRisk/total*100),      color: T.yellow },
                { label: "Blacklisted articles",  value: blacklisted, pct: Math.round(blacklisted/total*100), color: T.red },
              ].map(row => (
                <div key={row.label} style={{ marginBottom: 18 }}>
                  <div style={{ display: "flex", justifyContent: "space-between",
                    marginBottom: 6 }}>
                    <Mono style={{ fontSize: 10, color: T.text }}>{row.label}</Mono>
                    <Mono style={{ fontSize: 10, color: row.color }}>
                      {row.value} ({row.pct}%)
                    </Mono>
                  </div>
                  <div style={{ height: 6, background: T.border, borderRadius: 3 }}>
                    <div style={{ width: `${row.pct}%`, height: "100%",
                      background: row.color, borderRadius: 3,
                      transition: "width 0.8s ease" }} />
                  </div>
                </div>
              ))}
            </Card>
            <Card style={{ padding: 20 }}>
              <SectionLabel>RISK ASSESSMENT</SectionLabel>
              <div style={{ padding: 16, background: `${aColor}10`,
                borderRadius: 8, border: `1px solid ${aColor}20`, marginBottom: 16 }}>
                <div style={{ fontFamily: "'Bebas Neue'", fontSize: 32,
                  color: aColor, marginBottom: 4 }}>{a.aci_status?.toUpperCase()}</div>
                <div style={{ fontSize: 13, color: T.muted, lineHeight: 1.6 }}>
                  {a.aci_status === "Reliable"
                    ? `${a.name} demonstrates consistently high credibility across their published work. Suitable for citation without additional verification.`
                    : a.aci_status === "At Risk"
                    ? `${a.name} shows mixed credibility signals. Some articles score well while others raise concerns. Independent verification recommended.`
                    : `${a.name} has a high proportion of low-credibility articles. Exercise significant caution when citing this author.`
                  }
                </div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {[
                  { label: "Primary outlet", value: outletStats[0]?.name || "—" },
                  { label: "Best VCI article", value: Math.max(...(a.articles||[{vci_score:0}]).map(x=>x.vci_score)) },
                  { label: "Worst VCI article", value: Math.min(...(a.articles||[{vci_score:100}]).map(x=>x.vci_score)) },
                  { label: "Avg VCI", value: a.avg_vci },
                ].map(row => (
                  <div key={row.label} style={{ display: "flex",
                    justifyContent: "space-between", padding: "7px 0",
                    borderBottom: `1px solid ${T.border}` }}>
                    <Mono style={{ fontSize: 10, color: T.muted }}>{row.label}</Mono>
                    <Mono style={{ fontSize: 10, color: T.text }}>{row.value}</Mono>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      {/* HEADER */}
      <div style={{ display: "flex", justifyContent: "space-between",
        alignItems: "center", marginBottom: 16 }}>
        <div>
          <SectionLabel>AUTHOR CREDIBILITY INDEX</SectionLabel>
          <div style={{ fontSize: 13, color: "#94a3b8" }}>
            {total} authors tracked · {stats.reliable || 0} reliable · {stats.at_risk || 0} at risk · {stats.blacklisted || 0} blacklisted
          </div>
        </div>
        <button onClick={() => load()} style={{
          background: "transparent", border: "1px solid #1e293b",
          color: T.muted, borderRadius: 4, padding: "6px 14px",
          cursor: "pointer", fontFamily: "'DM Mono', monospace", fontSize: 10,
        }}>↺ REFRESH</button>
      </div>

      {/* QUICK BIAS REPORT CHIPS */}
      <div style={{ marginBottom: 12 }}>
        <Mono style={{ fontSize: 9, color: T.muted, display: "block", marginBottom: 8,
          letterSpacing: "0.1em" }}>QUICK BIAS REPORTS — MOST COVERED ENTITIES</Mono>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {[
            "Donald Trump", "Benjamin Netanyahu", "Javier Milei", "Volodymyr Zelensky",
            "Vladimir Putin", "Keir Starmer", "Emmanuel Macron", "Elon Musk",
            "Pete Hegseth", "JD Vance",
          ].map(entity => (
            <button key={entity} onClick={() => {
              setSearch(entity);
              load(1, entity, statusFilter, sortBy, order);
            }} style={{
              background: T.surface, border: `1px solid ${T.border}`,
              color: T.muted, borderRadius: 4, padding: "4px 10px",
              cursor: "pointer", fontFamily: "'DM Mono'", fontSize: 9,
              letterSpacing: "0.06em", transition: "all 0.15s",
            }}
            onMouseEnter={e => { e.target.style.borderColor = T.blue; e.target.style.color = T.blue; }}
            onMouseLeave={e => { e.target.style.borderColor = T.border; e.target.style.color = T.muted; }}
            >{entity}</button>
          ))}
        </div>
      </div>

      {/* FILTERS */}
      <div style={{ display: "flex", gap: 10, marginBottom: 16 }}>
        <input
          value={search}
          onChange={e => { setSearch(e.target.value); load(1, e.target.value, statusFilter, sortBy, order); }}
          placeholder="Search author or outlet..."
          style={{
            flex: 1, background: T.surface, border: `1px solid ${T.border}`,
            borderRadius: 6, padding: "8px 14px", color: T.text, fontSize: 12,
            fontFamily: "'DM Mono', monospace", outline: "none",
          }}
        />
        <select value={statusFilter}
          onChange={e => { setStatusFilter(e.target.value); load(1, search, e.target.value, sortBy, order); }}
          style={{
            background: T.surface, border: `1px solid ${T.border}`, borderRadius: 6,
            padding: "8px 14px", color: T.text, fontSize: 11,
            fontFamily: "'DM Mono', monospace", cursor: "pointer",
          }}>
          <option value="">All Statuses</option>
          <option value="Reliable">Reliable</option>
          <option value="At Risk">At Risk</option>
          <option value="Blacklisted">Blacklisted</option>
        </select>
      </div>

      {loading ? <Spinner /> : authors.length === 0 ? (
        <div style={{ textAlign: "center", padding: 60 }}>
          <Mono style={{ fontSize: 13, color: T.muted, display: "block", marginBottom: 8 }}>
            NO AUTHORS FOUND
          </Mono>
          <Mono style={{ fontSize: 10, color: "#334155" }}>
            Run the scraper to populate author data
          </Mono>
        </div>
      ) : (
        <Card>
          {/* TABLE HEADER — clickable for sorting */}
          <div style={{ display: "grid", gridTemplateColumns: "28px 1fr 120px 70px 70px 140px",
            gap: 14, padding: "10px 18px",
            borderBottom: `1px solid ${T.border}`, background: T.surfaceAlt }}>
            <Mono style={{ fontSize: 8, color: T.muted }}>#</Mono>
            <Mono style={{ fontSize: 8, color: T.muted, letterSpacing: "0.1em" }}>AUTHOR</Mono>
            <Mono style={{ fontSize: 8, color: T.muted, letterSpacing: "0.1em" }}>OUTLET</Mono>
            <Mono onClick={() => handleSort("avg_vci")}
              style={{ fontSize: 8, color: sortBy === "avg_vci" ? T.blue : T.muted,
                letterSpacing: "0.1em", cursor: "pointer" }}>
              VCI <SortArrow col="avg_vci" />
            </Mono>
            <Mono onClick={() => handleSort("aci_score")}
              style={{ fontSize: 8, color: sortBy === "aci_score" ? T.blue : T.muted,
                letterSpacing: "0.1em", cursor: "pointer" }}>
              ACI <SortArrow col="aci_score" />
            </Mono>
            <Mono style={{ fontSize: 8, color: T.muted, letterSpacing: "0.1em" }}>STATUS</Mono>
          </div>

          {/* TABLE ROWS */}
          {authors.map((a, i) => (
            <div key={a.id || i} onClick={async () => {
                const data = await apiFetch(`/articles?author=${encodeURIComponent(a.name)}&per_page=20&sort_by=scraped_at&order=desc`);
                setSelected({ ...a, articles: data.articles || [] });
              }}
              style={{ display: "grid", gridTemplateColumns: "28px 1fr 120px 70px 70px 140px",
                gap: 14, padding: "12px 18px", alignItems: "center", cursor: "pointer",
                borderBottom: "1px solid #0a1120",
                background: i % 2 === 0 ? T.surface : T.surfaceAlt,
                transition: "background 0.1s" }}
              onMouseEnter={e => e.currentTarget.style.background = "#111827"}
              onMouseLeave={e => e.currentTarget.style.background = i%2===0 ? T.surface : T.surfaceAlt}>
              <Mono style={{ fontSize: 10, color: T.muted }}>#{i+1}</Mono>
              <div style={{ fontSize: 13, fontWeight: 600,
                whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {a.name}
              </div>
              <Mono style={{ fontSize: 9, color: T.muted,
                whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {a.outlet}
              </Mono>
              <Mono style={{ fontSize: 11, color: T.blue }}>{a.avg_vci}</Mono>
              <Mono style={{ fontSize: 11, color: aciColor(a.aci_status), fontWeight: 700 }}>
                {a.aci_score}
              </Mono>
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <Badge status={a.aci_status} />
                <button onClick={async (e) => {
                  e.stopPropagation();
                  const data = await apiFetch(`/articles?author=${encodeURIComponent(a.name)}&per_page=20&sort_by=scraped_at&order=desc`);
                  setSelected({ ...a, articles: data.articles || [] });
                  setAuthorTab("BIAS REPORT");
                }} style={{
                  background: `${T.purple}20`, border: `1px solid ${T.purple}40`,
                  color: T.purple, borderRadius: 3, padding: "2px 7px",
                  cursor: "pointer", fontFamily: "'DM Mono'", fontSize: 8,
                  letterSpacing: "0.06em", whiteSpace: "nowrap",
                }}>BIAS →</button>
              </div>
            </div>
          ))}

          {/* PAGINATION */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
            padding: "12px 18px", borderTop: `1px solid ${T.border}` }}>
            <Mono style={{ fontSize: 10, color: T.muted }}>
              Showing {authors.length} of {total} authors
            </Mono>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => load(page - 1)} disabled={page <= 1}
                style={{ background: T.surface, border: `1px solid ${T.border}`,
                  color: page <= 1 ? T.muted : T.text, borderRadius: 4,
                  padding: "4px 12px", cursor: page <= 1 ? "default" : "pointer",
                  fontFamily: "'DM Mono', monospace", fontSize: 10 }}>← PREV</button>
              <Mono style={{ fontSize: 10, color: T.muted, padding: "4px 8px" }}>
                PAGE {page} OF {Math.ceil(total / PER_PAGE) || 1}
              </Mono>
              <button onClick={() => load(page + 1)} disabled={page >= Math.ceil(total / PER_PAGE)}
                style={{ background: T.surface, border: `1px solid ${T.border}`,
                  color: page >= Math.ceil(total / PER_PAGE) ? T.muted : T.text, borderRadius: 4,
                  padding: "4px 12px", cursor: page >= Math.ceil(total / PER_PAGE) ? "default" : "pointer",
                  fontFamily: "'DM Mono', monospace", fontSize: 10 }}>NEXT →</button>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}


// ============================================================
// YOUTUBE TAB
// ============================================================
function YouTubeTab() {
  const [channels, setChannels] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await apiFetch("/youtube?per_page=50");
      setChannels(data.channels || []);
    } catch(e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const cciColor = (status) =>
    status === "Reliable" ? T.green : status === "At Risk" ? T.yellow : T.red;

  const trendIcon = (trend) =>
    trend === "IMPROVING" ? "↑" : trend === "DECLINING" ? "↓" : trend === "STABLE" ? "→" : "—";

  if (selected) return (
    <div style={{ animation: "fadeIn 0.2s ease" }}>
      <button onClick={() => setSelected(null)} style={{
        background: "transparent", border: "1px solid #1e293b", color: T.muted,
        borderRadius: 4, padding: "6px 14px", cursor: "pointer", marginBottom: 24,
        fontFamily: "'DM Mono', monospace", fontSize: 10,
      }}>← BACK TO CHANNELS</button>

      <div style={{ background: "#0d1521", border: "1px solid #1e293b",
        borderRadius: 10, padding: 24, marginBottom: 20,
        display: "flex", alignItems: "center", gap: 20 }}>
        {selected.thumbnail && (
          <img src={selected.thumbnail} alt={selected.channel_name}
            style={{ width: 80, height: 80, borderRadius: 40, objectFit: "cover",
              border: `2px solid ${cciColor(selected.cci_status)}` }} />
        )}
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 20, fontWeight: 600, marginBottom: 4 }}>
            {selected.channel_name}
          </div>
          <div style={{ fontSize: 11, color: T.muted, marginBottom: 10 }}>
            {parseInt(selected.subscribers).toLocaleString()} subscribers · {selected.country}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <span style={{ background: cciColor(selected.cci_status) + "20",
              color: cciColor(selected.cci_status),
              border: `1px solid ${cciColor(selected.cci_status)}`,
              padding: "3px 10px", borderRadius: 4, fontSize: 10,
              fontFamily: "'DM Mono', monospace" }}>{selected.cci_status?.toUpperCase()}</span>
            <span style={{ background: "#1e293b", color: T.muted,
              padding: "3px 10px", borderRadius: 4, fontSize: 10,
              fontFamily: "'DM Mono', monospace" }}>CCI {selected.cci_score}</span>
          </div>
        </div>
        <div style={{ fontSize: 36, fontWeight: 700,
          color: cciColor(selected.cci_status) }}>{selected.cci_score}</div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 12, marginBottom: 20 }}>
        {[
          { label: "VIDEOS SCORED", value: selected.videos_scored },
          { label: "FLAGGED",       value: selected.flagged_videos, color: selected.flagged_videos > 0 ? T.red : T.green },
          { label: "MIN CCI",       value: selected.min_cci, color: T.yellow },
          { label: "MAX CCI",       value: selected.max_cci, color: T.green },
        ].map(({ label, value, color }) => (
          <div key={label} style={{ background: "#0d1521", border: "1px solid #1e293b",
            borderRadius: 8, padding: 16, textAlign: "center" }}>
            <div style={{ fontSize: 22, fontWeight: 700, color: color || T.text }}>{value ?? "—"}</div>
            <div style={{ fontSize: 9, color: T.muted, fontFamily: "'DM Mono', monospace",
              marginTop: 4, letterSpacing: "0.1em" }}>{label}</div>
          </div>
        ))}
      </div>

      <div style={{ background: "#0d1521", border: "1px solid #1e293b",
        borderRadius: 8, padding: 20 }}>
        <SectionLabel>RECENT VIDEOS</SectionLabel>
        {(selected.recent_videos || []).map((v, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 12,
            padding: "10px 0",
            borderBottom: i < selected.recent_videos.length - 1 ? "1px solid #1e293b" : "none" }}>
            {v.thumbnail && (
              <img src={v.thumbnail} alt="" style={{ width: 80, height: 45,
                borderRadius: 4, objectFit: "cover", flexShrink: 0 }} />
            )}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, color: T.text, marginBottom: 3,
                whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{v.title.replace(/^(WATCH|PODCAST|LIVE)\s*\|\s*/i, "")}</div>
              <span style={{ fontSize: 11, fontWeight: 600,
                color: v.vci_score >= 82 ? T.green : v.vci_score >= 65 ? T.yellow : T.red }}>
                {v.vci_score}
              </span>
            </div>
            <a href={`https://youtube.com/watch?v=${v.video_id}`}
              target="_blank" rel="noopener noreferrer"
              style={{ fontSize: 9, color: T.blue, fontFamily: "'DM Mono', monospace",
                textDecoration: "none", whiteSpace: "nowrap" }}>
              WATCH →
            </a>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between",
        alignItems: "center", marginBottom: 24 }}>
        <div>
          <SectionLabel>CHANNEL CREDIBILITY INDEX</SectionLabel>
          <div style={{ fontSize: 13, color: T.muted }}>
            YouTube news channels scored by video title analysis
          </div>
        </div>
        <button onClick={() => load()} style={{
            background: "none", border: `1px solid ${T.border}`, color: T.muted,
            padding: "6px 14px", borderRadius: 4, cursor: "pointer",
            fontFamily: "'DM Mono', monospace", fontSize: 10,
          }}>↺ REFRESH</button>
      </div>

      {loading ? <Spinner /> : channels.length === 0 ? (
        <div style={{ textAlign: "center", padding: 60 }}>
          <Mono style={{ fontSize: 13, color: T.muted, display: "block", marginBottom: 8 }}>
            NO CHANNELS SCORED YET
          </Mono>
          <Mono style={{ fontSize: 10, color: "#334155" }}>
            Run youtube_scorer.py to populate channel data
          </Mono>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 16 }}>
          {channels.map((ch, i) => (
            <div key={i} onClick={() => setSelected(ch)}
              style={{ background: "#0d1521",
                border: `1px solid ${cciColor(ch.cci_status)}30`,
                borderRadius: 10, padding: 20, cursor: "pointer",
                transition: "border-color 0.2s, background 0.2s" }}
              onMouseEnter={e => {
                e.currentTarget.style.borderColor = cciColor(ch.cci_status);
                e.currentTarget.style.background  = "#111827";
              }}
              onMouseLeave={e => {
                e.currentTarget.style.borderColor = cciColor(ch.cci_status) + "30";
                e.currentTarget.style.background  = "#0d1521";
              }}>
              <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 14 }}>
                {ch.thumbnail ? (
                  <img src={ch.thumbnail} alt={ch.channel_name}
                    style={{ width: 48, height: 48, borderRadius: 24, objectFit: "cover",
                      border: `2px solid ${cciColor(ch.cci_status)}` }} />
                ) : (
                  <div style={{ width: 48, height: 48, borderRadius: 24,
                    background: "#1e293b", display: "flex",
                    alignItems: "center", justifyContent: "center",
                    fontSize: 18, color: T.muted }}>▶</div>
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 2 }}>{ch.channel_name}</div>
                  <div style={{ fontSize: 10, color: T.muted }}>
                    {parseInt(ch.subscribers).toLocaleString()} subs · {ch.country}
                  </div>
                </div>
                <div style={{ fontSize: 26, fontWeight: 700,
                  color: cciColor(ch.cci_status) }}>{ch.cci_score}</div>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ background: cciColor(ch.cci_status) + "20",
                  color: cciColor(ch.cci_status),
                  border: `1px solid ${cciColor(ch.cci_status)}`,
                  padding: "2px 8px", borderRadius: 3, fontSize: 9,
                  fontFamily: "'DM Mono', monospace" }}>{ch.cci_status?.toUpperCase()}</span>
                <span style={{ fontSize: 10, color: T.muted, fontFamily: "'DM Mono', monospace" }}>
                  {ch.flagged_videos}/{ch.videos_scored} flagged
                </span>
                <span style={{ fontSize: 12,
                  color: ch.cci_trend === "IMPROVING" ? T.green :
                         ch.cci_trend === "DECLINING" ? T.red : T.muted }}>
                  {trendIcon(ch.cci_trend)}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================
// TABS
// ============================================================
const TABS = ["Scan", "History", "Account"];
// Hidden tabs (code intact, not shown in nav):
// "Map", "Narratives", "Digest", "Sources", "Simulator", "Authors", "YouTube", "Markets"
const ADMIN_ID = "82ddc0ee-84e0-487b-aa6a-e3b4b96a6bf1";

const TAB_REQUIREMENTS = {
  "Overview":    "free",
  "Leaderboard": "free",
  "Authors":     "free",
  "Articles":    "free",
  "Map":         "reader",
  "Digest":      "reader",
  "Narratives":  "analyst",
  "Scan":        "free",
  "Sources":     "analyst",
  "Watchlist":   "analyst",
  "Simulator":   "analyst",
  "YouTube":     "analyst",
  "Markets":     "analyst",
  "Account":     "free",
  "Reports":     "free",
};

const TIER_RANK = { "free": 0, "reader": 1, "analyst": 2, "professional": 3 };

function canAccessTab(tab, tier) {
  const required = TAB_REQUIREMENTS[tab] || "free";
  return (TIER_RANK[tier] || 0) >= (TIER_RANK[required] || 0);
}

function LockedTab({ tab, tier, onUpgrade }) {
  const required = TAB_REQUIREMENTS[tab] || "analyst";
  const tierNames = { reader: "Reader", analyst: "Analyst", professional: "Professional" };
  const tierPrices = { reader: "$12/mo", analyst: "$49/mo", professional: "$199/mo" };
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center",
      justifyContent: "center", padding: "80px 24px", textAlign: "center" }}>
      <div style={{ fontSize: 40, marginBottom: 16 }}>🔒</div>
      <Mono style={{ fontSize: 10, color: T.blue, letterSpacing: "0.15em",
        display: "block", marginBottom: 12 }}>
        {tierNames[required] || "ANALYST"} FEATURE
      </Mono>
      <div style={{ fontSize: 20, fontWeight: 700, color: T.text, marginBottom: 8 }}>
        {tab} requires {tierNames[required]} or higher
      </div>
      <div style={{ fontSize: 14, color: T.muted, maxWidth: 400,
        marginBottom: 28, lineHeight: 1.6 }}>
        Upgrade to {tierNames[required]} ({tierPrices[required]}) to unlock {tab.toLowerCase()},
        plus all other {tierNames[required]} features.
      </div>
      <button onClick={onUpgrade} style={{
        background: T.blue, border: "none", color: "white",
        borderRadius: 6, padding: "12px 28px", cursor: "pointer",
        fontFamily: "'DM Mono'", fontSize: 11, letterSpacing: "0.08em",
      }}>UPGRADE NOW</button>
    </div>
  );
}

// ============================================================
// OVERVIEW TAB
// ============================================================
function OverviewTab({ user, sub, onUpgrade, adminId, onArticleClick }) {
  const { data: stats,     loading: sL, error: sE, refetch: sR } = useAPI("/stats");
  const { data: outlets,   loading: oL } = useAPI("/outlets?per_page=5&sort_by=oci_score&order=desc&overview=1");
  // const { data: map                    } = useAPI("/map");  // disabled — re-enable when Map tab returns
  const { data: authors,   loading: aL } = useAPI("/authors?per_page=5&sort_by=avg_vci&order=desc", [sub?.tier]);
  // const { data: sources,   loading: srL} = useAPI("/sources", [sub?.tier]);  // disabled
  // const { data: contested              } = useAPI("/clusters?per_page=3&verdict=CONTESTED");  // disabled
  // const { data: trending,  loading: tL } = useAPI("/trending");  // disabled
  const { data: politics               } = useAPI("/articles?per_page=8&category=Politics&sort_by=scraped_at&order=desc&min_vci=65");
  const { data: sports                 } = useAPI("/articles?per_page=8&category=Sports&sort_by=scraped_at&order=desc&min_vci=65");
  const { data: business               } = useAPI("/articles?per_page=8&category=Business&sort_by=scraped_at&order=desc&min_vci=65");

  const [auditing, setAuditing] = useState(false);
  const [auditMsg, setAuditMsg] = useState(null);
  const isAdmin = user?.id === adminId;
  const tier = sub?.tier || "free";

  const triggerAudit = async () => {
    setAuditing(true); setAuditMsg(null);
    try {
      const r = await fetch(`${API_BASE}/audit`, {
        method: "POST",
        headers: { "Authorization": `Bearer ${localStorage.getItem("vs_token")}` }
      });
      const d = await r.json();
      setAuditMsg(d.message || "Pipeline triggered.");
    } catch { setAuditMsg("Pipeline triggered."); }
    finally { setAuditing(false); setTimeout(sR, 5000); }
  };

  const kpis = stats ? [
    { label: "ARTICLES AUDITED",    value: stats.total_articles?.toLocaleString(), color: T.blue },
    { label: "GLOBAL AVG VCI",      value: stats.avg_vci,                          color: vciColor(stats.avg_vci) },
    { label: "SIMULATED PENALTIES", value: `$${(stats.total_fines||0).toLocaleString()}`, color: T.red },
    { label: "FLAGGED TODAY",       value: stats.flagged_count,                    color: T.yellow },
  ] : [];

  const ArticleFeed = ({ articles, category, color }) => (
    <div>
      {(articles?.articles || []).filter(a => a.vci_score >= 65).slice(0,4).map((a, i) => (
        <div key={a.id}
          onClick={() => onArticleClick ? onArticleClick(a.id) : window.open(a.url, "_blank")}
          style={{ padding: "10px 0",
          borderBottom: i < 3 ? `1px solid ${T.border}` : "none",
          display: "flex", gap: 10, alignItems: "flex-start",
          cursor: "pointer", transition: "opacity 0.15s" }}
          onMouseOver={e => e.currentTarget.style.opacity = "0.75"}
          onMouseOut={e => e.currentTarget.style.opacity = "1"}>
          <div style={{ width: 36, height: 36, borderRadius: 4, flexShrink: 0,
            background: `${color}15`, display: "flex", alignItems: "center",
            justifyContent: "center" }}>
            <div style={{ fontFamily: "'Bebas Neue'", fontSize: 14,
              color: color }}>{Math.round(a.vci_score)}</div>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 500, color: T.text,
              lineHeight: 1.4, marginBottom: 3,
              overflow: "hidden", textOverflow: "ellipsis",
              display: "-webkit-box", WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical" }}>{a.title}</div>
            <Mono style={{ fontSize: 9, color: T.muted }}>
              {a.outlet} · {new Date(a.scraped_at).toLocaleDateString()}
            </Mono>
          </div>
        </div>
      ))}
    </div>
  );

  const LockedOverlay = ({ label }) => (
    <div style={{ position: "relative" }}>
      <div style={{ filter: "blur(4px)", pointerEvents: "none", opacity: 0.4 }}>
        {[1,2,3].map(i => (
          <div key={i} style={{ padding: "10px 0", borderBottom: `1px solid ${T.border}`,
            display: "flex", gap: 10 }}>
            <div style={{ width: 36, height: 36, background: T.border, borderRadius: 4 }} />
            <div style={{ flex: 1 }}>
              <div style={{ height: 8, background: T.border, borderRadius: 4, marginBottom: 6, width: "80%" }} />
              <div style={{ height: 6, background: T.border, borderRadius: 4, width: "40%" }} />
            </div>
          </div>
        ))}
      </div>
      <div style={{ position: "absolute", inset: 0, display: "flex",
        flexDirection: "column", alignItems: "center", justifyContent: "center",
        background: `linear-gradient(transparent, ${T.card})` }}>
        <div style={{ fontSize: 20, marginBottom: 6 }}>🔒</div>
        <Mono style={{ fontSize: 9, color: T.muted, marginBottom: 10 }}>{label}</Mono>
        <button onClick={onUpgrade} style={{
          background: T.blue, border: "none", color: "white",
          borderRadius: 4, padding: "6px 14px", cursor: "pointer",
          fontFamily: "'DM Mono'", fontSize: 9 }}>UPGRADE</button>
      </div>
    </div>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

      {/* ROW 1: KPI Cards */}
      <div className="kpi-grid" style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 12 }}>
        {sL ? Array(4).fill(0).map((_,i) => (
          <Card key={i} style={{ padding: 20, minHeight: 90 }}>
            <div style={{ width: "60%", height: 8, background: T.border, borderRadius: 4, marginBottom: 8 }} />
            <div style={{ width: "40%", height: 28, background: T.border, borderRadius: 4 }} />
          </Card>
        )) : sE ? (
          <div style={{ gridColumn: "1/-1" }}><ErrorBox message={sE} onRetry={sR} /></div>
        ) : kpis.map(k => (
          <Card key={k.label} style={{ padding: 20 }}>
            <Mono style={{ fontSize: 9, color: T.muted, letterSpacing: "0.1em",
              display: "block", marginBottom: 8 }}>{k.label}</Mono>
            <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 36,
              color: k.color, lineHeight: 1 }}>{k.value}</div>
          </Card>
        ))}
      </div>

      {/* ROW 2: Verdict breakdown */}
      {stats && (
        <Card style={{ padding: 18 }}>
          <SectionLabel>VERDICT BREAKDOWN</SectionLabel>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            {Object.entries(stats.by_status || {}).map(([s, count]) => {
              const pct = Math.round((count / stats.total_articles) * 100);
              const c = statusColor(s);
              return (
                <div key={s} style={{ flex: 1 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                    <Mono style={{ fontSize: 10, color: c }}>{s.toUpperCase()}</Mono>
                    <Mono style={{ fontSize: 10, color: T.muted }}>{count} ({pct}%)</Mono>
                  </div>
                  <div style={{ height: 4, background: T.border, borderRadius: 2 }}>
                    <div style={{ width: `${pct}%`, height: "100%",
                      background: c, borderRadius: 2, transition: "width 0.8s ease" }} />
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* ROW 3: Magazine grid — Latest Stories */}
      <div className="three-col-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
        <Card style={{ padding: 18 }}>
          <SectionLabel style={{ color: T.red }}>⚡ POLITICS</SectionLabel>
          <ArticleFeed articles={politics} category="Politics" color={T.red} />
        </Card>
        <Card style={{ padding: 18 }}>
          <SectionLabel style={{ color: T.yellow }}>⚡ SPORTS</SectionLabel>
          <ArticleFeed articles={sports} category="Sports" color={T.yellow} />
        </Card>
        <Card style={{ padding: 18 }}>
          <SectionLabel style={{ color: T.blue }}>⚡ BUSINESS</SectionLabel>
          <ArticleFeed articles={business} category="Business" color={T.blue} />
        </Card>
      </div>

{/* ROW 6: Top Outlets */}
      <Card style={{ padding: 18 }}>
          <SectionLabel>🏆 TOP OUTLETS BY VCI</SectionLabel>
          {oL ? <Spinner /> : (outlets?.outlets || []).slice(0, 5).map((o, i) => (
            <div key={o.name} style={{ display: "flex", alignItems: "center",
              gap: 12, padding: "9px 0",
              borderBottom: i < 4 ? `1px solid ${T.border}` : "none" }}>
              <Mono style={{ fontSize: 10, color: T.muted, width: 16 }}>#{i+1}</Mono>
              <ScoreRing score={o.avg_vci} size={40} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 600,
                  whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {o.name}
                </div>
                <Mono style={{ fontSize: 9, color: T.muted }}>{o.category} · {o.country}</Mono>
              </div>
              <Badge status={o.status} />
            </div>
          ))}
        </Card>

{/* ROW 8: Admin only — Manual Audit Trigger */}
      {isAdmin && (
        <Card style={{ padding: 18 }}>
          <SectionLabel>MANUAL PIPELINE TRIGGER</SectionLabel>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <button onClick={triggerAudit} disabled={auditing} style={{
              background: auditing ? T.border : `${T.blue}20`,
              border: `1px solid ${auditing ? T.border : T.blue}`,
              color: auditing ? T.muted : T.blue,
              borderRadius: 5, padding: "8px 20px", cursor: auditing ? "default" : "pointer",
              fontFamily: "'DM Mono', monospace", fontSize: 11, letterSpacing: "0.06em",
            }}>
              {auditing ? "RUNNING…" : "RUN AUDIT NOW"}
            </button>
            {auditMsg && <Mono style={{ fontSize: 11, color: T.green }}>{auditMsg}</Mono>}
            <Mono style={{ fontSize: 10, color: T.muted, marginLeft: "auto" }}>
              Scheduled: every 6h · Next PDF: 07:00 SAST
            </Mono>
          </div>
        </Card>
      )}
    </div>
  );
}

// ============================================================
// LEADERBOARD TAB
// ============================================================

// ============================================================
// MARKETS INTELLIGENCE TAB
// ============================================================
function MarketsTab() {
  const { data, loading, error, refetch } = useAPI("/markets");
  const [selected, setSelected] = useState(null);

  const signalColor = s => s === "WARNING" ? T.red : s === "CAUTION" ? T.yellow : T.green;
  const signalIcon  = s => s === "WARNING" ? "⚠️" : s === "CAUTION" ? "⚡" : "✓";

  if (loading) return <Spinner />;
  if (error)   return <ErrorBox message={error} onRetry={refetch} />;

  const markets = data?.markets || [];

  if (selected) {
    const m = markets.find(m => m.key === selected);
    if (!m) return null;
    return (
      <div style={{ animation: "fadeIn 0.2s ease" }}>
        <button onClick={() => setSelected(null)} style={{
          background: "none", border: `1px solid ${T.border}`, color: T.muted,
          borderRadius: 4, padding: "6px 14px", cursor: "pointer", marginBottom: 24,
          fontFamily: "'DM Mono'", fontSize: 10,
        }}>← BACK TO MARKETS</button>

        {/* Asset Header */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 16,
          marginBottom: 20 }}>
          <Card style={{ padding: 24 }}>
            <Mono style={{ fontSize: 9, color: T.muted, letterSpacing: "0.1em",
              display: "block", marginBottom: 8 }}>{m.ticker}</Mono>
            <div style={{ fontSize: 28, fontWeight: 700, color: T.text,
              marginBottom: 4 }}>{m.name}</div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 12 }}>
              <div style={{ fontFamily: "'Bebas Neue'", fontSize: 48,
                color: T.text, lineHeight: 1 }}>
                {m.price?.toLocaleString(undefined, {maximumFractionDigits: 2})}
              </div>
              <Mono style={{ fontSize: 12, color: T.muted }}>{m.unit}</Mono>
              <Mono style={{ fontSize: 16, fontWeight: 700,
                color: m.direction === "up" ? T.green : T.red }}>
                {m.direction === "up" ? "↑" : "↓"} {Math.abs(m.change_pct)}%
              </Mono>
            </div>
          </Card>
          <Card style={{ padding: 24, minWidth: 200, textAlign: "center" }}>
            <Mono style={{ fontSize: 9, color: T.muted, letterSpacing: "0.1em",
              display: "block", marginBottom: 12 }}>NEWS CREDIBILITY SIGNAL</Mono>
            <div style={{ fontSize: 36, marginBottom: 8 }}>{signalIcon(m.signal)}</div>
            <Mono style={{ fontSize: 14, color: signalColor(m.signal),
              fontWeight: 700, display: "block", marginBottom: 6 }}>
              {m.signal}
            </Mono>
            {m.avg_news_vci && (
              <Mono style={{ fontSize: 10, color: T.muted }}>
                Avg news VCI: {m.avg_news_vci}
              </Mono>
            )}
            {m.low_cred_count > 0 && (
              <Mono style={{ fontSize: 10, color: T.red, display: "block", marginTop: 6 }}>
                {m.low_cred_count} low-credibility stories detected
              </Mono>
            )}
          </Card>
        </div>

        {/* Signal explanation */}
        <Card style={{ padding: 18, marginBottom: 20,
          borderLeft: `3px solid ${signalColor(m.signal)}` }}>
          <Mono style={{ fontSize: 9, color: T.muted, letterSpacing: "0.1em",
            display: "block", marginBottom: 8 }}>SIGNAL ANALYSIS</Mono>
          <div style={{ fontSize: 14, color: T.text, lineHeight: 1.6 }}>
            {m.signal === "WARNING" && (
              `⚠️ Multiple low-credibility sources (${m.low_cred_count}) are driving narratives around ${m.name}. 
               Exercise extreme caution — price movements may be based on unreliable reporting.`
            )}
            {m.signal === "CAUTION" && (
              `⚡ Some low-credibility sources detected in ${m.name} news coverage. 
               Verify key claims independently before acting on market-moving headlines.`
            )}
            {m.signal === "CLEAR" && (
              `✓ News coverage around ${m.name} is predominantly from reliable sources (VCI 65+). 
               Market signals appear to be based on credible reporting.`
            )}
          </div>
        </Card>

        {/* Related Articles */}
        <SectionLabel>RELATED NEWS ({m.articles?.length || 0} articles · last 48h)</SectionLabel>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {(m.articles || []).map(a => (
            <Card key={a.id} style={{ padding: 16,
              borderLeft: `3px solid ${vciColor(a.vci_score)}` }}>
              <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
                <div style={{ textAlign: "center", flexShrink: 0 }}>
                  <div style={{ fontFamily: "'Bebas Neue'", fontSize: 32,
                    color: vciColor(a.vci_score), lineHeight: 1 }}>
                    {Math.round(a.vci_score)}
                  </div>
                  <Mono style={{ fontSize: 8, color: vciColor(a.vci_score) }}>
                    {a.verdict?.toUpperCase()}
                  </Mono>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: T.text,
                    marginBottom: 6, lineHeight: 1.4 }}>{a.title}</div>
                  <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                    <Mono style={{ fontSize: 9, color: T.muted }}>{a.outlet}</Mono>
                    <Mono style={{ fontSize: 9, color: T.muted }}>{a.country}</Mono>
                    <Mono style={{ fontSize: 9, color: T.muted }}>
                      {new Date(a.scraped_at).toLocaleString()}
                    </Mono>
                  </div>
                  {a.vci_score < 65 && (
                    <div style={{ marginTop: 8, padding: "4px 10px",
                      background: `${T.red}15`, borderRadius: 4, display: "inline-block" }}>
                      <Mono style={{ fontSize: 9, color: T.red }}>
                        ⚠️ LOW CREDIBILITY — verify before trading on this story
                      </Mono>
                    </div>
                  )}
                </div>
              </div>
            </Card>
          ))}
          {(!m.articles?.length) && (
            <Mono style={{ fontSize: 12, color: T.muted }}>
              No recent articles found for this asset
            </Mono>
          )}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between",
        alignItems: "center", marginBottom: 20 }}>
        <div>
          <SectionLabel>MARKETS INTELLIGENCE</SectionLabel>
          <div style={{ fontSize: 13, color: T.muted, marginTop: 4 }}>
            Live prices with credibility-scored news signals
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ width: 6, height: 6, borderRadius: "50%",
            background: T.green, animation: "pulse 2s infinite" }} />
          <Mono style={{ fontSize: 9, color: T.muted }}>
            Updated: {data?.updated_at
              ? new Date(data.updated_at).toLocaleTimeString()
              : "—"}
          </Mono>
          <button onClick={refetch} style={{
            background: "transparent", border: `1px solid ${T.border}`,
            color: T.muted, borderRadius: 4, padding: "4px 10px",
            cursor: "pointer", fontFamily: "'DM Mono'", fontSize: 9,
          }}>↻ REFRESH</button>
        </div>
      </div>

      {/* Signal Summary Bar */}
      <Card style={{ padding: 16, marginBottom: 20 }}>
        <Mono style={{ fontSize: 9, color: T.muted, letterSpacing: "0.1em",
          display: "block", marginBottom: 12 }}>OVERALL MARKET NEWS CREDIBILITY</Mono>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {markets.map(m => (
            <div key={m.key} style={{ display: "flex", alignItems: "center",
              gap: 6, padding: "5px 12px",
              background: `${signalColor(m.signal)}15`,
              border: `1px solid ${signalColor(m.signal)}30`,
              borderRadius: 20 }}>
              <div style={{ width: 6, height: 6, borderRadius: "50%",
                background: signalColor(m.signal) }} />
              <Mono style={{ fontSize: 9, color: signalColor(m.signal) }}>
                {m.name}
              </Mono>
            </div>
          ))}
        </div>
      </Card>

      {/* Market Cards Grid */}
      <div style={{ display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
        gap: 12 }}>
        {markets.map(m => (
          <Card key={m.key}
            onClick={() => setSelected(m.key)}
            style={{ padding: 20, cursor: "pointer",
              borderLeft: `3px solid ${signalColor(m.signal)}`,
              transition: "all 0.15s" }}
            onMouseOver={e => e.currentTarget.style.borderColor = T.blue}
            onMouseOut={e => e.currentTarget.style.borderColor = signalColor(m.signal)}
          >
            {/* Asset name + signal */}
            <div style={{ display: "flex", justifyContent: "space-between",
              alignItems: "flex-start", marginBottom: 12 }}>
              <div>
                <Mono style={{ fontSize: 8, color: T.muted,
                  letterSpacing: "0.1em", display: "block", marginBottom: 4 }}>
                  {m.ticker}
                </Mono>
                <div style={{ fontSize: 15, fontWeight: 600, color: T.text }}>
                  {m.name}
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 4,
                padding: "3px 8px",
                background: `${signalColor(m.signal)}15`,
                border: `1px solid ${signalColor(m.signal)}30`,
                borderRadius: 4 }}>
                <Mono style={{ fontSize: 8, color: signalColor(m.signal) }}>
                  {m.signal}
                </Mono>
              </div>
            </div>

            {/* Price */}
            <div style={{ display: "flex", alignItems: "baseline",
              gap: 10, marginBottom: 12 }}>
              <div style={{ fontFamily: "'Bebas Neue'", fontSize: 36,
                color: T.text, lineHeight: 1 }}>
                {m.price?.toLocaleString(undefined, {maximumFractionDigits: 2})}
              </div>
              <Mono style={{ fontSize: 10, color: T.muted }}>{m.unit}</Mono>
              <Mono style={{ fontSize: 14, fontWeight: 700,
                color: m.direction === "up" ? T.green : T.red,
                marginLeft: "auto" }}>
                {m.direction === "up" ? "↑" : "↓"} {Math.abs(m.change_pct)}%
              </Mono>
            </div>

            {/* News credibility */}
            <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between",
                alignItems: "center" }}>
                <Mono style={{ fontSize: 9, color: T.muted }}>
                  {m.articles?.length || 0} news articles · 48h
                </Mono>
                {m.avg_news_vci && (
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <Mono style={{ fontSize: 9, color: T.muted }}>Avg VCI</Mono>
                    <Mono style={{ fontSize: 11, fontWeight: 700,
                      color: vciColor(m.avg_news_vci) }}>
                      {m.avg_news_vci}
                    </Mono>
                  </div>
                )}
              </div>
              {m.low_cred_count > 0 && (
                <div style={{ marginTop: 8, display: "flex",
                  alignItems: "center", gap: 6 }}>
                  <div style={{ width: 6, height: 6, borderRadius: "50%",
                    background: T.red }} />
                  <Mono style={{ fontSize: 9, color: T.red }}>
                    {m.low_cred_count} low-credibility stories detected
                  </Mono>
                </div>
              )}
            </div>
          </Card>
        ))}
      </div>

      {/* Disclaimer */}
      <div style={{ marginTop: 24, padding: "14px 18px",
        background: `${T.yellow}08`, border: `1px solid ${T.yellow}20`,
        borderRadius: 6 }}>
        <Mono style={{ fontSize: 9, color: T.muted, lineHeight: 1.6 }}>
          ⚠️ DISCLAIMER: Verdad Score provides media credibility intelligence only.
          This is not financial advice. Market prices are delayed and for reference only.
          Always conduct your own research before making investment decisions.
        </Mono>
      </div>
    </div>
  );
}

function LeaderboardTab() {
  const [category, setCategory] = useState("");
  const [country,  setCountry]  = useState("");
  const [selected, setSelected] = useState(null);
  const [outletArticles, setOutletArticles] = useState([]);
  const [artLoading, setArtLoading] = useState(false);
  const path = `/outlets?sort_by=oci_score&order=desc&per_page=100${country ? `&country=${country}` : ""}`;
  const { data, loading, error, refetch } = useAPI(path, [category, country]);

  const countries = ["", "ZA", "GB", "US", "DE", "FR", "QA", "IN", "PK", "HK", "NG", "BR", "AR", "RU"];

  const openOutlet = async (o) => {
    setSelected(o);
    setArtLoading(true);
    try {
      const d = await apiFetch(`/articles?outlet=${encodeURIComponent(o.name)}&per_page=20&sort_by=scraped_at&order=desc`);
      setOutletArticles(d.articles || []);
    } catch(e) { console.error(e); }
    setArtLoading(false);
  };

  const [outletTab, setOutletTab] = useState("OVERVIEW");

  const { shareUrl: outShareUrl, setShareUrl: setOutShareUrl, sharing: outSharing, share: outShare } = useShare();

  if (selected) {
    const o = selected;
    const lTabs = ["OVERVIEW", "ARTICLES", "CREDIBILITY"];
    const reliable    = outletArticles.filter(a => a.vci_score >= 82).length;
    const atRisk      = outletArticles.filter(a => a.vci_score >= 65 && a.vci_score < 82).length;
    const blacklisted = outletArticles.filter(a => a.vci_score < 65).length;
    const total       = outletArticles.length || 1;

    return (
      <div style={{ animation: "fadeIn 0.2s ease" }}>
        <button onClick={() => { setSelected(null); setOutletTab("OVERVIEW"); }} style={{
          background: "none", border: `1px solid ${T.border}`, color: T.muted,
          borderRadius: 4, padding: "6px 14px", cursor: "pointer", marginBottom: 20,
          fontFamily: "'DM Mono'", fontSize: 10,
        }}>← BACK TO LEADERBOARD</button>

        {/* Header */}
        <Card style={{ padding: 24, marginBottom: 16 }}>
          <div style={{ display: "flex", gap: 20, alignItems: "flex-start" }}>
            <ScoreRing score={o.oci_score || o.avg_vci} size={80} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
                <Badge status={o.status} />
                {o.category && (
                  <Mono style={{ fontSize: 9, color: T.muted,
                    background: T.surface, padding: "2px 8px", borderRadius: 3 }}>
                    {o.category.toUpperCase()}
                  </Mono>
                )}
                {o.refuted_signals > 0 && (
                  <Mono style={{ fontSize: 9, color: T.red,
                    background: `${T.red}15`, padding: "2px 8px", borderRadius: 3 }}>
                    {o.refuted_signals} REFUTED SIGNALS
                  </Mono>
                )}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                <div style={{ fontSize: 22, fontWeight: 700, color: T.text }}>{o.name}</div>
                <button onClick={() => outShare({ vci_score: o.oci_score || o.avg_vci, verdict: o.status, title: o.name, summary: `${o.name} — VCI Score ${o.oci_score || o.avg_vci}` })}
                  disabled={outSharing} style={{
                  background: `${T.blue}20`, border: `1px solid ${T.blue}`,
                  color: T.blue, borderRadius: 4, padding: "4px 10px",
                  cursor: "pointer", fontFamily: "'DM Mono'", fontSize: 8,
                }}>{outSharing ? "..." : "↗ SHARE"}</button>
              </div>
              <ShareModal shareUrl={outShareUrl} onClose={() => setOutShareUrl(null)} />
              <Mono style={{ fontSize: 10, color: T.muted }}>
                {o.country} · {o.article_count} articles scored
              </Mono>
            </div>
          </div>

          {/* KPIs */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)",
            gap: 8, marginTop: 20, paddingTop: 20,
            borderTop: `1px solid ${T.border}` }}>
            {[
              { label: "OCI SCORE", value: o.oci_score || o.avg_vci,    color: vciColor(o.oci_score || o.avg_vci), tip: "OCI" },
              { label: "AVG VCI",   value: o.avg_vci,                  color: T.muted },
              { label: "ARTICLES",  value: o.article_count,            color: T.blue },
              { label: "VERIFIED",  value: o.verified_signals || 0,    color: T.green },
              { label: "REFUTED",   value: o.refuted_signals || 0,     color: T.red },
              { label: "PENALTY",   value: o.tokens_slashed > 0 ? `-$${(o.tokens_slashed||0).toLocaleString()}` : "None",
                color: o.tokens_slashed > 0 ? T.red : T.green },
            ].map(k => (
              <div key={k.label} style={{ textAlign: "center" }}>
                <Mono style={{ fontSize: 8, color: T.muted, display: "block",
                  marginBottom: 4 }}>{k.label}</Mono>
                <div style={{ fontFamily: "'Bebas Neue'", fontSize: 24,
                  color: k.color, lineHeight: 1 }}>{k.value ?? "—"}</div>
              </div>
            ))}
          </div>
        </Card>

        {/* Tabs */}
        <div style={{ display: "flex", gap: 0, marginBottom: 16,
          borderBottom: `1px solid ${T.border}` }}>
          {lTabs.map(t => (
            <button key={t} onClick={() => setOutletTab(t)} style={{
              background: "none", border: "none",
              borderBottom: outletTab === t ? `2px solid ${T.blue}` : "2px solid transparent",
              color: outletTab === t ? T.blue : T.muted,
              padding: "8px 16px", cursor: "pointer",
              fontFamily: "'DM Mono'", fontSize: 10, letterSpacing: "0.06em",
            }}>{t}</button>
          ))}
        </div>

        {/* OVERVIEW */}
        {outletTab === "OVERVIEW" && (
          <div className="two-col-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            <Card style={{ padding: 20 }}>
              <SectionLabel>CREDIBILITY DISTRIBUTION</SectionLabel>
              {artLoading ? <Spinner /> : [
                { label: "Reliable (82+)",    count: reliable,    color: T.green },
                { label: "At Risk (60-81)",   count: atRisk,      color: T.yellow },
                { label: "Blacklisted (<60)", count: blacklisted, color: T.red },
              ].map(row => (
                <div key={row.label} style={{ marginBottom: 14 }}>
                  <div style={{ display: "flex", justifyContent: "space-between",
                    marginBottom: 5 }}>
                    <Mono style={{ fontSize: 10, color: T.text }}>{row.label}</Mono>
                    <Mono style={{ fontSize: 10, color: row.color }}>
                      {row.count} ({Math.round(row.count/total*100)}%)
                    </Mono>
                  </div>
                  <div style={{ height: 4, background: T.border, borderRadius: 2 }}>
                    <div style={{ width: `${Math.round(row.count/total*100)}%`,
                      height: "100%", background: row.color,
                      borderRadius: 2, transition: "width 0.8s ease" }} />
                  </div>
                </div>
              ))}
            </Card>
            <Card style={{ padding: 20 }}>
              <SectionLabel>OUTLET PROFILE</SectionLabel>
              {[
                { label: "Name",      value: o.name },
                { label: "Country",   value: o.country },
                { label: "Category",  value: o.category },
                { label: "Status",    value: o.status },
                { label: "Avg VCI",   value: o.avg_vci },
                { label: "Articles",  value: o.article_count },
              ].map(row => (
                <div key={row.label} style={{ display: "flex",
                  justifyContent: "space-between", padding: "8px 0",
                  borderBottom: `1px solid ${T.border}` }}>
                  <Mono style={{ fontSize: 10, color: T.muted }}>{row.label}</Mono>
                  <Mono style={{ fontSize: 10, color: T.text }}>{row.value || "—"}</Mono>
                </div>
              ))}
            </Card>
          </div>
        )}

        {/* ARTICLES */}
        {outletTab === "ARTICLES" && (
          <Card style={{ padding: 0 }}>
            <div style={{ padding: "12px 18px",
              borderBottom: `1px solid ${T.border}`,
              background: T.surface }}>
              <Mono style={{ fontSize: 9, color: T.muted, letterSpacing: "0.1em" }}>
                RECENT ARTICLES — {o.name.toUpperCase()}
              </Mono>
            </div>
            {artLoading ? <Spinner /> : outletArticles.map((a, i) => (
              <div key={a.id || i}
                onClick={() => a.url && window.open(a.url, "_blank")}
                style={{ display: "flex", alignItems: "center", gap: 12,
                  padding: "12px 18px",
                  borderBottom: i < outletArticles.length-1
                    ? `1px solid ${T.border}` : "none",
                  cursor: a.url ? "pointer" : "default",
                  transition: "opacity 0.15s" }}
                onMouseOver={e => e.currentTarget.style.opacity="0.75"}
                onMouseOut={e => e.currentTarget.style.opacity="1"}>
                <ScoreRing score={a.vci_score} size={40} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 500, color: T.text,
                    marginBottom: 3, overflow: "hidden", textOverflow: "ellipsis",
                    whiteSpace: "nowrap" }}>{a.title}</div>
                  <Mono style={{ fontSize: 9, color: T.muted }}>
                    {a.category} · {new Date(a.published_at || a.scraped_at).toLocaleDateString()}
                  </Mono>
                </div>
                <Badge status={a.vci_status} />
              </div>
            ))}
          </Card>
        )}

        {/* CREDIBILITY */}
        {outletTab === "CREDIBILITY" && (
          <div className="two-col-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            <Card style={{ padding: 20 }}>
              <SectionLabel>SIGNAL RECORD</SectionLabel>
              {[
                { label: "Verified signals",  value: o.verified_signals || 0,  color: T.green },
                { label: "Refuted signals",   value: o.refuted_signals || 0,   color: T.red },
                { label: "Total penalty",     value: o.tokens_slashed > 0 ? `-$${(o.tokens_slashed||0).toLocaleString()}` : "None",
                  color: o.tokens_slashed > 0 ? T.red : T.green },
              ].map(row => (
                <div key={row.label} style={{ display: "flex",
                  justifyContent: "space-between", padding: "12px 0",
                  borderBottom: `1px solid ${T.border}` }}>
                  <Mono style={{ fontSize: 10, color: T.muted }}>{row.label}</Mono>
                  <Mono style={{ fontSize: 13, color: row.color,
                    fontWeight: 700 }}>{row.value}</Mono>
                </div>
              ))}
            </Card>
            <Card style={{ padding: 20 }}>
              <SectionLabel>CREDIBILITY ASSESSMENT</SectionLabel>
              <div style={{ padding: 16, background: `${vciColor(o.avg_vci)}10`,
                borderRadius: 8, border: `1px solid ${vciColor(o.avg_vci)}20`,
                marginBottom: 16 }}>
                <div style={{ fontFamily: "'Bebas Neue'", fontSize: 32,
                  color: vciColor(o.avg_vci), marginBottom: 4 }}>
                  {o.status?.toUpperCase()}
                </div>
                <div style={{ fontSize: 13, color: T.muted, lineHeight: 1.6 }}>
                  {o.status === "Reliable"
                    ? `${o.name} demonstrates consistently high editorial standards with an average VCI of ${o.avg_vci}. Suitable for citation without additional verification.`
                    : o.status === "At Risk"
                    ? `${o.name} shows mixed credibility signals with an average VCI of ${o.avg_vci}. Some articles score well while others raise concerns.`
                    : `${o.name} has a low average VCI of ${o.avg_vci}. Exercise significant caution when citing articles from this outlet.`
                  }
                </div>
              </div>
            </Card>
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: "flex", gap: 10, marginBottom: 18, alignItems: "center" }}>
        <SectionLabel>OUTLET LEADERBOARD</SectionLabel>
        <div style={{ display: "flex", gap: 8, marginLeft: "auto" }}>
          <select value={country} onChange={e => setCountry(e.target.value)} style={{
            background: T.surface, border: `1px solid ${T.border}`,
            color: T.text, borderRadius: 4, padding: "5px 10px",
            fontFamily: "'DM Mono', monospace", fontSize: 10, cursor: "pointer",
          }}>
            {countries.map(c => (
              <option key={c} value={c}>{c || "All Countries"}</option>
            ))}
          </select>
        </div>
      </div>

      {loading ? <Spinner /> : error ? <ErrorBox message={error} onRetry={refetch} /> : (
        <Card>
          {/* Header */}
          <div style={{ display: "grid",
            gridTemplateColumns: "28px 1fr 70px 80px 100px",
            gap: 14, padding: "10px 18px",
            borderBottom: `1px solid ${T.border}`, background: T.surfaceAlt }}>
            {["#","OUTLET","CAT","OCI","STATUS"].map(h => (
              <Mono key={h} style={{ fontSize: 8, color: T.muted, letterSpacing: "0.1em" }}>{h}</Mono>
            ))}
          </div>
          {(data?.outlets || []).map((o, i) => (
            <div key={o.name} onClick={() => openOutlet(o)} style={{
              display: "grid",
              gridTemplateColumns: "28px 1fr 70px 80px 100px",
              gap: 14, padding: "12px 18px", alignItems: "center",
              borderBottom: `1px solid #0a1120`,
              background: i % 2 === 0 ? T.surface : T.surfaceAlt,
              transition: "background 0.1s", cursor: "pointer",
            }}
            onMouseEnter={e => e.currentTarget.style.background = "#111827"}
            onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? T.surface : T.surfaceAlt}>
              <Mono style={{ fontSize: 10, color: T.muted }}>#{i+1}</Mono>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{o.name}</div>
                <Mono style={{ fontSize: 9, color: T.muted }}>{o.country}</Mono>
              </div>
              <Mono style={{ fontSize: 9, color: T.muted, background: T.border,
                padding: "2px 6px", borderRadius: 3 }}>{o.category === "Politics" ? "POL" : o.category === "Science" ? "SCI" : o.category === "Sports" ? "SPO" : o.category === "Business" ? "BUS" : o.category === "Technology" ? "TEC" : o.category === "News" ? "NEWS" : (o.category?.slice(0,3).toUpperCase() || "—")}</Mono>
              <div style={{ textAlign: "center" }}>
                <ScoreRing score={o.oci_score || o.avg_vci} size={42} />
              </div>
              <Badge status={o.status} />
            </div>
          ))}
        </Card>
      )}
    </div>
  );
}

// ============================================================
// MAP TAB
// ============================================================
function MapTab() {
  const { data, loading, error, refetch } = useAPI("/map");
  return (
    <div>
      <SectionLabel>GLOBAL INFORMATION INTEGRITY MAP</SectionLabel>
      {loading ? <Spinner /> : error ? <ErrorBox message={error} onRetry={refetch} /> : (
        <>
          <WorldMap hotspots={data?.hotspots || []} />
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)",
            gap: 12, marginTop: 16 }}>
            {(data?.hotspots || []).map(h => (
              <Card key={h.country_code} style={{
                padding: 14, display: "flex", gap: 12, alignItems: "center",
                borderLeft: `3px solid ${vciColor(h.avg_vci)}`,
              }}>
                <ScoreRing score={h.avg_vci} size={48} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{h.country_name}</div>
                  <Mono style={{ fontSize: 9, color: T.muted, display: "block", marginBottom: 3 }}>
                    {h.article_count} articles · {h.outlets.length} outlets
                  </Mono>
                  <Mono style={{ fontSize: 9, color: T.muted }}>{h.top_incident}</Mono>
                </div>
                {h.simulated_penalty > 0 && (
                  <Mono style={{ fontSize: 10, color: T.red }}>
                    -${h.simulated_penalty.toLocaleString()}
                  </Mono>
                )}
              </Card>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ============================================================
// ARTICLES TAB
// ============================================================
function ArticlesTab({ initialArticleId, onClose }) {
  const [page,     setPage]     = useState(1);
  const [category, setCategory] = useState("");
  const [status,   setStatus]   = useState("");
  const [risk,     setRisk]     = useState("");
  const [selected, setSelected] = useState(initialArticleId || null);

  // When initialArticleId changes, select that article
  useEffect(() => {
    if (initialArticleId) setSelected(initialArticleId);
  }, [initialArticleId]);

  const path = `/articles?page=${page}&per_page=15&sort_by=scraped_at&order=desc${category ? `&category=${category}` : ""}${status ? `&status=${status}` : ""}${risk ? `&risk=${risk}` : ""}`;
  const { data, loading, error, refetch } = useAPI(path, [page, category, status, risk]);

  const { data: detail, loading: dL } = useAPI(
    selected ? `/articles/${selected}` : null, [selected]
  );

  const [detailTab, setDetailTab] = useState("OVERVIEW");
  const [enriched, setEnriched] = React.useState(null);
  const [enriching, setEnriching] = React.useState(false);

  React.useEffect(() => {
    setEnriched(null);
    if (!selected) return;
  }, [selected]);

  React.useEffect(() => {
    if (!detail) return;
    if (detail.result_json && detail.result_json.framing) {
      setEnriched(detail.result_json);
    }
  }, [detail]);

  const [enrichError, setEnrichError] = React.useState(null);
  const { shareUrl: artShareUrl, setShareUrl: setArtShareUrl, sharing: artSharing, share: artShare } = useShare();
  const runEnrich = () => {
    if (!selected) return;
    setEnriching(true);
    setEnrichError(null);
    apiFetch(`/articles/${selected}/enrich`, { method: 'POST' })
      .then(r => { setEnriched(r); setEnriching(false); })
      .catch(() => {
        setEnriching(false);
        setEnrichError("Full analysis unavailable — this outlet restricts automated access.");
      });
  };


  if (selected) {
    const d = detail;
    const r = enriched; // same shape as scan result

    const vciColor = (score) =>
      score >= 82 ? T.green : score >= 65 ? T.yellow : T.red;
    const credColor = (c) =>
      c === "HIGH" ? T.green : c === "MEDIUM" ? T.yellow : c === "LOW" ? T.red : T.muted;
    const typeColor = (t) => {
      if (t === "STATE_MEDIA" || t === "SOCIAL_MEDIA") return T.red;
      if (t === "ANONYMOUS") return T.yellow;
      if (t === "INTERNATIONAL_ORG" || t === "EXPERT") return T.green;
      return T.muted;
    };

    return (
      <div style={{ animation: "fadeIn 0.2s ease" }}>
        <button onClick={() => { setSelected(null); setDetailTab("OVERVIEW"); if(onClose) onClose(); }} style={{
          background: "none", border: `1px solid ${T.border}`, color: T.muted,
          borderRadius: 4, padding: "6px 14px", cursor: "pointer", marginBottom: 20,
          fontFamily: "'DM Mono'", fontSize: 10,
        }}>← BACK TO ARTICLES</button>

        {dL ? <Spinner /> : d && (
          <div>

            {/* Score header */}
            <Card style={{ padding: 24, marginBottom: 16,
              borderColor: vciColor(d.vci_score),
              background: `linear-gradient(135deg, ${T.surface}, ${vciColor(d.vci_score)}08)` }}>
              <div style={{ display: "flex", gap: 24, alignItems: "center", flexWrap: "wrap" }}>
                <div style={{ textAlign: "center", flexShrink: 0 }}>
                  <ScoreRing score={d.vci_score} size={90} />
                  <div style={{ display: "flex", gap: 4, marginTop: 6, justifyContent: "center" }}>
                    <Mono style={{ fontSize: 7, color: T.green }}>82+ RELIABLE</Mono>
                    <Mono style={{ fontSize: 7, color: T.muted }}>·</Mono>
                    <Mono style={{ fontSize: 7, color: T.yellow }}>65 AT RISK</Mono>
                    <Mono style={{ fontSize: 7, color: T.muted }}>·</Mono>
                    <Mono style={{ fontSize: 7, color: T.red }}>&lt;65 BLACKLISTED</Mono>
                  </div>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 6 }}>
                    {d.title || "Article Analysis"}
                  </div>
                  <div style={{ display: "flex", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
                    <Badge status={d.vci_status} />
                    {d.category && (
                      <Mono style={{ fontSize: 9, background: T.surfaceAlt,
                        border: `1px solid ${T.border}`, padding: "3px 8px",
                        borderRadius: 3, color: T.muted }}>{d.category.toUpperCase()}</Mono>
                    )}
                    {d.outlet && (
                      <Mono style={{ fontSize: 9, background: T.surfaceAlt,
                        border: `1px solid ${T.border}`, padding: "3px 8px",
                        borderRadius: 3, color: T.muted }}>{d.outlet}</Mono>
                    )}
                    {d.author && (
                      <Mono style={{ fontSize: 9, background: T.surfaceAlt,
                        border: `1px solid ${T.border}`, padding: "3px 8px",
                        borderRadius: 3, color: T.muted }}>✍ {d.author}</Mono>
                    )}
                  </div>
                  <div style={{ fontSize: 13, color: T.muted, lineHeight: 1.6 }}>
                    {r?.summary || d.summary || d.executive_summary || ""}
                  </div>
                </div>
                <a href={d.url} target="_blank" rel="noreferrer" style={{ flexShrink: 0 }}>
                  <button style={{ background: T.blue, border: "none", color: "white",
                    borderRadius: 4, padding: "8px 14px", cursor: "pointer",
                    fontFamily: "'DM Mono'", fontSize: 9, letterSpacing: "0.08em" }}>
                    VIEW ORIGINAL →
                  </button>
                </a>
                <button onClick={() => artShare({ vci_score: d.vci_score, verdict: d.vci_status, outlet: d.outlet, title: d.title, summary: d.summary })}
                  disabled={artSharing} style={{
                  background: `${T.blue}20`, border: `1px solid ${T.blue}`,
                  color: T.blue, borderRadius: 4, padding: "8px 14px",
                  cursor: "pointer", fontFamily: "'DM Mono'", fontSize: 9,
                  letterSpacing: "0.08em", flexShrink: 0,
                }}>{artSharing ? "..." : "↗ SHARE"}</button>
                <ShareModal shareUrl={artShareUrl} onClose={() => setArtShareUrl(null)} />
                {!enriched && (
                  <button onClick={runEnrich} disabled={enriching} style={{
                    background: enriching ? T.surfaceAlt : `${T.blue}20`,
                    border: `1px solid ${enriching ? T.border : T.blue}`,
                    color: enriching ? T.muted : T.blue,
                    borderRadius: 4, padding: "8px 14px", cursor: enriching ? "default" : "pointer",
                    fontFamily: "'DM Mono'", fontSize: 9, letterSpacing: "0.08em",
                    flexShrink: 0,
                  }}>
                    {enriching ? "ANALYSING..." : "⚡ GET FULL ANALYSIS"}
                  </button>
                )}
              </div>

              {/* Reasoning */}
              {r?.reasoning && (
                <div style={{ marginTop: 16, paddingTop: 16,
                  borderTop: `1px solid ${T.border}`,
                  fontSize: 12, color: T.muted, lineHeight: 1.6, fontStyle: "italic" }}>
                  {r.reasoning}
                </div>
              )}
            </Card>

            {enriching && (
              <div style={{ textAlign: "center", padding: 20 }}>
                <Spinner />
                <Mono style={{ fontSize: 10, color: T.muted, marginTop: 8 }}>
                  LOADING ANALYSIS...
                </Mono>
              </div>
            )}

            {enrichError && (
              <div style={{ background: `${T.yellow}10`, border: `1px solid ${T.yellow}40`,
                borderRadius: 6, padding: "12px 16px", marginBottom: 16,
                display: "flex", gap: 10, alignItems: "center" }}>
                <span>⚠️</span>
                <Mono style={{ fontSize: 10, color: T.yellow }}>{enrichError}</Mono>
              </div>
            )}

            {/* Framing analysis */}
            {r?.framing && Object.keys(r.framing).length > 0 && (
              <Card style={{ padding: 20, marginBottom: 16 }}>
                <Mono style={{ fontSize: 9, color: T.muted, letterSpacing: "0.1em",
                  display: "block", marginBottom: 14 }}>FRAMING ANALYSIS</Mono>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)",
                  gap: 10, marginBottom: 14 }}>
                  {[
                    { label: "SENSATIONAL",       val: r.framing.is_sensational },
                    { label: "ONE-SIDED",          val: r.framing.is_one_sided },
                    { label: "EMOTIONAL LANGUAGE", val: r.framing.has_emotional_language },
                  ].map(({ label, val }) => (
                    <div key={label} style={{
                      background: val ? `${T.red}15` : `${T.green}15`,
                      border: `1px solid ${val ? T.red : T.green}40`,
                      borderRadius: 6, padding: "10px 14px", textAlign: "center" }}>
                      <div style={{ fontSize: 11, fontWeight: 700, marginBottom: 4,
                        color: val ? T.red : T.green }}>
                        {val ? "⚠ FLAGGED" : "✓ PASS"}
                      </div>
                      <Mono style={{ fontSize: 8, color: T.muted,
                        letterSpacing: "0.08em" }}>{label}</Mono>
                    </div>
                  ))}
                </div>
                {r.framing.narrative_push && (
                  <div style={{ marginBottom: 10 }}>
                    <Mono style={{ fontSize: 8, color: T.yellow,
                      letterSpacing: "0.08em", display: "block", marginBottom: 4 }}>
                      NARRATIVE PUSH
                    </Mono>
                    <div style={{ fontSize: 12, color: T.muted, lineHeight: 1.6 }}>
                      {r.framing.narrative_push}
                    </div>
                  </div>
                )}
                {r.framing.missing_context && (
                  <div>
                    <Mono style={{ fontSize: 8, color: T.muted,
                      letterSpacing: "0.08em", display: "block", marginBottom: 4 }}>
                      MISSING CONTEXT
                    </Mono>
                    <div style={{ fontSize: 12, color: T.muted, lineHeight: 1.6 }}>
                      {r.framing.missing_context}
                    </div>
                  </div>
                )}
              </Card>
            )}

            {/* Sources */}
            {r?.sources?.length > 0 && (
              <Card style={{ marginBottom: 16 }}>
                <div style={{ padding: "12px 18px", borderBottom: `1px solid ${T.border}`,
                  background: T.surfaceAlt }}>
                  <Mono style={{ fontSize: 9, color: T.muted, letterSpacing: "0.1em" }}>
                    SOURCES CITED ({r.sources.length})
                  </Mono>
                </div>
                {r.sources.map((s, i) => (
                  <div key={i} style={{
                    display: "flex", gap: 12, padding: "10px 18px", alignItems: "center",
                    borderBottom: i < r.sources.length - 1 ? `1px solid ${T.border}` : "none",
                    background: i % 2 === 0 ? T.surface : T.surfaceAlt,
                  }}>
                    <div style={{ fontSize: 13, fontWeight: 500, flex: 1 }}>{s.entity}</div>
                    <Mono style={{ fontSize: 9, color: typeColor(s.type),
                      background: `${typeColor(s.type)}15`,
                      border: `1px solid ${typeColor(s.type)}30`,
                      padding: "2px 8px", borderRadius: 3,
                      whiteSpace: "nowrap" }}>{SOURCE_TYPE_LABELS[s.type] || s.type}</Mono>
                    <Mono style={{ fontSize: 9, color: credColor(s.credibility),
                      minWidth: 60, textAlign: "right" }}>{CRED_LABELS[s.credibility] || s.credibility}</Mono>
                  </div>
                ))}
              </Card>
            )}

            {/* Claims */}
            {r?.claims?.length > 0 && (
              <Card style={{ padding: 20, marginBottom: 16 }}>
                <Mono style={{ fontSize: 9, color: T.muted, letterSpacing: "0.1em",
                  display: "block", marginBottom: 12 }}>KEY CLAIMS</Mono>
                {r.claims.map((c, i) => (
                  <div key={i} style={{ display: "flex", gap: 10, marginBottom: 8,
                    alignItems: "flex-start" }}>
                    <Mono style={{ fontSize: 9, color: T.blue, minWidth: 20 }}>{i + 1}.</Mono>
                    <div style={{ fontSize: 12, color: T.muted, lineHeight: 1.6 }}>{c}</div>
                  </div>
                ))}
              </Card>
            )}

            {/* Fact checks */}
            {r?.fact_checks?.length > 0 && (
              <Card style={{ padding: 20, marginBottom: 16 }}>
                <Mono style={{ fontSize: 9, color: T.muted, letterSpacing: "0.1em",
                  display: "block", marginBottom: 12 }}>RELATED FACT CHECKS</Mono>
                {r.fact_checks.map((fc, i) => {
                  const vc = fc.verdict_norm === "FALSE" ? T.red :
                             fc.verdict_norm === "MISLEADING" ? T.yellow :
                             fc.verdict_norm === "TRUE" ? T.green : T.muted;
                  return (
                    <div key={i} style={{ borderLeft: `3px solid ${vc}`,
                      paddingLeft: 14, marginBottom: 12 }}>
                      <div style={{ display: "flex", gap: 8, alignItems: "center",
                        marginBottom: 4 }}>
                        <Mono style={{ fontSize: 9, color: vc,
                          background: `${vc}20`, border: `1px solid ${vc}`,
                          padding: "2px 6px", borderRadius: 3 }}>{fc.verdict_norm}</Mono>
                        <Mono style={{ fontSize: 9, color: T.muted }}>{fc.publisher}</Mono>
                      </div>
                      <div style={{ fontSize: 12, color: T.muted }}>{fc.claim}</div>
                    </div>
                  );
                })}
              </Card>
            )}

            {/* Penalties */}
            {(r?.penalties?.framing > 0 || r?.penalties?.sources > 0) && (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)",
                gap: 10, marginBottom: 16 }}>
                {[
                  { label: "VCI SCORE",       value: d.vci_score,                    color: vciColor(d.vci_score) },
                  { label: "FRAMING PENALTY", value: `-${r.penalties?.framing || 0}`, color: T.red },
                  { label: "SOURCE PENALTY",  value: `-${r.penalties?.sources || 0}`, color: T.red },
                ].map(({ label, value, color }) => (
                  <Card key={label} style={{ padding: "14px 18px", textAlign: "center" }}>
                    <div style={{ fontFamily: "'Bebas Neue', sans-serif",
                      fontSize: 28, color }}>{value}</div>
                    <Mono style={{ fontSize: 8, color: T.muted,
                      letterSpacing: "0.1em" }}>{label}</Mono>
                  </Card>
                ))}
              </div>
            )}

          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: 20, display: "flex", gap: 10, flexWrap: "wrap" }}>
        {[["", "ALL"], ["Politics","POLITICS"], ["Business","BUSINESS"], ["Science","SCIENCE"],
          ["Sports","SPORTS"], ["News","NEWS"], ["Technology","TECHNOLOGY"]].map(([val, label]) => (
          <button key={val} onClick={() => { setCategory(val); setPage(1); }}
            style={{ background: category === val ? T.blue : T.surfaceAlt,
              border: `1px solid ${category === val ? T.blue : T.border}`,
              color: category === val ? "#fff" : T.muted,
              borderRadius: 4, padding: "5px 12px", cursor: "pointer",
              fontFamily: "'DM Mono'", fontSize: 9 }}>{label}</button>
        ))}
      </div>
      {loading ? <Spinner /> : (
        <div>
          {(data?.articles || []).map((a, i) => (
            <div key={a.id} onClick={() => setSelected(a.id)}
              style={{ display: "flex", gap: 12, padding: "12px 16px", cursor: "pointer",
                borderBottom: `1px solid ${T.border}`,
                background: i % 2 === 0 ? T.surface : T.surfaceAlt,
                alignItems: "center" }}>
              <div style={{ width: 42, height: 42, borderRadius: 6, flexShrink: 0,
                background: `${vciColor(a.vci_score)}15`,
                display: "flex", alignItems: "center", justifyContent: "center" }}>
                <div style={{ fontFamily: "'Bebas Neue'", fontSize: 18,
                  color: vciColor(a.vci_score) }}>{Math.round(a.vci_score)}</div>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 500, color: T.text,
                  marginBottom: 4, overflow: "hidden", textOverflow: "ellipsis",
                  whiteSpace: "nowrap" }}>{a.title}</div>
                <div style={{ display: "flex", gap: 10 }}>
                  <Mono style={{ fontSize: 9, color: T.blue }}>{a.outlet}</Mono>
                  <Mono style={{ fontSize: 9, color: T.muted }}>{a.category}</Mono>
                </div>
              </div>
              <Badge status={a.vci_status} />
            </div>
          ))}
          <div style={{ display: "flex", gap: 8, justifyContent: "center", padding: 16 }}>
            <button onClick={() => setPage(p => Math.max(1, p-1))} disabled={page === 1}
              style={{ background: T.surfaceAlt, border: `1px solid ${T.border}`,
                color: T.muted, borderRadius: 4, padding: "6px 14px", cursor: "pointer",
                fontFamily: "'DM Mono'", fontSize: 9 }}>← PREV</button>
            <Mono style={{ fontSize: 9, color: T.muted }}>PAGE {page}</Mono>
            <button onClick={() => setPage(p => p+1)}
              disabled={(data?.articles||[]).length < 15}
              style={{ background: T.surfaceAlt, border: `1px solid ${T.border}`,
                color: T.muted, borderRadius: 4, padding: "6px 14px", cursor: "pointer",
                fontFamily: "'DM Mono'", fontSize: 9 }}>NEXT →</button>
          </div>
        </div>
      )}
    </div>
  );
}


// ============================================================
// REPORTS TAB — Narrative Intelligence Reports
// ============================================================
function ReportsTab() {
  const [subject, setSubject]   = React.useState("");
  const [days, setDays]         = React.useState(90);
  const [language, setLanguage] = React.useState("en");
  const [loading, setLoading]   = React.useState(false);
  const [report, setReport]     = React.useState(null);
  const [error, setError]       = React.useState(null);

  const [shareUrl, setShareUrl]   = React.useState(null);
  const [showShareModal, setShowShareModal] = React.useState(false);
  const [sharing, setSharing]     = React.useState(false);
  const [copied, setCopied]       = React.useState(false);

  const generate = async () => {
    if (!subject.trim()) return;
    setLoading(true);
    setError(null);
    setReport(null);
    setShareUrl(null);
    try {
      const data = await apiFetch(`/report/subject?subject=${encodeURIComponent(subject)}&days=${days}&language=${language}`);
      setReport(data);
    } catch(e) {
      setError(e.message?.includes("Insufficient") 
        ? e.message 
        : "Failed to generate report. Try a different subject or date range.");
    }
    setLoading(false);
  };

  const handleShare = async () => {
    setSharing(true);
    try {
      const token = localStorage.getItem("vs_token");
      const res = await fetch(`${API_BASE}/share`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({
          vci_score:   Math.round(report.avg_vci),
          verdict:     report.verdict,
          outlet:      "Narrative Intelligence Report",
          title:       `${report.subject} — Narrative Intelligence Report`,
          summary:     (report.narrative || "").slice(0, 300).replace(/^#+\s*/gm, "").replace(/\*\*(.*?)\*\*/g, "$1"),
          result_json: { ...report, is_report: true },
        }),
      });
      const data = await res.json();
      const url = `https://verdadscore.com/share/${data.id}`;
      setShareUrl(url);
      setShowShareModal(true);
    } catch(e) {
      console.error("Share failed:", e);
    }
    setSharing(false);
  };

  const handlePDF = () => {
    const win = window.open("", "_blank");
    if (!win) { alert("Please allow popups to export PDF"); return; }
    win.document.title = `Verdad Score — ${report.subject} Report`;
    const vciColor = s => s >= 82 ? "#16a34a" : s >= 65 ? "#D97706" : "#DC2626";
    const vciLabel = s => s >= 82 ? "Reliable" : s >= 65 ? "At Risk" : "Blacklisted";
    const gap = report.credibility_gap || 0;
    const gapColor = gap > 5 ? "#DC2626" : "#16a34a";
    const narrative = (report.narrative || "").replace(/^#+\s*/gm, "").replace(/\*\*(.*?)\*\*/g, "$1").trim();

    const toneColor = t => t === "Hostile" ? "#DC2626" : t === "Critical" ? "#D97706" : t === "Neutral" ? "#16a34a" : "#94A3B8";

    const authorRows = (report.authors || []).slice(0, 20).map(a => {
      const sc = vciColor(a.avg_vci);
      const tc = toneColor(a.tone);
      return `<tr>
        <td class="score-cell" style="color:${sc}">${a.avg_vci}</td>
        <td class="author-cell">
          <div class="author-name">${a.author}</div>
          <div class="author-meta">${a.outlet} · ${a.article_count} article${a.article_count !== 1 ? "s" : ""}</div>
        </td>
        <td class="status-cell">
          <span class="badge" style="color:${tc};background:${tc}18;border-color:${tc}40">${a.tone || a.status}</span>
        </td>
      </tr>`;
    }).join("");

    // Drivers section — authors with 2+ articles and drivers
    const driversHTML = (report.authors || [])
      .filter(a => a.drivers && a.drivers.length > 0 && a.article_count >= 2 && a.avg_vci < 82)
      .slice(0, 8)
      .map(a => {
        const sc = vciColor(a.avg_vci);
        const tc = toneColor(a.tone);
        return `<div class="driver-block">
          <div class="driver-header">
            <span class="driver-name">${a.author}</span>
            <span class="driver-meta">${a.outlet} · ${a.article_count} articles · VCI ${a.avg_vci}</span>
            <span class="driver-tone" style="color:${tc}">${a.tone || ""}</span>
          </div>
          <div class="driver-list">
            ${(a.drivers || []).map(d => `<div class="driver-item">• ${d}</div>`).join("")}
          </div>
        </div>`;
      }).join("");

    // Briefing targets
    const briefingHTML = (report.briefing_targets || []).map(a => {
      return `<tr>
        <td class="author-cell">
          <div class="author-name">${a.author}</div>
          <div class="author-meta">${a.outlet}</div>
        </td>
        <td class="score-cell" style="color:#16a34a">${a.avg_vci}</td>
        <td style="padding:10px;font-family:monospace;font-size:9px;color:#16a34a">${a.article_count} articles</td>
        <td class="status-cell">
          <span class="badge" style="color:#16a34a;background:#16a34a18;border-color:#16a34a40">HIGH QUALITY</span>
        </td>
      </tr>`;
    }).join("");

    const patternRows = (report.patterns || []).map(p =>
      `<div class="pattern-row">⚠ ${p}</div>`
    ).join("");

    win.document.write(`<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Verdad Score — ${report.subject} Report</title>
  <style>
    @media print {
      @page { size: A4; margin: 12mm 15mm; }
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .no-break { page-break-inside: avoid; }
      .page-break { page-break-before: always; }
    }
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      background: #fff; color: #0F172A;
      padding: 48px 52px; font-size: 13px; line-height: 1.6;
    }
    /* HEADER */
    .header {
      display: flex; align-items: flex-start;
      justify-content: space-between;
      border-bottom: 2px solid #0F172A;
      padding-bottom: 20px; margin-bottom: 28px;
    }
    .header-left { flex: 1; }
    .brand-row {
      display: flex; align-items: center; gap: 10px; margin-bottom: 16px;
    }
    .brand-text {
      font-family: monospace; font-size: 9px;
      letter-spacing: 4px; color: #16a34a; font-weight: 600;
    }
    .report-subject {
      font-size: 32px; font-weight: 800; letter-spacing: -0.5px;
      color: #0F172A; margin-bottom: 8px; line-height: 1.1;
    }
    .report-type {
      font-family: monospace; font-size: 9px;
      letter-spacing: 3px; color: #64748B;
    }
    .verdict-badge {
      display: inline-block; padding: 5px 14px; border-radius: 4px;
      font-family: monospace; font-size: 10px; font-weight: 600;
      letter-spacing: 1px; margin-top: 12px;
    }
    /* STATS */
    .stats {
      display: grid; grid-template-columns: repeat(4,1fr);
      gap: 14px; margin-bottom: 28px;
    }
    .stat {
      border: 1px solid #E2E8F0; border-radius: 8px;
      padding: 14px 12px; text-align: center;
      background: #F8FAFC;
    }
    .stat-value {
      font-size: 30px; font-weight: 800;
      line-height: 1; margin-bottom: 6px;
    }
    .stat-label {
      font-family: monospace; font-size: 8px;
      color: #94A3B8; letter-spacing: 1.5px;
    }
    /* SECTIONS */
    .section { margin-bottom: 28px; }
    .section-title {
      font-family: monospace; font-size: 8px;
      letter-spacing: 3px; color: #94A3B8;
      border-bottom: 1px solid #E2E8F0;
      padding-bottom: 8px; margin-bottom: 14px;
      text-transform: uppercase;
    }
    /* NARRATIVE */
    .narrative {
      font-size: 13px; line-height: 1.85; color: #334155;
      padding: 16px 20px; background: #F8FAFC;
      border-left: 3px solid #16a34a; border-radius: 0 6px 6px 0;
    }
    /* PATTERNS */
    .pattern-row {
      font-size: 12px; color: #7C3AED; padding: 8px 12px;
      background: #F5F3FF; border-radius: 4px;
      margin-bottom: 6px; font-weight: 500;
    }
    /* AUTHORS TABLE */
    .authors-table {
      width: 100%; border-collapse: collapse;
    }
    .authors-table th {
      font-family: monospace; font-size: 8px;
      letter-spacing: 2px; color: #94A3B8;
      text-align: left; padding: 6px 10px;
      border-bottom: 1px solid #E2E8F0;
    }
    .authors-table tr:nth-child(even) td { background: #F8FAFC; }
    .score-cell {
      font-size: 18px; font-weight: 800;
      padding: 10px; text-align: center; width: 56px;
    }
    .author-cell { padding: 10px 12px; }
    .author-name { font-size: 13px; font-weight: 600; color: #0F172A; }
    .author-meta {
      font-family: monospace; font-size: 9px;
      color: #64748B; margin-top: 2px;
    }
    .status-cell { padding: 10px; text-align: right; }
    .badge {
      display: inline-block; padding: 3px 10px;
      border-radius: 3px; font-family: monospace;
      font-size: 8px; font-weight: 600;
      letter-spacing: 1px; border: 1px solid;
    }
    /* DRIVERS */
    .driver-block {
      border: 1px solid #E2E8F0; border-radius: 6px;
      padding: 14px 16px; margin-bottom: 8px; background: #F8FAFC;
    }
    .driver-header {
      display: flex; align-items: baseline; gap: 12px; margin-bottom: 8px;
    }
    .driver-name {
      font-size: 13px; font-weight: 700; color: #0F172A;
    }
    .driver-meta {
      font-family: monospace; font-size: 9px; color: #94A3B8;
    }
    .driver-tone {
      font-family: monospace; font-size: 9px; font-weight: 700;
      margin-left: auto;
    }
    .driver-list { padding-left: 4px; }
    .driver-item {
      font-size: 12px; color: #D97706; padding: 2px 0; line-height: 1.5;
    }
    /* BRIEFING */
    .briefing-intro {
      font-size: 12px; color: #475569; margin-bottom: 12px; line-height: 1.6;
      padding: 12px 16px; background: #F0FDF4;
      border-left: 3px solid #16a34a; border-radius: 0 6px 6px 0;
    }
    /* FOOTER */
    .footer {
      margin-top: 48px; padding-top: 16px;
      border-top: 1px solid #E2E8F0;
      display: flex; justify-content: space-between;
      align-items: center;
    }
    .footer-brand {
      display: flex; align-items: center; gap: 8px;
    }
    .footer-text {
      font-family: monospace; font-size: 9px;
      color: #94A3B8; letter-spacing: 1px;
    }
    .footer-right {
      font-family: monospace; font-size: 9px; color: #CBD5E1;
    }
  </style>
</head>
<body>
  <div class="header no-break">
    <div class="header-left">
      <div class="brand-row">
        <svg width="24" height="24" viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
          <g transform="translate(25,28)">
            <rect x="0"   y="62" width="14" height="48"  rx="7" fill="#16a34a" opacity="0.25"/>
            <rect x="22"  y="48" width="14" height="76"  rx="7" fill="#16a34a" opacity="0.45"/>
            <rect x="44"  y="54" width="14" height="64"  rx="7" fill="#16a34a" opacity="0.35"/>
            <rect x="66"  y="28" width="14" height="116" rx="7" fill="#16a34a" opacity="0.65"/>
            <rect x="88"  y="4"  width="14" height="164" rx="7" fill="#16a34a" opacity="1"/>
            <rect x="110" y="28" width="14" height="116" rx="7" fill="#16a34a" opacity="0.65"/>
            <rect x="132" y="54" width="14" height="64"  rx="7" fill="#16a34a" opacity="0.35"/>
            <rect x="154" y="48" width="14" height="76"  rx="7" fill="#16a34a" opacity="0.45"/>
            <rect x="176" y="62" width="14" height="48"  rx="7" fill="#16a34a" opacity="0.25"/>
          </g>
        </svg>
        <span class="brand-text">VERDAD SCORE</span>
      </div>
      <div class="report-subject">${report.subject.toUpperCase()}</div>
      <div class="report-type">NARRATIVE INTELLIGENCE REPORT</div>
      <div class="verdict-badge" style="color:${vciColor(report.avg_vci)};background:${vciColor(report.avg_vci)}18;border:1px solid ${vciColor(report.avg_vci)}40">
        ${report.verdict?.toUpperCase() || vciLabel(report.avg_vci).toUpperCase()}
      </div>
    </div>
  </div>

  <div class="stats no-break">
    <div class="stat">
      <div class="stat-value" style="color:#3B82F6">${report.articles_analysed}</div>
      <div class="stat-label">ARTICLES</div>
    </div>
    <div class="stat">
      <div class="stat-value" style="color:${vciColor(report.avg_vci)}">${report.avg_vci}</div>
      <div class="stat-label">AVG VCI</div>
    </div>
    <div class="stat">
      <div class="stat-value" style="color:#DC2626">${report.blacklisted}</div>
      <div class="stat-label">BLACKLISTED</div>
    </div>
    <div class="stat">
      <div class="stat-value" style="color:${gapColor}">${gap > 0 ? "−" : "+"}${Math.abs(gap)}</div>
      <div class="stat-label">CRED GAP</div>
    </div>
  </div>

  <div class="section no-break">
    <div class="section-title">Executive Summary</div>
    <div class="narrative">${narrative}</div>
  </div>

  ${driversHTML ? `<div class="section"><div class="section-title">What Is Driving Low Scores</div>${driversHTML}</div>` : ""}

  ${briefingHTML ? `<div class="section no-break"><div class="section-title">Recommended Briefing Targets</div><div class="briefing-intro">These journalists are covering this subject to a higher journalistic standard. They are more likely to report with appropriate balance and source attribution.</div><table class="authors-table"><thead><tr><th>AUTHOR</th><th>AVG VCI</th><th>ARTICLES</th><th>QUALITY</th></tr></thead><tbody>${briefingHTML}</tbody></table></div>` : ""}

  ${patternRows ? '<div class="section no-break"><div class="section-title">Patterns Detected</div>' + patternRows + '</div>' : ""}

  <div class="section">
    <div class="section-title">Author Credibility Breakdown</div>
    <table class="authors-table">
      <thead>
        <tr>
          <th style="text-align:center">VCI</th>
          <th>AUTHOR</th>
          <th style="text-align:right">STATUS</th>
        </tr>
      </thead>
      <tbody>${authorRows}</tbody>
    </table>
  </div>

  <div class="footer">
    <div class="footer-brand">
      <svg width="16" height="16" viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
        <g transform="translate(25,28)">
          <rect x="0"   y="62" width="14" height="48"  rx="7" fill="#16a34a" opacity="0.25"/>
          <rect x="22"  y="48" width="14" height="76"  rx="7" fill="#16a34a" opacity="0.45"/>
          <rect x="44"  y="54" width="14" height="64"  rx="7" fill="#16a34a" opacity="0.35"/>
          <rect x="66"  y="28" width="14" height="116" rx="7" fill="#16a34a" opacity="0.65"/>
          <rect x="88"  y="4"  width="14" height="164" rx="7" fill="#16a34a" opacity="1"/>
          <rect x="110" y="28" width="14" height="116" rx="7" fill="#16a34a" opacity="0.65"/>
          <rect x="132" y="54" width="14" height="64"  rx="7" fill="#16a34a" opacity="0.35"/>
          <rect x="154" y="48" width="14" height="76"  rx="7" fill="#16a34a" opacity="0.45"/>
          <rect x="176" y="62" width="14" height="48"  rx="7" fill="#16a34a" opacity="0.25"/>
        </g>
      </svg>
      <span class="footer-text">VERDAD SCORE · verdadscore.com</span>
    </div>
    <div style="text-align:right;">
      <span class="footer-right">Generated ${new Date().toLocaleDateString("en-GB", { day:"2-digit", month:"short", year:"numeric" })}</span><br>
      <span class="footer-right">Verdad Score Intelligence &middot; verdadscore.com</span><br>
      <span class="footer-right">matthew@verdadscore.com</span>
    </div>
  </div>
</body>
</html>`);
    win.document.close();
    setTimeout(() => { win.print(); win.close(); }, 600);
  };

  const vciCol = s => s >= 82 ? T.green : s >= 65 ? T.yellow : T.red;

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <SectionLabel>NARRATIVE INTELLIGENCE REPORTS</SectionLabel>
        <div style={{ fontSize: 13, color: T.muted, marginTop: 4 }}>
          Analyse how media covers any person, company or topic. Identify credibility patterns and biased reporting.
        </div>
      </div>

      {/* Search bar */}
      <Card style={{ padding: 20, marginBottom: 20 }}>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <input
            value={subject}
            onChange={e => setSubject(e.target.value)}
            onKeyDown={e => e.key === "Enter" && generate()}
            placeholder="Enter subject name (e.g. Manuel Adorni, Donald Trump, Elon Musk...)"
            style={{
              flex: 1, minWidth: 200,
              background: T.surfaceAlt, border: `1px solid ${T.border}`,
              borderRadius: 6, padding: "10px 14px", color: T.text,
              fontFamily: "'DM Sans'", fontSize: 13, outline: "none",
            }}
          />
          <select value={days} onChange={e => setDays(Number(e.target.value))} style={{
            background: T.surfaceAlt, border: `1px solid ${T.border}`,
            borderRadius: 6, padding: "10px 14px", color: T.text,
            fontFamily: "'DM Mono'", fontSize: 10, outline: "none",
          }}>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={180}>Last 180 days</option>
            <option value={365}>Last 365 days</option>
          </select>
          <div style={{ display: "flex", gap: 0, borderRadius: 6,
            border: `1px solid ${T.border}`, overflow: "hidden" }}>
            {["en", "es"].map(lang => (
              <button key={lang} onClick={() => setLanguage(lang)} style={{
                background: language === lang ? T.blue : T.surface,
                border: "none", color: language === lang ? "#fff" : T.muted,
                padding: "10px 14px", cursor: "pointer",
                fontFamily: "'DM Mono'", fontSize: 10, letterSpacing: "0.08em",
              }}>{lang.toUpperCase()}</button>
            ))}
          </div>
          <button onClick={generate} disabled={loading || !subject.trim()} style={{
            background: loading ? T.surfaceAlt : T.blue,
            border: "none", color: loading ? T.muted : "#fff",
            borderRadius: 6, padding: "10px 24px", cursor: loading ? "default" : "pointer",
            fontFamily: "'DM Mono'", fontSize: 10, letterSpacing: "0.08em",
            whiteSpace: "nowrap",
          }}>{loading ? "GENERATING..." : "GENERATE REPORT"}</button>
        </div>
      </Card>

      {loading && (
        <div style={{ textAlign: "center", padding: 40 }}>
          <Spinner />
          <Mono style={{ fontSize: 10, color: T.muted, marginTop: 12, display: "block" }}>
            ANALYSING MEDIA COVERAGE — THIS MAY TAKE 15-30 SECONDS...
          </Mono>
        </div>
      )}

      {error && (
        <div style={{ background: `${T.red}10`, border: `1px solid ${T.red}30`,
          borderRadius: 6, padding: "12px 16px", color: T.red,
          fontFamily: "'DM Mono'", fontSize: 11 }}>{error}</div>
      )}

      {report && !loading && (
        <div style={{ animation: "fadeIn 0.3s ease" }}>

          {/* Header */}
          <Card style={{ padding: 24, marginBottom: 16,
            borderColor: report.credibility_gap > 5 ? T.red : T.green,
            background: `linear-gradient(135deg, ${T.surface}, ${report.credibility_gap > 5 ? T.red : T.green}08)` }}>
            <div style={{ display: "flex", justifyContent: "space-between",
              alignItems: "flex-start", flexWrap: "wrap", gap: 16 }}>
              <div>
                <Mono style={{ fontSize: 9, color: T.blue, letterSpacing: "0.15em",
                  display: "block", marginBottom: 8 }}>NARRATIVE INTELLIGENCE REPORT</Mono>
                <div style={{ fontSize: 24, fontWeight: 700, color: T.text,
                  marginBottom: 8 }}>{report.subject.toUpperCase()}</div>
                <div style={{ display: "inline-block", padding: "4px 12px",
                  borderRadius: 4, fontSize: 10, fontFamily: "'DM Mono'",
                  background: report.credibility_gap > 5 ? `${T.red}20` : `${T.green}20`,
                  color: report.credibility_gap > 5 ? T.red : T.green,
                  border: `1px solid ${report.credibility_gap > 5 ? T.red : T.green}40`,
                }}>{report.verdict}</div>
              <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                <button onClick={handleShare} disabled={sharing} style={{
                  background: sharing ? T.surfaceAlt : T.blue,
                  border: "none", color: sharing ? T.muted : "#fff",
                  borderRadius: 4, padding: "8px 16px", cursor: sharing ? "default" : "pointer",
                  fontFamily: "'DM Mono'", fontSize: 9, letterSpacing: "0.08em",
                }}>
                  {sharing ? "SHARING..." : "↗ SHARE REPORT"}
                </button>
                <button onClick={handlePDF} style={{
                  background: "transparent",
                  border: `1px solid ${T.border}`, color: T.text,
                  borderRadius: 4, padding: "8px 16px", cursor: "pointer",
                  fontFamily: "'DM Mono'", fontSize: 9, letterSpacing: "0.08em",
                }}>
                  ⬇ EXPORT PDF
                </button>
              </div>
              {showShareModal && (
                <ShareModal
                  shareUrl={shareUrl}
                  onClose={() => setShowShareModal(false)}
                />
              )}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)",
                gap: 16, textAlign: "center" }}>
                {[
                  { label: "ARTICLES",    value: report.articles_analysed, color: T.blue },
                  { label: "AVG VCI",     value: report.avg_vci,           color: vciCol(report.avg_vci) },
                  { label: "BLACKLISTED", value: report.blacklisted,       color: T.red },
                  { label: "CRED GAP",    value: `${report.credibility_gap > 0 ? "-" : "+"}${Math.abs(report.credibility_gap)}`, color: report.credibility_gap > 5 ? T.red : T.green },
                ].map(k => (
                  <div key={k.label}>
                    <div style={{ fontFamily: "'Bebas Neue'", fontSize: 32,
                      color: k.color, lineHeight: 1 }}>{k.value}</div>
                    <Mono style={{ fontSize: 8, color: T.muted }}>{k.label}</Mono>
                  </div>
                ))}
              </div>
            </div>
          </Card>

          {/* Narrative */}
          <Card style={{ padding: 24, marginBottom: 16 }}>
            <Mono style={{ fontSize: 9, color: T.muted, letterSpacing: "0.1em",
              display: "block", marginBottom: 12 }}>EXECUTIVE SUMMARY</Mono>
            <div style={{ fontSize: 13, color: T.text, lineHeight: 1.8,
              whiteSpace: "pre-wrap" }}>{report.narrative.replace(/^#+\s*/gm, "").replace(/\*\*(.*?)\*\*/g, "$1").trim()}</div>
          </Card>

          {/* Patterns */}
          {report.patterns?.length > 0 && (
            <Card style={{ padding: 20, marginBottom: 16,
              borderColor: T.red, background: `${T.red}05` }}>
              <Mono style={{ fontSize: 9, color: T.red, letterSpacing: "0.1em",
                display: "block", marginBottom: 12 }}>⚠ PATTERNS DETECTED</Mono>
              {report.patterns.map((p, i) => (
                <div key={i} style={{ fontSize: 12, color: T.text,
                  padding: "8px 0", borderBottom: i < report.patterns.length - 1
                    ? `1px solid ${T.border}` : "none" }}>
                  {p}
                </div>
              ))}
            </Card>
          )}

          {/* Author breakdown */}
          <Card style={{ marginBottom: 16 }}>
            <div style={{ padding: "12px 18px", borderBottom: `1px solid ${T.border}`,
              background: T.surfaceAlt }}>
              <Mono style={{ fontSize: 9, color: T.muted, letterSpacing: "0.1em" }}>
                AUTHOR CREDIBILITY BREAKDOWN
              </Mono>
            </div>
            {report.authors?.slice(0, 15).map((a, i) => (
              <div key={i} style={{
                display: "flex", gap: 12, padding: "10px 18px",
                alignItems: "center",
                borderBottom: i < Math.min(report.authors.length, 15) - 1
                  ? `1px solid ${T.border}` : "none",
                background: i % 2 === 0 ? T.surface : T.surfaceAlt,
              }}>
                <div style={{ width: 42, height: 42, borderRadius: 6, flexShrink: 0,
                  background: `${vciCol(a.avg_vci)}15`,
                  display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <div style={{ fontFamily: "'Bebas Neue'", fontSize: 16,
                    color: vciCol(a.avg_vci) }}>{a.avg_vci}</div>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: T.text,
                    marginBottom: 2 }}>{a.author}</div>
                  <Mono style={{ fontSize: 9, color: T.blue }}>{a.outlet}</Mono>
                </div>
                <div style={{ textAlign: "right", flexShrink: 0 }}>
                  {a.tone && (
                    <div style={{ fontSize: 10, fontFamily: "'DM Mono'", marginBottom: 3,
                      color: a.tone === "Hostile" ? T.red : a.tone === "Critical" ? T.yellow : T.green }}>
                      {a.tone}
                    </div>
                  )}
                  <div style={{ fontSize: 11, color: vciCol(a.avg_vci),
                    fontFamily: "'DM Mono'", marginBottom: 2 }}>{a.status}</div>
                  <Mono style={{ fontSize: 9, color: T.muted }}>{a.article_count} articles</Mono>
                  {a.delta !== null && a.delta !== undefined && (
                    <Mono style={{ fontSize: 9,
                      color: a.delta > 5 ? T.green : a.delta < -5 ? T.red : T.muted,
                      marginTop: 2 }}>
                      {a.delta > 0 ? "+" : ""}{a.delta} vs overall
                    </Mono>
                  )}
                </div>
              </div>
            ))}
          </Card>

          {/* What Is Driving Low Scores */}
          {report.authors?.some(a => a.drivers?.length > 0 && a.article_count >= 2) && (
            <Card style={{ marginBottom: 16 }}>
              <div style={{ padding: "12px 18px", borderBottom: `1px solid ${T.border}`,
                background: T.surfaceAlt }}>
                <Mono style={{ fontSize: 9, color: T.muted, letterSpacing: "0.1em" }}>
                  WHAT IS DRIVING LOW SCORES
                </Mono>
              </div>
              {report.authors
                .filter(a => a.drivers?.length > 0 && a.article_count >= 2 && a.avg_vci < 82)
                .slice(0, 8)
                .map((a, i) => (
                  <div key={i} style={{
                    padding: "12px 18px",
                    borderBottom: `1px solid ${T.border}`,
                    background: i % 2 === 0 ? T.surface : T.surfaceAlt,
                  }}>
                    <div style={{ display: "flex", justifyContent: "space-between",
                      alignItems: "baseline", marginBottom: 6 }}>
                      <div>
                        <span style={{ fontSize: 13, fontWeight: 600,
                          color: T.text }}>{a.author}</span>
                        <Mono style={{ fontSize: 9, color: T.muted,
                          marginLeft: 10 }}>{a.outlet} · {a.article_count} articles · VCI {a.avg_vci}</Mono>
                      </div>
                      {a.tone && (
                        <Mono style={{ fontSize: 9, fontWeight: 700,
                          color: a.tone === "Hostile" ? T.red : a.tone === "Critical" ? T.yellow : T.green }}>
                          {a.tone}
                        </Mono>
                      )}
                    </div>
                    {a.drivers.map((d, j) => (
                      <div key={j} style={{ fontSize: 11, color: T.yellow,
                        padding: "2px 0", lineHeight: 1.5 }}>• {d}</div>
                    ))}
                  </div>
                ))}
            </Card>
          )}

          {/* Briefing Targets */}
          {report.briefing_targets?.length > 0 && (
            <Card style={{ marginBottom: 16 }}>
              <div style={{ padding: "12px 18px", borderBottom: `1px solid ${T.border}`,
                background: T.surfaceAlt }}>
                <Mono style={{ fontSize: 9, color: T.muted, letterSpacing: "0.1em" }}>
                  RECOMMENDED BRIEFING TARGETS
                </Mono>
              </div>
              <div style={{ padding: "12px 18px", borderBottom: `1px solid ${T.border}`,
                background: T.surface }}>
                <div style={{ fontSize: 11, color: T.muted, lineHeight: 1.6 }}>
                  These journalists are covering this subject to a higher journalistic standard.
                  More likely to report with appropriate balance and source attribution.
                </div>
              </div>
              {report.briefing_targets.map((a, i) => (
                <div key={i} style={{
                  display: "flex", gap: 12, padding: "10px 18px", alignItems: "center",
                  borderBottom: i < report.briefing_targets.length - 1
                    ? `1px solid ${T.border}` : "none",
                  background: i % 2 === 0 ? T.surface : T.surfaceAlt,
                }}>
                  <div style={{ width: 42, height: 42, borderRadius: 6, flexShrink: 0,
                    background: `${T.green}15`,
                    display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <div style={{ fontFamily: "'Bebas Neue'", fontSize: 16,
                      color: T.green }}>{a.avg_vci}</div>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 500,
                      color: T.text, marginBottom: 2 }}>{a.author}</div>
                    <Mono style={{ fontSize: 9, color: T.blue }}>{a.outlet}</Mono>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: 10, color: T.green,
                      fontFamily: "'DM Mono'", marginBottom: 2 }}>HIGH QUALITY</div>
                    <Mono style={{ fontSize: 9, color: T.muted }}>{a.article_count} articles</Mono>
                  </div>
                </div>
              ))}
            </Card>
          )}

          {/* Comparison */}
          {report.comparison?.length > 0 && (
            <Card style={{ marginBottom: 16 }}>
              <div style={{ padding: "12px 18px", borderBottom: `1px solid ${T.border}`,
                background: T.surfaceAlt }}>
                <Mono style={{ fontSize: 9, color: T.muted, letterSpacing: "0.1em" }}>
                  COMPARISON COVERAGE (AVG VCI {report.comparison_avg})
                </Mono>
              </div>
              {report.comparison.map((c, i) => (
                <div key={i} style={{
                  display: "flex", gap: 12, padding: "10px 18px", alignItems: "center",
                  borderBottom: i < report.comparison.length - 1
                    ? `1px solid ${T.border}` : "none",
                  background: i % 2 === 0 ? T.surface : T.surfaceAlt,
                }}>
                  <div style={{ fontFamily: "'Bebas Neue'", fontSize: 18,
                    color: vciCol(c.vci_score), flexShrink: 0, width: 40,
                    textAlign: "center" }}>{Math.round(c.vci_score)}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <a href={c.url} target="_blank" rel="noreferrer"
                      style={{ fontSize: 12, color: T.text, textDecoration: "none",
                        display: "block", marginBottom: 2,
                        overflow: "hidden", textOverflow: "ellipsis",
                        whiteSpace: "nowrap" }}>{c.title}</a>
                    <Mono style={{ fontSize: 9, color: T.blue }}>{c.outlet}</Mono>
                  </div>
                </div>
              ))}
            </Card>
          )}

        </div>
      )}
    </div>
  );
}

function SimulatorTab() {
  const [inputs, setInputs] = useState(
    { base: 80, verified: 3, refuted: 1, retracted: 0, stake: 10000 }
  );
  const score   = Math.max(0, Math.min(100,
    inputs.base - inputs.refuted * 15 + inputs.retracted * 2 + inputs.verified
  ));
  const slashed = inputs.refuted > 0 ? inputs.stake * 0.30 : 0;
  const status  = score >= 85 ? "Reliable" : score >= 60 ? "At Risk" : "Blacklisted";
  const c       = vciColor(score);

  const sliders = [
    { key: "base",      label: "Base VCI Score",           min: 0,    max: 100,   color: T.blue },
    { key: "verified",  label: "Verified Claims (+1 each)", min: 0,    max: 20,    color: T.green },
    { key: "refuted",   label: "Refuted Claims (−15 each)", min: 0,    max: 10,    color: T.red },
    { key: "retracted", label: "Retractions (+2 each)",    min: 0,    max: 10,    color: T.yellow },
    { key: "stake",     label: "Staked Amount ($)",        min: 1000, max: 100000, color: T.purple, step: 1000 },
  ];

  return (
    <div className="two-col-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
      <Card style={{ padding: 24 }}>
        <SectionLabel>VCI SIMULATOR — ADJUST PARAMETERS</SectionLabel>
        {sliders.map(s => (
          <div key={s.key} style={{ marginBottom: 22 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
              <Mono style={{ fontSize: 11, color: "#94a3b8" }}>{s.label}</Mono>
              <Mono style={{ fontSize: 13, fontWeight: 700, color: s.color }}>
                {s.key === "stake" ? `$${inputs[s.key].toLocaleString()}` : inputs[s.key]}
              </Mono>
            </div>
            <input type="range" min={s.min} max={s.max} step={s.step || 1}
              value={inputs[s.key]}
              onChange={e => setInputs(p => ({ ...p, [s.key]: +e.target.value }))}
              style={{ width: "100%", accentColor: s.color, cursor: "pointer" }} />
          </div>
        ))}
        <Card style={{ padding: 14, background: T.surfaceAlt, marginTop: 4 }}>
          <SectionLabel>FORMULA</SectionLabel>
          <Mono style={{ fontSize: 10, color: T.purple, lineHeight: 1.9, display: "block" }}>
            VCI = {inputs.base} − ({inputs.refuted} × 15) + ({inputs.retracted} × 2) + ({inputs.verified} × 1)<br/>
            VCI = {inputs.base} − {inputs.refuted*15} + {inputs.retracted*2} + {inputs.verified} = <span style={{ color: c, fontWeight: 700 }}>{score}</span><br/>
            Penalty = ${inputs.stake.toLocaleString()} × 0.30 = <span style={{ color: T.red }}>−${slashed.toLocaleString()}</span>
          </Mono>
        </Card>
      </Card>

      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <Card style={{ padding: 28, textAlign: "center" }}>
          <ScoreRing score={score} size={120} />
          <div style={{ marginTop: 16 }}><Badge status={status} /></div>
          <Mono style={{ fontSize: 28, color: c, display: "block", marginTop: 12,
            fontWeight: 800 }}>{score} / 100</Mono>
        </Card>
        <div className="two-col-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          {[
            { label: "SCORE CHANGE", value: `${score-inputs.base > 0 ? "+" : ""}${score-inputs.base}`,
              color: score >= inputs.base ? T.green : T.red },
            { label: "SIMULATED PENALTY", value: `-$${slashed.toLocaleString()}`,
              color: slashed > 0 ? T.red : T.green },
            { label: "VERIFIED BONUS", value: `+${inputs.verified} pts`, color: T.green },
            { label: "REFUTE COST",    value: `-${inputs.refuted*15} pts`, color: T.red },
          ].map(k => (
            <Card key={k.label} style={{ padding: 14 }}>
              <Mono style={{ fontSize: 8, color: T.muted, letterSpacing: "0.1em",
                display: "block", marginBottom: 5 }}>{k.label}</Mono>
              <div style={{ fontFamily: "'Bebas Neue', sans-serif",
                fontSize: 26, color: k.color }}>{k.value}</div>
            </Card>
          ))}
        </div>
        {slashed > 0 && (
          <Card style={{ padding: 14, background: `${T.red}08`,
            border: `1px solid ${T.red}30` }}>
            <Mono style={{ fontSize: 10, color: T.red, display: "block", marginBottom: 5 }}>
              PENALTY TRIGGERED
            </Mono>
            <p style={{ margin: 0, fontSize: 11, color: "#94a3b8", lineHeight: 1.6 }}>
              ${slashed.toLocaleString()} (30% of credibility stake) simulated penalty applied.
              A retraction recovers +2 VCI but does not restore capital.
            </p>
          </Card>
        )}
      </div>
    </div>
  );
}

// ============================================================
// ROOT APP
// ============================================================

// ============================================================
// INVESTOR DEMO TAB
// ============================================================


function HistoryTab({ onSelect }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState("");

  useEffect(() => {
    apiFetch("/scan/history")
      .then(d => { setHistory(d.history || []); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, []);

  if (loading) return <div style={{ padding: 40, color: T.muted, fontFamily: "monospace" }}>LOADING...</div>;
  if (error)   return <div style={{ padding: 40, color: T.red,  fontFamily: "monospace" }}>ERROR: {error}</div>;

  return (
    <div style={{ padding: "24px 32px", maxWidth: 860 }}>
      <SectionLabel>SCAN HISTORY</SectionLabel>
      {history.length === 0 ? (
        <div style={{ color: T.muted, fontFamily: "monospace", fontSize: 13, marginTop: 24 }}>
          NO SCANS YET — run your first scan to see history here.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 16 }}>
          {history.map(h => (
            <div key={h.id} onClick={() => h.result_json && onSelect && onSelect(h.result_json)}
              style={{
              background: T.surface, border: `1px solid ${T.border}`,
              borderRadius: 6, padding: "14px 18px",
              display: "flex", alignItems: "center", gap: 16,
              cursor: h.result_json ? "pointer" : "default",
              transition: "border-color 0.15s",
            }}
            onMouseEnter={e => { if (h.result_json) e.currentTarget.style.borderColor = T.blue; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; }}>
              <div style={{
                minWidth: 48, height: 48, borderRadius: 4,
                background: vciColor(h.vci_score),
                display: "flex", alignItems: "center", justifyContent: "center",
                fontFamily: "monospace", fontWeight: 700, fontSize: 15, color: "#000"
              }}>
                {h.vci_score != null ? Math.round(h.vci_score) : "—"}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontFamily: "monospace", fontSize: 13, fontWeight: 600,
                  color: T.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"
                }}>
                  {h.title || h.url || "Untitled"}
                </div>
                <div style={{ display: "flex", gap: 16, marginTop: 4 }}>
                  {h.outlet && (
                    <Mono style={{ fontSize: 10, color: T.blue }}>{h.outlet.toUpperCase()}</Mono>
                  )}
                  {h.verdict && (
                    <Mono style={{ fontSize: 10, color: T.muted }}>{h.verdict.toUpperCase()}</Mono>
                  )}
                  <Mono style={{ fontSize: 10, color: T.muted }}>
                    {new Date(h.scanned_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                  </Mono>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ScanTab({ externalResult, clearExternalResult }) {
  const [input, setInput]       = useState("");
  const [inputType, setInputType] = useState("text"); // "text" or "url"
  const [loading, setLoading]   = useState(false);
  const [result, setResult]     = useState(null);
  const [error, setError]       = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [shareUrl, setShareUrl]   = useState(null);
  const [sharing, setSharing]     = useState(false);

  // Load result from history if passed in
  useEffect(() => {
    if (externalResult) {
      setResult(externalResult);
      setInput("");
      if (clearExternalResult) clearExternalResult();
    }
  }, [externalResult]);

  const scan = async () => {
    if (!input.trim()) return;
    setLoading(true);
    setResult(null);
    setError(null);
    setFeedback(null);
    try {
      let body = {};
      if (inputType === "url") {
        // Send URL directly to backend — it handles fetching with ScraperAPI fallback
        body = { url: input.trim() };
      } else {
        body = { text: input.trim() };
      }
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 60000);
      try {
        const res = await fetch(`${API_BASE}/scan`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
          signal: controller.signal,
        });
        clearTimeout(timeout);
        if (!res.ok) {
          const errData = await res.json().catch(() => ({}));
          const detail = errData?.detail;
          if (typeof detail === "object" && detail?.suggestion) {
            throw new Error(detail.suggestion);
          } else if (typeof detail === "string") {
            throw new Error(detail);
          } else if (res.status === 429) {
            throw new Error("You have used your 5 free scans for today. Sign up for unlimited access.");
          } else {
            throw new Error(`Scan failed (${res.status}). Please try again.`);
          }
        }
        const data = await res.json();
        setResult(data);
        // Save to history
        try {
          await apiFetch("/scan/history", {
            method: "POST",
            body: JSON.stringify({
              url:         input.startsWith("http") ? input : null,
              title:       data.title     || null,
              outlet:      data.outlet    || null,
              vci_score:   data.vci_score || null,
              verdict:     data.verdict   || null,
              summary:     data.summary   || null,
              result_json: data,
            })
          });
        } catch (_) {}
      } catch(fetchErr) {
        clearTimeout(timeout);
        if (fetchErr.name === "AbortError") {
          setError("Scan timed out after 60 seconds. Please try again.");
        } else {
          setError(fetchErr.message || "Scan failed");
        }
      }
    } catch(e) {
      setError(e.message || "Scan failed");
    }
    setLoading(false);
  };

  const submitFeedback = async (accurate) => {
    setFeedback(accurate ? "positive" : "negative");
    try {
      await apiFetch("/scan/feedback", {
        method: "POST",
        body: JSON.stringify({
          accurate,
          vci_score: result?.vci_score,
          verdict:   result?.verdict,
          url:       result?.url,
          title:     result?.title,
          outlet:    result?.outlet,
          sources:   result?.sources,
          framing:   result?.framing,
          claims:    result?.claims,
        })
      });
    } catch(e) { console.error(e); }
  };

  const share = async () => {
    if (!result) return;
    setSharing(true);
    try {
      const res = await fetch(`${API_BASE}/share`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          vci_score:   Math.round(result.vci_score),
          verdict:     result.verdict,
          outlet:      result.outlet || null,
          title:       result.title  || null,
          summary:     result.summary || null,
          result_json: result,
        }),
      });
      const data = await res.json();
      setShareUrl(data.url);
      navigator.clipboard.writeText(data.url).catch(() => {});
    } catch(e) {
      console.error("Share failed", e);
    }
    setSharing(false);
  };

  const vciColor = (score) =>
    score >= 82 ? T.green : score >= 65 ? T.yellow : T.red;

  const credColor = (c) =>
    c === "HIGH" ? T.green : c === "MEDIUM" ? T.yellow : c === "LOW" ? T.red : T.muted;

  const typeColor = (t) => {
    if (t === "STATE_MEDIA" || t === "SOCIAL_MEDIA") return T.red;
    if (t === "ANONYMOUS") return T.yellow;
    if (t === "INTERNATIONAL_ORG" || t === "EXPERT") return T.green;
    return T.muted;
  };

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <SectionLabel>ARTICLE SCANNER</SectionLabel>
        <div style={{ fontSize: 13, color: T.muted, marginTop: 4 }}>
          Paste article text or a URL for instant credibility analysis
        </div>
      </div>

      {/* Input area */}
      <Card style={{ padding: 24, marginBottom: 20 }}>
        {/* Toggle */}
        <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
          {[["text", "PASTE TEXT"], ["url", "SCAN URL"]].map(([val, label]) => (
            <button key={val} onClick={() => { setInputType(val); setInput(""); }}
              style={{
                background: inputType === val ? T.blue : T.surfaceAlt,
                border: `1px solid ${inputType === val ? T.blue : T.border}`,
                color: inputType === val ? "#fff" : T.muted,
                borderRadius: 4, padding: "6px 16px", cursor: "pointer",
                fontFamily: "'DM Mono', monospace", fontSize: 10,
              }}>{label}</button>
          ))}
        </div>

        {inputType === "text" ? (
          <textarea
            value={input}
            onChange={e => setInput(e.target.value)}
            placeholder="Paste article text here..."
            style={{
              width: "100%", minHeight: 160, background: T.surfaceAlt,
              border: `1px solid ${T.border}`, borderRadius: 6,
              color: T.text, fontFamily: "'DM Sans', sans-serif",
              fontSize: 13, padding: "12px 14px", marginBottom: 14,
              resize: "vertical", boxSizing: "border-box", outline: "none",
            }}
          />
        ) : (<>
          <input
            value={input}
            onChange={e => setInput(e.target.value)}
            placeholder="https://..."
            style={{
              width: "100%", background: T.surfaceAlt,
              border: `1px solid ${T.border}`, borderRadius: 6,
              color: T.text, fontFamily: "'DM Mono', monospace",
              fontSize: 12, padding: "10px 14px", marginBottom: 8,
              boxSizing: "border-box", outline: "none",
            }}
          />
          <Mono style={{ fontSize: 9, color: T.muted, display: "block", marginBottom: 14 }}>
            ⚠ Some sites block URL scanning. If it fails, use PASTE TEXT instead.
          </Mono>
        </>)}

        <button onClick={scan} disabled={loading || !input.trim()} style={{
          background: loading || !input.trim() ? T.surfaceAlt : T.blue,
          border: "none", color: loading || !input.trim() ? T.muted : "#fff",
          borderRadius: 6, padding: "10px 28px", cursor: loading || !input.trim() ? "not-allowed" : "pointer",
          fontFamily: "'DM Mono', monospace", fontSize: 11, letterSpacing: "0.08em",
          transition: "all 0.15s",
        }}>
          {loading ? "SCANNING..." : "SCAN ARTICLE →"}
        </button>
      </Card>

      {/* Loading */}
      {loading && (
        <Card style={{ padding: 40, textAlign: "center" }}>
          <Spinner />
          <Mono style={{ fontSize: 11, color: T.muted, display: "block", marginTop: 16 }}>
            ANALYSING SOURCES · DETECTING FRAMING · CHECKING FACTS...
          </Mono>
        </Card>
      )}

      {/* Error */}
      {error && (
        <Card style={{ padding: 20, borderColor: T.red, background: `${T.red}08` }}>
          <Mono style={{ fontSize: 11, color: T.red, display: "block", marginBottom: 8 }}>⚠ {error}</Mono>
          {(error.toLowerCase().includes("paste") || error.toLowerCase().includes("text") || error.toLowerCase().includes("copy")) && (
            <Mono style={{ fontSize: 10, color: T.muted }}>
              Tip: Copy the article text from the site and use the PASTE TEXT tab above for a full analysis.
            </Mono>
          )}
        </Card>
      )}

      {/* Results */}
      {result && !loading && (
        <div style={{ animation: "fadeIn 0.3s ease" }}>

          {/* Score header */}
          <Card style={{ padding: 24, marginBottom: 16,
            borderColor: vciColor(result.vci_score),
            background: `linear-gradient(135deg, ${T.surface}, ${vciColor(result.vci_score)}08)` }}>
            <div style={{ display: "flex", gap: 24, alignItems: "center", flexWrap: "wrap" }}>
              <div style={{ textAlign: "center", flexShrink: 0 }}>
                <ScoreRing score={result.vci_score} size={90} />
                <div style={{ display: "flex", gap: 4, marginTop: 6, justifyContent: "center" }}>
                  <Mono style={{ fontSize: 7, color: T.green }}>82+ RELIABLE</Mono>
                  <Mono style={{ fontSize: 7, color: T.muted }}>·</Mono>
                  <Mono style={{ fontSize: 7, color: T.yellow }}>65 AT RISK</Mono>
                  <Mono style={{ fontSize: 7, color: T.muted }}>·</Mono>
                  <Mono style={{ fontSize: 7, color: T.red }}>&lt;65 BLACKLISTED</Mono>
                </div>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 6 }}>
                  {result.title || "Article Analysis"}
                </div>
                <div style={{ display: "flex", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
                  <Badge status={result.verdict} />
                  {result.content_type && result.content_type !== "NEWS" && (
                    <Mono style={{ fontSize: 9, padding: "3px 8px", borderRadius: 3,
                      background: {
                        SOCIAL:        "#7C3AED20",
                        NEWSLETTER:    "#0EA5E920",
                        PRESS_RELEASE: "#F59E0B20",
                        OPINION:       "#EC489920",
                        REVIEW:        "#10B98120",
                        ACADEMIC:      "#6366F120",
                      }[result.content_type] || T.surfaceAlt,
                      color: {
                        SOCIAL:        "#7C3AED",
                        NEWSLETTER:    "#0EA5E9",
                        PRESS_RELEASE: "#F59E0B",
                        OPINION:       "#EC4899",
                        REVIEW:        "#10B981",
                        ACADEMIC:      "#6366F1",
                      }[result.content_type] || T.muted,
                      border: `1px solid currentColor`,
                    }}>{result.content_type}</Mono>
                  )}
                  {result.outlet && result.outlet !== "Unknown" && (
                    <Mono style={{ fontSize: 9, background: T.surfaceAlt,
                      border: `1px solid ${T.border}`, padding: "3px 8px",
                      borderRadius: 3, color: T.muted }}>{result.outlet}</Mono>
                  )}
                  {result.author && (
                    <Mono style={{ fontSize: 9, background: T.surfaceAlt,
                      border: `1px solid ${T.border}`, padding: "3px 8px",
                      borderRadius: 3, color: T.muted }}>✍ {result.author}</Mono>
                  )}
                </div>
                <div style={{ fontSize: 13, color: T.muted, lineHeight: 1.6 }}>
                  {result.summary}
                </div>
              </div>
            </div>
            {/* Share button */}
            <div style={{ marginTop: 16, paddingTop: 16,
              borderTop: `1px solid ${T.border}`,
              display: "flex", alignItems: "center", gap: 10 }}>
              <button onClick={share} disabled={sharing} style={{
                background: sharing ? T.surfaceAlt : `${T.blue}20`,
                border: `1px solid ${sharing ? T.border : T.blue}`,
                color: sharing ? T.muted : T.blue,
                borderRadius: 4, padding: "8px 18px", cursor: sharing ? "default" : "pointer",
                fontFamily: "'DM Mono', monospace", fontSize: 10,
                letterSpacing: "0.08em", transition: "all 0.15s",
              }}>
                {sharing ? "GENERATING..." : "↗ SHARE RESULT"}
              </button>
              {shareUrl && (
                <div style={{
                  position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  zIndex: 1000,
                }} onClick={() => setShareUrl(null)}>
                  <div style={{
                    background: T.card, border: `1px solid ${T.border}`,
                    borderRadius: 10, padding: 24, width: 340, maxWidth: "90vw",
                  }} onClick={e => e.stopPropagation()}>

                    {/* Header */}
                    <div style={{ display: "flex", justifyContent: "space-between",
                      alignItems: "center", marginBottom: 20 }}>
                      <Mono style={{ fontSize: 11, color: T.text, letterSpacing: "0.1em" }}>
                        SHARE RESULT
                      </Mono>
                      <button onClick={() => setShareUrl(null)} style={{
                        background: "transparent", border: "none", color: T.muted,
                        cursor: "pointer", fontSize: 18, lineHeight: 1,
                      }}>×</button>
                    </div>

                    {/* Platform buttons */}
                    <div style={{ display: "flex", gap: 12, justifyContent: "center",
                      marginBottom: 20, flexWrap: "wrap" }}>
                      {[
                        { label: "WhatsApp",  color: "#25D366", icon: "💬",
                          url: `https://wa.me/?text=${encodeURIComponent("Check this credibility report: " + shareUrl)}` },
                        { label: "X",         color: "#000000", icon: "𝕏",
                          url: `https://x.com/intent/tweet?text=${encodeURIComponent("Credibility report via @VerdadScore")}&url=${encodeURIComponent(shareUrl)}` },
                        { label: "Facebook",  color: "#1877F2", icon: "f",
                          url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}` },
                        { label: "LinkedIn",  color: "#0A66C2", icon: "in",
                          url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}` },
                        { label: "Email",     color: "#64748B", icon: "✉",
                          url: `mailto:?subject=${encodeURIComponent("Credibility Report — Verdad Score")}&body=${encodeURIComponent("Check this credibility report: " + shareUrl)}` },
                        { label: "Reddit",    color: "#FF4500", icon: "👽",
                          url: `https://www.reddit.com/submit?url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent("Credibility Report — Verdad Score")}` },
                      ].map(({ label, color, icon, url }) => (
                        <a key={label} href={url} target="_blank" rel="noopener noreferrer"
                          style={{ display: "flex", flexDirection: "column",
                            alignItems: "center", gap: 6, textDecoration: "none" }}>
                          <div style={{
                            width: 48, height: 48, borderRadius: 24,
                            background: color, display: "flex",
                            alignItems: "center", justifyContent: "center",
                            fontSize: 18, color: "#fff", fontWeight: 700,
                            fontFamily: "serif",
                          }}>{icon}</div>
                          <Mono style={{ fontSize: 8, color: T.muted }}>{label.toUpperCase()}</Mono>
                        </a>
                      ))}
                    </div>

                    {/* Copy URL bar */}
                    <div style={{ display: "flex", gap: 8, alignItems: "center",
                      background: T.surface, border: `1px solid ${T.border}`,
                      borderRadius: 6, padding: "8px 12px" }}>
                      <Mono style={{ fontSize: 10, color: T.muted, flex: 1,
                        overflow: "hidden", textOverflow: "ellipsis",
                        whiteSpace: "nowrap" }}>{shareUrl}</Mono>
                      <button onClick={() => {
                        try {
                          if (navigator.clipboard && window.isSecureContext) {
                            navigator.clipboard.writeText(shareUrl).catch(() => {
                              const el = document.createElement("textarea");
                              el.value = shareUrl; document.body.appendChild(el);
                              el.select(); document.execCommand("copy");
                              document.body.removeChild(el);
                            });
                          } else {
                            const el = document.createElement("textarea");
                            el.value = shareUrl; document.body.appendChild(el);
                            el.select(); document.execCommand("copy");
                            document.body.removeChild(el);
                          }
                        } catch(e) {}
                      }} style={{
                        background: T.blue, border: "none", color: "#fff",
                        borderRadius: 4, padding: "4px 12px", cursor: "pointer",
                        fontFamily: "'DM Mono', monospace", fontSize: 9,
                        letterSpacing: "0.08em", whiteSpace: "nowrap",
                      }}>COPY</button>
                    </div>

                  </div>
                </div>
              )}
            </div>

            {result.reasoning && (
              <div style={{ marginTop: 16, paddingTop: 16,
                borderTop: `1px solid ${T.border}`,
                fontSize: 12, color: T.muted, lineHeight: 1.6,
                fontStyle: "italic" }}>
                {result.reasoning}
              </div>
            )}
          </Card>

          {/* Partial content warning */}
          {result.reasoning && result.reasoning.toLowerCase().includes("full article text is not provided") && (
            <div style={{
              background: "#1a1a00", border: "1px solid #F59E0B",
              borderRadius: 6, padding: "10px 14px", marginBottom: 16,
              display: "flex", gap: 10, alignItems: "flex-start"
            }}>
              <span style={{ fontSize: 16 }}>⚠️</span>
              <div>
                <Mono style={{ fontSize: 10, color: "#F59E0B", display: "block", marginBottom: 4 }}>
                  PARTIAL ARTICLE — LIMITED ANALYSIS
                </Mono>
                <div style={{ fontSize: 12, color: T.muted, lineHeight: 1.5 }}>
                  This site restricts automated access. Only a partial excerpt was retrieved,
                  which may affect the accuracy of this score. For a complete analysis,
                  copy the full article text and use PASTE TEXT instead.
                </div>
              </div>
            </div>
          )}

          {/* Penalties */}
          {(result.penalties?.framing > 0 || result.penalties?.sources > 0) && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)",
              gap: 10, marginBottom: 16 }}>
              {[
                { label: "VCI SCORE",       value: result.vci_score,           color: vciColor(result.vci_score) },
                { label: "FRAMING PENALTY", value: `-${result.penalties?.framing || 0}`, color: T.red },
                { label: "SOURCE PENALTY",  value: `-${result.penalties?.sources || 0}`, color: T.red },
              ].map(({ label, value, color }) => (
                <Card key={label} style={{ padding: "14px 18px", textAlign: "center" }}>
                  <div style={{ fontFamily: "'Bebas Neue', sans-serif",
                    fontSize: 28, color }}>{value}</div>
                  <Mono style={{ fontSize: 8, color: T.muted,
                    letterSpacing: "0.1em" }}>{label}</Mono>
                </Card>
              ))}
            </div>
          )}

          {/* Framing analysis */}
          {result.framing && Object.keys(result.framing).length > 0 && (
            <Card style={{ padding: 20, marginBottom: 16 }}>
              <Mono style={{ fontSize: 9, color: T.muted, letterSpacing: "0.1em",
                display: "block", marginBottom: 14 }}>FRAMING ANALYSIS</Mono>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)",
                gap: 10, marginBottom: 14 }}>
                {[
                  { label: "SENSATIONAL",       val: result.framing.is_sensational },
                  { label: "ONE-SIDED",          val: result.framing.is_one_sided },
                  { label: "EMOTIONAL LANGUAGE", val: result.framing.has_emotional_language },
                ].map(({ label, val }) => (
                  <div key={label} style={{
                    background: val ? `${T.red}15` : `${T.green}15`,
                    border: `1px solid ${val ? T.red : T.green}40`,
                    borderRadius: 6, padding: "10px 14px", textAlign: "center" }}>
                    <div style={{ fontSize: 11, fontWeight: 700, marginBottom: 4,
                      color: val ? T.red : T.green }}>
                      {val ? "⚠ FLAGGED" : "✓ PASS"}
                    </div>
                    <Mono style={{ fontSize: 8, color: T.muted,
                      letterSpacing: "0.08em" }}>{label}</Mono>
                  </div>
                ))}
              </div>
              {result.framing.narrative_push && (
                <div style={{ marginBottom: 10 }}>
                  <Mono style={{ fontSize: 8, color: T.yellow,
                    letterSpacing: "0.08em", display: "block", marginBottom: 4 }}>
                    NARRATIVE PUSH
                  </Mono>
                  <div style={{ fontSize: 12, color: T.muted, lineHeight: 1.6 }}>
                    {result.framing.narrative_push}
                  </div>
                </div>
              )}
              {result.framing.missing_context && (
                <div>
                  <Mono style={{ fontSize: 8, color: T.muted,
                    letterSpacing: "0.08em", display: "block", marginBottom: 4 }}>
                    MISSING CONTEXT
                  </Mono>
                  <div style={{ fontSize: 12, color: T.muted, lineHeight: 1.6 }}>
                    {result.framing.missing_context}
                  </div>
                </div>
              )}
            </Card>
          )}

          {/* Sources */}
          {result.sources?.length > 0 && (
            <Card style={{ marginBottom: 16 }}>
              <div style={{ padding: "12px 18px", borderBottom: `1px solid ${T.border}`,
                background: T.surfaceAlt }}>
                <Mono style={{ fontSize: 9, color: T.muted, letterSpacing: "0.1em" }}>
                  SOURCES CITED ({result.sources.length})
                </Mono>
              </div>
              {result.sources.map((s, i) => (
                <div key={i} style={{
                  display: "flex", gap: 12, padding: "10px 18px", alignItems: "center",
                  borderBottom: i < result.sources.length - 1
                    ? `1px solid ${T.border}` : "none",
                  background: i % 2 === 0 ? T.surface : T.surfaceAlt,
                }}>
                  <div style={{ fontSize: 13, fontWeight: 500, flex: 1 }}>{s.entity}</div>
                  <Mono style={{ fontSize: 9, color: typeColor(s.type),
                    background: `${typeColor(s.type)}15`,
                    border: `1px solid ${typeColor(s.type)}30`,
                    padding: "2px 8px", borderRadius: 3,
                    whiteSpace: "nowrap" }}>{SOURCE_TYPE_LABELS[s.type] || s.type}</Mono>
                  <Mono style={{ fontSize: 9, color: credColor(s.credibility),
                    minWidth: 60, textAlign: "right" }}>{CRED_LABELS[s.credibility] || s.credibility}</Mono>
                </div>
              ))}
            </Card>
          )}

          {/* Claims */}
          {result.claims?.length > 0 && (
            <Card style={{ padding: 20, marginBottom: 16 }}>
              <Mono style={{ fontSize: 9, color: T.muted, letterSpacing: "0.1em",
                display: "block", marginBottom: 12 }}>KEY CLAIMS</Mono>
              {result.claims.map((c, i) => (
                <div key={i} style={{ display: "flex", gap: 10, marginBottom: 8,
                  alignItems: "flex-start" }}>
                  <Mono style={{ fontSize: 9, color: T.blue, minWidth: 20 }}>
                    {i + 1}.
                  </Mono>
                  <div style={{ fontSize: 12, color: T.muted, lineHeight: 1.6 }}>{c}</div>
                </div>
              ))}
            </Card>
          )}

          {/* Fact checks */}
          {result.fact_checks?.length > 0 && (
            <Card style={{ padding: 20, marginBottom: 16 }}>
              <Mono style={{ fontSize: 9, color: T.muted, letterSpacing: "0.1em",
                display: "block", marginBottom: 12 }}>RELATED FACT CHECKS</Mono>
              {result.fact_checks.map((fc, i) => {
                const vc = fc.verdict_norm === "FALSE" ? T.red :
                           fc.verdict_norm === "MISLEADING" ? T.yellow :
                           fc.verdict_norm === "TRUE" ? T.green : T.muted;
                return (
                  <div key={i} style={{ borderLeft: `3px solid ${vc}`,
                    paddingLeft: 14, marginBottom: 12 }}>
                    <div style={{ display: "flex", gap: 8, alignItems: "center",
                      marginBottom: 4 }}>
                      <Mono style={{ fontSize: 9, color: vc,
                        background: `${vc}20`, border: `1px solid ${vc}`,
                        padding: "2px 6px", borderRadius: 3 }}>
                        {fc.verdict_norm}
                      </Mono>
                      <Mono style={{ fontSize: 9, color: T.muted }}>
                        {fc.publisher}
                      </Mono>
                    </div>
                    <div style={{ fontSize: 12, color: T.muted }}>{fc.claim}</div>
                  </div>
                );
              })}
            </Card>
          )}

          {/* Feedback */}
          <Card style={{ padding: 20 }}>
            <Mono style={{ fontSize: 9, color: T.muted, letterSpacing: "0.1em",
              display: "block", marginBottom: 12 }}>WAS THIS ASSESSMENT ACCURATE?</Mono>
            {feedback ? (
              <Mono style={{ fontSize: 11, color: feedback === "positive" ? T.green : T.yellow }}>
                {feedback === "positive"
                  ? "✓ Thanks — positive feedback recorded"
                  : "✗ Thanks — we'll use this to improve"}
              </Mono>
            ) : (
              <div style={{ display: "flex", gap: 10 }}>
                <button onClick={() => submitFeedback(true)} style={{
                  background: `${T.green}15`, border: `1px solid ${T.green}40`,
                  color: T.green, borderRadius: 4, padding: "8px 20px",
                  cursor: "pointer", fontFamily: "'DM Mono', monospace", fontSize: 11,
                }}>✓ ACCURATE</button>
                <button onClick={() => submitFeedback(false)} style={{
                  background: `${T.red}15`, border: `1px solid ${T.red}40`,
                  color: T.red, borderRadius: 4, padding: "8px 20px",
                  cursor: "pointer", fontFamily: "'DM Mono', monospace", fontSize: 11,
                }}>✗ NEEDS IMPROVEMENT</button>
              </div>
            )}
          </Card>

        </div>
      )}
    </div>
  );
}


function SourceProfileDrawer({ entity, onClose }) {
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab]       = useState("overview");

  useEffect(() => {
    setLoading(true);
    apiFetch(`/sources/${encodeURIComponent(entity)}`)
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [entity]);

  const sciColor  = s => s >= 70 ? T.green : s >= 50 ? T.yellow : T.red;
  const vciColor  = s => s >= 82 ? T.green : s >= 60 ? T.yellow : T.red;
  const riskColor = r => r === "CRITICAL" ? T.red : r === "HIGH" ? T.red : r === "MEDIUM" ? T.yellow : T.green;

  const typeColor = (t) => {
    if (t === "STATE_MEDIA")       return T.red;
    if (t === "ANONYMOUS")         return T.yellow;
    if (t === "SOCIAL_MEDIA")      return T.red;
    if (t === "INTERNATIONAL_ORG") return T.green;
    if (t === "EXPERT")            return T.blue;
    if (t === "MILITARY")          return "#8b5cf6";
    if (t === "GOVERNMENT")        return T.blue;
    return T.muted;
  };

  const profile  = data?.profile || {};
  const vciDist  = data?.vci_distribution || {};
  const fc       = data?.fact_check_summary || {};
  const narrative = data?.narrative || {};

  return (
    <div style={{
      position: "fixed", top: 0, right: 0, bottom: 0, width: "min(680px, 100vw)",
      background: T.surface, borderLeft: `1px solid ${T.border}`,
      zIndex: 1000, display: "flex", flexDirection: "column",
      boxShadow: "-8px 0 40px rgba(0,0,0,0.4)",
    }}>
      {/* Header */}
      <div style={{
        padding: "20px 24px", borderBottom: `1px solid ${T.border}`,
        display: "flex", alignItems: "flex-start", justifyContent: "space-between",
        background: T.surfaceAlt, flexShrink: 0,
      }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
            <span style={{
              fontFamily: "'Bebas Neue', sans-serif", fontSize: 24, color: T.text,
            }}>{entity}</span>
            {profile.entity_type && (
              <Mono style={{
                fontSize: 9, color: typeColor(profile.entity_type),
                background: `${typeColor(profile.entity_type)}15`,
                border: `1px solid ${typeColor(profile.entity_type)}30`,
                padding: "2px 8px", borderRadius: 3,
              }}>{profile.entity_type}</Mono>
            )}
            {narrative.risk_level && narrative.risk_level !== "UNKNOWN" && (
              <Mono style={{
                fontSize: 9, color: riskColor(narrative.risk_level),
                background: `${riskColor(narrative.risk_level)}15`,
                border: `1px solid ${riskColor(narrative.risk_level)}30`,
                padding: "2px 8px", borderRadius: 3,
              }}>{narrative.risk_level} RISK</Mono>
            )}
          </div>
          <div style={{ display: "flex", gap: 20 }}>
            {[
              { label: "SCI", value: Math.round(profile.sci_score || 0), color: sciColor(profile.sci_score || 0) },
              { label: "CITATIONS", value: profile.citation_count || 0, color: T.blue },
              { label: "OUTLETS",   value: profile.outlet_count || 0,   color: T.muted },
              { label: "AVG VCI",   value: vciDist.avg_vci || "â€”",      color: vciColor(vciDist.avg_vci || 0) },
            ].map(({ label, value, color }) => (
              <div key={label} style={{ textAlign: "center" }}>
                <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 22, color }}>{value}</div>
                <Mono style={{ fontSize: 8, color: T.muted, letterSpacing: "0.1em" }}>{label}</Mono>
              </div>
            ))}
          </div>
        </div>
        <button onClick={onClose} style={{
          background: "transparent", border: `1px solid ${T.border}`,
          color: T.muted, borderRadius: 4, padding: "6px 12px",
          cursor: "pointer", fontFamily: "'DM Mono', monospace", fontSize: 10,
        }}>CLOSE</button>
      </div>

      {/* Sub-tabs */}
      <div style={{
        display: "flex", gap: 0, borderBottom: `1px solid ${T.border}`,
        background: T.surfaceAlt, flexShrink: 0, overflowX: "auto",
      }}>
        {["overview","outlets","articles","co-cited"].map(t => (
          <button key={t} onClick={() => setTab(t)} style={{
            background: "transparent",
            borderBottom: tab === t ? `2px solid ${T.blue}` : "2px solid transparent",
            border: "none", borderBottom: tab === t ? `2px solid ${T.blue}` : "2px solid transparent",
            color: tab === t ? T.text : T.muted,
            padding: "10px 18px", cursor: "pointer",
            fontFamily: "'DM Mono', monospace", fontSize: 9,
            letterSpacing: "0.08em", whiteSpace: "nowrap",
          }}>{t.toUpperCase()}</button>
        ))}
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflowY: "auto", padding: 24 }}>
        {loading ? <Spinner /> : !data ? (
          <Mono style={{ color: T.muted, fontSize: 11 }}>Failed to load profile.</Mono>
        ) : (
          <>
            {/* OVERVIEW TAB */}
            {tab === "overview" && (
              <div>
                {/* Narrative */}
                {narrative.overview && (
                  <div style={{ marginBottom: 20 }}>
                    <Mono style={{ fontSize: 9, color: T.blue, letterSpacing: "0.1em",
                      display: "block", marginBottom: 10 }}>INTELLIGENCE OVERVIEW</Mono>
                    <p style={{ fontSize: 13, color: T.text, lineHeight: 1.7,
                      margin: "0 0 12px 0" }}>{narrative.overview}</p>
                    {narrative.pattern_analysis && !narrative.overview.includes(narrative.pattern_analysis.slice(0,30)) && (
                      <p style={{ fontSize: 13, color: T.text, lineHeight: 1.7,
                        margin: "0 0 12px 0" }}>{narrative.pattern_analysis}</p>
                    )}
                    {narrative.risk_assessment && !narrative.overview.includes(narrative.risk_assessment.slice(0,30)) && (
                      <div style={{
                        background: `${riskColor(narrative.risk_level)}10`,
                        border: `1px solid ${riskColor(narrative.risk_level)}30`,
                        borderRadius: 6, padding: "12px 16px", marginTop: 8,
                      }}>
                        <Mono style={{ fontSize: 9, color: riskColor(narrative.risk_level),
                          letterSpacing: "0.1em", display: "block", marginBottom: 6 }}>
                          RISK ASSESSMENT
                        </Mono>
                        <p style={{ fontSize: 12, color: T.text, lineHeight: 1.6, margin: 0 }}>
                          {narrative.risk_assessment}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* VCI Distribution */}
                <div style={{ marginBottom: 20 }}>
                  <Mono style={{ fontSize: 9, color: T.muted, letterSpacing: "0.1em",
                    display: "block", marginBottom: 10 }}>CITING ARTICLE CREDIBILITY</Mono>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
                    {[
                      { label: "RELIABLE",    value: vciDist.reliable    || 0, color: T.green },
                      { label: "AT RISK",     value: vciDist.at_risk     || 0, color: T.yellow },
                      { label: "BLACKLISTED", value: vciDist.blacklisted || 0, color: T.red },
                    ].map(({ label, value, color }) => (
                      <div key={label} style={{
                        background: `${color}10`, border: `1px solid ${color}25`,
                        borderRadius: 6, padding: "14px 16px", textAlign: "center",
                      }}>
                        <div style={{ fontFamily: "'Bebas Neue', sans-serif",
                          fontSize: 28, color }}>{value}</div>
                        <Mono style={{ fontSize: 8, color: T.muted,
                          letterSpacing: "0.1em" }}>{label}</Mono>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Fact check summary */}
                <div style={{ marginBottom: 20 }}>
                  <Mono style={{ fontSize: 9, color: T.muted, letterSpacing: "0.1em",
                    display: "block", marginBottom: 10 }}>FACT-CHECK RECORD</Mono>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 8 }}>
                    {[
                      { label: "L4 TRIGGERED", value: fc.layer4_triggered || 0, color: T.yellow },
                      { label: "REFUTED",       value: fc.articles_refuted || 0, color: T.red },
                      { label: "VERIFIED",      value: fc.articles_verified || 0, color: T.green },
                      { label: "NEEDS REVIEW",  value: fc.needs_review      || 0, color: T.muted },
                    ].map(({ label, value, color }) => (
                      <Card key={label} style={{ padding: "12px 14px", textAlign: "center" }}>
                        <div style={{ fontFamily: "'Bebas Neue', sans-serif",
                          fontSize: 24, color }}>{value}</div>
                        <Mono style={{ fontSize: 8, color: T.muted,
                          letterSpacing: "0.1em" }}>{label}</Mono>
                      </Card>
                    ))}
                  </div>
                </div>

                {/* Citation timeline */}
                {data.citation_timeline?.length > 0 && (
                  <div>
                    <Mono style={{ fontSize: 9, color: T.muted, letterSpacing: "0.1em",
                      display: "block", marginBottom: 10 }}>CITATION TIMELINE</Mono>
                    <div style={{ display: "flex", gap: 4, alignItems: "flex-end", height: 60 }}>
                      {(() => {
                        const max = Math.max(...data.citation_timeline.map(t => t.citations));
                        return data.citation_timeline.map((t, i) => (
                          <div key={i} title={`Week of ${t.week?.slice(0,10)}: ${t.citations} citations, avg VCI ${t.avg_vci}`}
                            style={{
                              flex: 1, background: T.blue,
                              height: `${Math.max(4, (t.citations / max) * 56)}px`,
                              borderRadius: "2px 2px 0 0", opacity: 0.8,
                              cursor: "default",
                            }} />
                        ));
                      })()}
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}>
                      <Mono style={{ fontSize: 8, color: T.muted }}>
                        {data.citation_timeline[0]?.week?.slice(0,10)}
                      </Mono>
                      <Mono style={{ fontSize: 8, color: T.muted }}>
                        {data.citation_timeline[data.citation_timeline.length-1]?.week?.slice(0,10)}
                      </Mono>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* OUTLETS TAB */}
            {tab === "outlets" && (
              <div>
                <Mono style={{ fontSize: 9, color: T.muted, letterSpacing: "0.1em",
                  display: "block", marginBottom: 14 }}>
                  OUTLETS CITING THIS SOURCE ({data.outlet_breakdown?.length || 0})
                </Mono>
                {data.outlet_breakdown?.map((o, i) => (
                  <div key={i} style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    padding: "10px 14px", marginBottom: 6,
                    background: T.surfaceAlt, borderRadius: 6,
                    border: `1px solid ${T.border}`,
                  }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 2 }}>{o.outlet}</div>
                      <Mono style={{ fontSize: 9, color: T.muted }}>
                        {o.citation_count} citation{o.citation_count !== 1 ? "s" : ""}
                      </Mono>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <Mono style={{ fontSize: 11, color: vciColor(o.avg_article_vci),
                        fontWeight: 600 }}>{o.avg_article_vci}</Mono>
                      <Mono style={{ fontSize: 8, color: T.muted, display: "block" }}>AVG VCI</Mono>
                    </div>
                  </div>
                ))}

                {/* Category breakdown */}
                {data.category_breakdown?.length > 0 && (
                  <div style={{ marginTop: 24 }}>
                    <Mono style={{ fontSize: 9, color: T.muted, letterSpacing: "0.1em",
                      display: "block", marginBottom: 14 }}>TOPIC BREAKDOWN</Mono>
                    {data.category_breakdown.map((c, i) => {
                      const max = data.category_breakdown[0]?.count || 1;
                      return (
                        <div key={i} style={{ marginBottom: 8 }}>
                          <div style={{ display: "flex", justifyContent: "space-between",
                            marginBottom: 3 }}>
                            <Mono style={{ fontSize: 10, color: T.text }}>{c.category}</Mono>
                            <Mono style={{ fontSize: 10, color: T.muted }}>{c.count}</Mono>
                          </div>
                          <div style={{ height: 4, background: T.border, borderRadius: 2 }}>
                            <div style={{
                              height: "100%", borderRadius: 2, background: T.blue,
                              width: `${(c.count / max) * 100}%`,
                            }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* ARTICLES TAB */}
            {tab === "articles" && (
              <div>
                <Mono style={{ fontSize: 9, color: T.muted, letterSpacing: "0.1em",
                  display: "block", marginBottom: 14 }}>
                  ARTICLES CITING THIS SOURCE ({data.cited_articles?.length || 0})
                </Mono>
                {data.cited_articles?.map((a, i) => (
                  <div key={i} style={{
                    padding: "12px 14px", marginBottom: 8,
                    background: T.surfaceAlt, borderRadius: 6,
                    border: `1px solid ${T.border}`,
                  }}>
                    <div style={{ display: "flex", justifyContent: "space-between",
                      alignItems: "flex-start", gap: 12 }}>
                      <div style={{ flex: 1 }}>
                        <a href={a.url} target="_blank" rel="noreferrer" style={{
                          color: T.text, textDecoration: "none", fontSize: 13,
                          fontWeight: 500, lineHeight: 1.4, display: "block",
                          marginBottom: 4,
                        }}>{a.title}</a>
                        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                          <Mono style={{ fontSize: 9, color: T.muted }}>{a.outlet}</Mono>
                          <Mono style={{ fontSize: 9, color: T.muted }}>Â·</Mono>
                          <Mono style={{ fontSize: 9, color: T.muted }}>
                            {a.scraped_at?.slice(0,10)}
                          </Mono>
                          {a.layer4_triggered && (
                            <Mono style={{ fontSize: 8, color: T.yellow,
                              background: `${T.yellow}15`, padding: "1px 5px",
                              borderRadius: 2 }}>L4</Mono>
                          )}
                        </div>
                      </div>
                      <div style={{ textAlign: "right", flexShrink: 0 }}>
                        <Mono style={{ fontSize: 14, fontWeight: 700,
                          color: vciColor(a.vci_score) }}>{Math.round(a.vci_score)}</Mono>
                        <Mono style={{ fontSize: 8, color: T.muted,
                          display: "block" }}>VCI</Mono>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* CO-CITED TAB */}
            {tab === "co-cited" && (
              <div>
                <Mono style={{ fontSize: 9, color: T.muted, letterSpacing: "0.1em",
                  display: "block", marginBottom: 14 }}>
                  SOURCES APPEARING ALONGSIDE {entity.toUpperCase()}
                </Mono>
                {data.co_cited_sources?.map((s, i) => (
                  <div key={i} style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    padding: "10px 14px", marginBottom: 6,
                    background: T.surfaceAlt, borderRadius: 6,
                    border: `1px solid ${T.border}`,
                  }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 500,
                        marginBottom: 2 }}>{s.entity}</div>
                      <Mono style={{
                        fontSize: 9, color: typeColor(s.entity_type),
                        background: `${typeColor(s.entity_type)}15`,
                        border: `1px solid ${typeColor(s.entity_type)}30`,
                        padding: "1px 6px", borderRadius: 2,
                      }}>{s.entity_type}</Mono>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <Mono style={{ fontSize: 13, fontWeight: 600,
                        color: T.blue }}>{s.co_citations}</Mono>
                      <Mono style={{ fontSize: 8, color: T.muted,
                        display: "block" }}>CO-CITATIONS</Mono>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function SourcesTab() {
  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(true);
  const [typeFilter, setTypeFilter] = useState("");
  const [search, setSearch]       = useState("");
  const [selected, setSelected]   = useState(null);

  useEffect(() => {
    apiFetch("/sources").then(d => {
      setData(d);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const typeColor = (t) => {
    if (t === "STATE_MEDIA")       return T.red;
    if (t === "ANONYMOUS")         return T.yellow;
    if (t === "SOCIAL_MEDIA")      return T.red;
    if (t === "INTERNATIONAL_ORG") return T.green;
    if (t === "EXPERT")            return T.blue;
    if (t === "MILITARY")          return "#8b5cf6";
    if (t === "GOVERNMENT")        return T.blue;
    return T.muted;
  };

  const sciColor = (score) =>
    score >= 70 ? T.green : score >= 50 ? T.yellow : T.red;

  const profiles = (data?.profiles || [])
    .filter(p => !typeFilter || p.entity_type === typeFilter)
    .filter(p => !search || p.entity.toLowerCase().includes(search.toLowerCase()));

  const byType = data?.by_type || {};
  const totalSources = Object.values(byType).reduce((a,b) => a+b, 0);

  return (
    <div style={{ position: "relative" }}>
      <div style={{ marginBottom: 20 }}>
        <SectionLabel>SOURCE CREDIBILITY INDEX</SectionLabel>
        <div style={{ fontSize: 13, color: T.muted, marginTop: 4 }}>
          Entities cited across {data?.total || 0} tracked sources Â· {Object.keys(byType).length} types
        </div>
      </div>

      {/* Stats bar */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))",
        gap: 10, marginBottom: 20 }}>
        {[
          { label: "TOTAL CITATIONS",  value: totalSources,                          color: T.blue },
          { label: "UNIQUE ENTITIES",  value: data?.total || 0,                      color: T.blue },
          { label: "STATE MEDIA",      value: byType["STATE_MEDIA"] || 0,            color: T.red },
          { label: "ANONYMOUS",        value: byType["ANONYMOUS"] || 0,              color: T.yellow },
          { label: "INTL ORGS",        value: byType["INTERNATIONAL_ORG"] || 0,      color: T.green },
          { label: "OFFICIALS",        value: byType["OFFICIAL"] || 0,               color: T.blue },
        ].map(({ label, value, color }) => (
          <Card key={label} style={{ padding: "14px 16px", textAlign: "center" }}>
            <div style={{ fontFamily: "'Bebas Neue', sans-serif",
              fontSize: 28, color }}>{value?.toLocaleString()}</div>
            <Mono style={{ fontSize: 8, color: T.muted,
              letterSpacing: "0.1em" }}>{label}</Mono>
          </Card>
        ))}
      </div>

      {/* Anonymous sources by outlet */}
      {data?.top_anonymous_outlets?.length > 0 && (
        <Card style={{ padding: 20, marginBottom: 20 }}>
          <Mono style={{ fontSize: 9, color: T.yellow, letterSpacing: "0.1em",
            display: "block", marginBottom: 14 }}>
            âš  OUTLETS RELYING ON ANONYMOUS SOURCES
          </Mono>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {data.top_anonymous_outlets.map((o, i) => (
              <div key={i} style={{
                background: `${T.yellow}15`, border: `1px solid ${T.yellow}30`,
                borderRadius: 4, padding: "6px 12px", display: "flex",
                alignItems: "center", gap: 8 }}>
                <Mono style={{ fontSize: 10, color: T.text }}>{o.outlet}</Mono>
                <Mono style={{ fontSize: 9, color: T.yellow }}>{o.anon_count} anon</Mono>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* State media citations */}
      {data?.top_state_media_outlets?.length > 0 && (
        <Card style={{ padding: 20, marginBottom: 20 }}>
          <Mono style={{ fontSize: 9, color: T.red, letterSpacing: "0.1em",
            display: "block", marginBottom: 14 }}>
            âš  OUTLETS CITING STATE MEDIA
          </Mono>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {data.top_state_media_outlets.map((o, i) => (
              <div key={i} style={{
                background: `${T.red}15`, border: `1px solid ${T.red}30`,
                borderRadius: 4, padding: "6px 12px", display: "flex",
                alignItems: "center", gap: 8 }}>
                <Mono style={{ fontSize: 10, color: T.text }}>{o.outlet}</Mono>
                <Mono style={{ fontSize: 9, color: T.red }}>{o.sm_count} citations</Mono>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Filters */}
      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap",
        alignItems: "center" }}>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search entity..."
          style={{ background: T.surface, border: `1px solid ${T.border}`,
            color: T.text, borderRadius: 4, padding: "6px 12px",
            fontFamily: "'DM Mono', monospace", fontSize: 10,
            outline: "none", width: 180 }}
        />
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {["", "OFFICIAL", "GOVERNMENT", "MILITARY", "INTERNATIONAL_ORG",
            "EXPERT", "STATE_MEDIA", "ANONYMOUS", "SOCIAL_MEDIA"].map(t => (
            <button key={t} onClick={() => setTypeFilter(t)} style={{
              background: typeFilter === t ? T.blue : T.surface,
              border: `1px solid ${typeFilter === t ? T.blue : T.border}`,
              color: typeFilter === t ? "#fff" : T.muted,
              borderRadius: 4, padding: "5px 10px", cursor: "pointer",
              fontFamily: "'DM Mono', monospace", fontSize: 9,
            }}>{t || "ALL"}</button>
          ))}
        </div>
      </div>

      {/* Entity table */}
      {loading ? <Spinner /> : (
        <Card>
          <div style={{ display: "grid",
            gridTemplateColumns: "28px 1fr 140px 80px 80px 70px",
            gap: 12, padding: "10px 18px",
            borderBottom: `1px solid ${T.border}`,
            background: T.surfaceAlt }}>
            {["#","ENTITY","TYPE","CITATIONS","OUTLETS","SCI"].map(h => (
              <Mono key={h} style={{ fontSize: 8, color: T.muted,
                letterSpacing: "0.1em" }}>{h}</Mono>
            ))}
          </div>

          {profiles.length === 0 ? (
            <div style={{ padding: 40, textAlign: "center" }}>
              <Mono style={{ fontSize: 11, color: T.muted }}>NO SOURCES FOUND</Mono>
            </div>
          ) : profiles.slice(0, 100).map((p, i) => (
            <div key={p.entity}
              onClick={() => setSelected(p.entity)}
              style={{
                display: "grid",
                gridTemplateColumns: "28px 1fr 140px 80px 80px 70px",
                gap: 12, padding: "10px 18px", alignItems: "center",
                borderBottom: `1px solid #0a1120`,
                background: selected === p.entity
                  ? `${T.blue}15`
                  : i % 2 === 0 ? T.surface : T.surfaceAlt,
                cursor: "pointer",
                transition: "background 0.15s",
              }}
              onMouseEnter={e => {
                if (selected !== p.entity)
                  e.currentTarget.style.background = `${T.blue}08`;
              }}
              onMouseLeave={e => {
                if (selected !== p.entity)
                  e.currentTarget.style.background = i % 2 === 0 ? T.surface : T.surfaceAlt;
              }}
            >
              <Mono style={{ fontSize: 10, color: T.muted }}>#{i+1}</Mono>
              <div>
                <div style={{ fontSize: 13, fontWeight: 500 }}>{p.entity}</div>
                {p.outlets_citing?.length > 0 && (
                  <Mono style={{ fontSize: 9, color: T.muted }}>
                    {p.outlets_citing.slice(0,3).join(", ")}
                    {p.outlets_citing.length > 3 ? ` +${p.outlets_citing.length-3}` : ""}
                  </Mono>
                )}
              </div>
              <Mono style={{ fontSize: 9,
                color: typeColor(p.entity_type),
                background: `${typeColor(p.entity_type)}15`,
                border: `1px solid ${typeColor(p.entity_type)}30`,
                padding: "2px 8px", borderRadius: 3 }}>
                {p.entity_type}
              </Mono>
              <Mono style={{ fontSize: 11, color: T.text,
                textAlign: "center" }}>{p.citation_count}</Mono>
              <Mono style={{ fontSize: 11, color: T.muted,
                textAlign: "center" }}>{p.outlet_count}</Mono>
              <div style={{ textAlign: "center" }}>
                <Mono style={{ fontSize: 11,
                  color: sciColor(p.sci_score),
                  fontWeight: 600 }}>{Math.round(p.sci_score)}</Mono>
              </div>
            </div>
          ))}
        </Card>
      )}

      {/* Profile drawer */}
      {selected && (
        <>
          <div
            onClick={() => setSelected(null)}
            style={{
              position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
              zIndex: 999,
            }}
          />
          <SourceProfileDrawer key={selected} entity={selected} onClose={() => setSelected(null)} />
        </>
      )}
    </div>
  );
}


function DigestTab() {
  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(true);
  const [selected, setSelected]   = useState(null);
  const [clusterData, setClusterData] = useState(null);
  const [clusterLoading, setClusterLoading] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState("");
  const [verdictFilter, setVerdictFilter]   = useState("");

  useEffect(() => {
    apiFetch("/clusters?per_page=50")
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!selected) { setClusterData(null); return; }
    setClusterLoading(true);
    apiFetch(`/clusters/${selected.id}`)
      .then(d => { setClusterData(d); setClusterLoading(false); })
      .catch(() => setClusterLoading(false));
  }, [selected]);

  const verdictColor = v => {
    if (v === "WELL_VERIFIED")    return T.green;
    if (v === "MOSTLY_RELIABLE")  return "#22d3ee";
    if (v === "MIXED")            return T.yellow;
    if (v === "CONTESTED")        return T.red;
    if (v === "LOW_CREDIBILITY")  return "#f97316";
    if (v === "LIMITED_COVERAGE") return T.muted;
    return T.muted;
  };

  const divergenceTooltip = score => {
    if (score >= 20) return "Extreme divergence — outlets tell very different stories";
    if (score >= 10) return "High divergence — significant outlet disagreement detected";
    if (score >= 5)  return "Moderate divergence — some outlet variation";
    return "Low divergence — outlets broadly agree";
  };

  const verdictLabel = v => {
    if (v === "WELL_VERIFIED")    return "VERIFIED";
    if (v === "MOSTLY_RELIABLE")  return "RELIABLE";
    if (v === "MIXED")            return "MIXED";
    if (v === "CONTESTED")        return "CONTESTED";
    if (v === "LOW_CREDIBILITY")  return "LOW CRED";
    if (v === "LIMITED_COVERAGE") return "LIMITED";
    return v;
  };

  const vciColor = s => s >= 82 ? T.green : s >= 60 ? T.yellow : T.red;

  const stats   = data?.stats || {};
  const today   = new Date().toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

  const clusters = (data?.clusters || [])
    .filter(c => !categoryFilter || c.category === categoryFilter)
    .filter(c => !verdictFilter  || c.vci_verdict === verdictFilter);

  const contested = clusters.filter(c => c.vci_verdict === "CONTESTED" || c.divergence_score >= 10);
  const limited   = clusters.filter(c => c.vci_verdict === "LIMITED_COVERAGE");
  const verified  = clusters.filter(c => !contested.includes(c) && !limited.includes(c) && (c.vci_verdict === "WELL_VERIFIED" || c.vci_verdict === "MOSTLY_RELIABLE"));
  const rest      = clusters.filter(c => !contested.includes(c) && !limited.includes(c) && !verified.includes(c));

  const categories = [...new Set((data?.clusters || []).map(c => c.category).filter(Boolean))];

  return (
    <div style={{ display: "flex", gap: 24, alignItems: "flex-start" }}>
      {/* Main content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {/* Header */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 12 }}>
              <SectionLabel>DAILY INTELLIGENCE DIGEST</SectionLabel>
              <Mono style={{ fontSize: 9, color: T.muted }}>{today}</Mono>
            </div>
            <button
              onClick={async () => {
                const btn = document.getElementById("digest-dl-btn");
                try {
                  if (btn) btn.textContent = "GENERATING...";
                  const res = await fetch(`${API_BASE}/digest/export`, {
                    headers: { "Content-Type": "application/json" },
                  });
                  if (!res.ok) throw new Error(`Server error ${res.status}`);
                  const blob = await res.blob();
                  const url  = URL.createObjectURL(blob);
                  const a    = document.createElement("a");
                  a.href     = url;
                  a.download = `verdad-score-digest-${new Date().toISOString().slice(0,10)}.pdf`;
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  URL.revokeObjectURL(url);
                } catch(e) {
                  alert("PDF generation failed: " + e.message);
                } finally {
                  if (btn) btn.textContent = "↓ DOWNLOAD PDF";
                }
              }}
              id="digest-dl-btn"
              style={{
                background: T.blue, border: "none", color: "#fff",
                borderRadius: 4, padding: "8px 16px", cursor: "pointer",
                fontFamily: "'DM Mono', monospace", fontSize: 9,
                letterSpacing: "0.08em", display: "flex", alignItems: "center", gap: 6,
              }}
            >
              ↓ DOWNLOAD PDF
            </button>
          </div>
          <Mono style={{ fontSize: 10, color: T.muted }}>
            {stats.total_clusters || 0} stories tracked ·{" "}
            {stats.total_articles || 0} articles analysed ·{" "}
            {stats.contested || 0} contested narratives ·{" "}
            avg divergence {stats.avg_divergence || 0}
          </Mono>
        </div>

        {/* Stats row */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginBottom: 24 }}>
          {[
            { label: "STORIES",      value: stats.total_clusters  || 0, color: T.blue  },
            { label: "WELL VERIFIED",value: stats.well_verified   || 0, color: T.green },
            { label: "CONTESTED",    value: stats.contested       || 0, color: T.red   },
            { label: "AVG DIVERGENCE", value: stats.avg_divergence || 0, color: T.yellow },
          ].map(({ label, value, color }) => (
            <Card key={label} style={{ padding: "14px 16px", textAlign: "center" }}>
              <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 28, color }}>{value}</div>
              <Mono style={{ fontSize: 8, color: T.muted, letterSpacing: "0.1em" }}>{label}</Mono>
            </Card>
          ))}
        </div>

        {/* Filters */}
        <div style={{ display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap" }}>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {["", ...categories].map(c => (
              <button key={c} onClick={() => setCategoryFilter(c)} style={{
                background: categoryFilter === c ? T.blue : T.surface,
                border: `1px solid ${categoryFilter === c ? T.blue : T.border}`,
                color: categoryFilter === c ? "#fff" : T.muted,
                borderRadius: 4, padding: "5px 10px", cursor: "pointer",
                fontFamily: "'DM Mono', monospace", fontSize: 9,
              }}>{c || "ALL"}</button>
            ))}
          </div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {["", "WELL_VERIFIED", "MOSTLY_RELIABLE", "MIXED", "CONTESTED"].map(v => (
              <button key={v} onClick={() => setVerdictFilter(v)} style={{
                background: verdictFilter === v ? verdictColor(v) : T.surface,
                border: `1px solid ${verdictFilter === v ? verdictColor(v) : T.border}`,
                color: verdictFilter === v ? "#fff" : T.muted,
                borderRadius: 4, padding: "5px 10px", cursor: "pointer",
                fontFamily: "'DM Mono', monospace", fontSize: 9,
              }}>{v ? verdictLabel(v) : "ALL VERDICTS"}</button>
            ))}
          </div>
        </div>

        {loading ? <Spinner /> : (
          <>
            {/* CONTESTED section */}
            {contested.length > 0 && !categoryFilter && !verdictFilter && (
              <div style={{ marginBottom: 28 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
                  <Mono style={{ fontSize: 9, color: T.red, letterSpacing: "0.1em" }}>
                    ⚠ CONTESTED NARRATIVES — OUTLETS DIVERGE ON THESE STORIES
                  </Mono>
                </div>
                {contested.map(c => (
                  <ClusterCard key={c.id} cluster={c} selected={selected?.id === c.id}
                    onSelect={() => setSelected(selected?.id === c.id ? null : c)}
                    verdictColor={verdictColor} verdictLabel={verdictLabel} vciColor={vciColor}
                    divergenceTooltip={divergenceTooltip} />
                ))}
              </div>
            )}

            {/* Main stories */}
            {(categoryFilter || verdictFilter ? clusters : [...verified, ...rest]).map(c => (
              <ClusterCard key={c.id} cluster={c} selected={selected?.id === c.id}
                onSelect={() => setSelected(selected?.id === c.id ? null : c)}
                verdictColor={verdictColor} verdictLabel={verdictLabel} vciColor={vciColor}
                divergenceTooltip={divergenceTooltip} />
            ))}

            {/* Limited coverage section */}
            {limited.length > 0 && !categoryFilter && !verdictFilter && (
              <div style={{ marginTop: 28 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
                  <Mono style={{ fontSize: 9, color: T.muted, letterSpacing: "0.1em" }}>
                    EMERGING / LIMITED COVERAGE — FEW SOURCES AVAILABLE
                  </Mono>
                </div>
                {limited.map(c => (
                  <ClusterCard key={c.id} cluster={c} selected={selected?.id === c.id}
                    onSelect={() => setSelected(selected?.id === c.id ? null : c)}
                    verdictColor={verdictColor} verdictLabel={verdictLabel} vciColor={vciColor}
                    divergenceTooltip={divergenceTooltip} />
                ))}
              </div>
            )}

            {clusters.length === 0 && (
              <Card style={{ padding: 40, textAlign: "center" }}>
                <Mono style={{ fontSize: 11, color: T.muted }}>NO STORIES FOUND</Mono>
                <Mono style={{ fontSize: 10, color: T.muted, display: "block", marginTop: 8 }}>
                  Clusters are built after each scraper run. Check back after the next pipeline cycle.
                </Mono>
              </Card>
            )}
          </>
        )}
      </div>

    </div>
  );

  // Full page cluster detail
  if (selected) {
    const c = clusterData;
    const digestTabs = ["OVERVIEW", "ARTICLES", "OUTLETS"];
    return (
      <div style={{ animation: "fadeIn 0.2s ease" }}>
        <button onClick={() => setSelected(null)} style={{
          background: "none", border: `1px solid ${T.border}`, color: T.muted,
          borderRadius: 4, padding: "6px 14px", cursor: "pointer", marginBottom: 20,
          fontFamily: "'DM Mono'", fontSize: 10,
        }}>← BACK TO DIGEST</button>

        {clusterLoading ? <Spinner /> : c ? (
          <div>
            {/* Header */}
            <Card style={{ padding: 24, marginBottom: 16 }}>
              <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
                <Mono style={{ fontSize: 9,
                  color: verdictColor(c.vci_verdict),
                  background: `${verdictColor(c.vci_verdict)}15`,
                  border: `1px solid ${verdictColor(c.vci_verdict)}30`,
                  padding: "3px 10px", borderRadius: 3 }}>
                  {verdictLabel(c.vci_verdict)}
                </Mono>
                {c.category && (
                  <Mono style={{ fontSize: 9, color: T.muted,
                    background: T.surface, padding: "3px 10px", borderRadius: 3 }}>
                    {c.category}
                  </Mono>
                )}
                {c.divergence_score >= 10 && (
                  <Mono style={{ fontSize: 9, color: T.red,
                    background: `${T.red}15`, padding: "3px 10px", borderRadius: 3 }}>
                    ⚠ HIGH DIVERGENCE
                  </Mono>
                )}
              </div>
              <div style={{ fontSize: 20, fontWeight: 700, color: T.text,
                lineHeight: 1.4, marginBottom: 16 }}>{c.headline}</div>

              {/* KPIs */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)",
                gap: 8, paddingTop: 16, borderTop: `1px solid ${T.border}` }}>
                {[
                  { label: "AVG VCI",    value: c.avg_vci,        color: vciColor(c.avg_vci) },
                  { label: "MIN VCI",    value: c.min_vci,        color: T.red },
                  { label: "MAX VCI",    value: c.max_vci,        color: T.green },
                  { label: "ARTICLES",  value: c.article_count,  color: T.blue },
                  { label: "OUTLETS",   value: c.outlet_count,   color: T.cyan },
                ].map(k => (
                  <div key={k.label} style={{ textAlign: "center" }}>
                    <Mono style={{ fontSize: 8, color: T.muted, display: "block",
                      marginBottom: 4 }}>{k.label}</Mono>
                    <div style={{ fontFamily: "'Bebas Neue'", fontSize: 28,
                      color: k.color, lineHeight: 1 }}>{k.value ?? "—"}</div>
                  </div>
                ))}
              </div>
            </Card>

            {/* Tabs */}
            <div style={{ display: "flex", gap: 0, marginBottom: 16,
              borderBottom: `1px solid ${T.border}` }}>
              {digestTabs.map(t => (
                <button key={t} onClick={() => setSelected({...selected, _tab: t})} style={{
                  background: "none", border: "none",
                  borderBottom: (selected._tab||"OVERVIEW") === t
                    ? `2px solid ${T.blue}` : "2px solid transparent",
                  color: (selected._tab||"OVERVIEW") === t ? T.blue : T.muted,
                  padding: "8px 16px", cursor: "pointer",
                  fontFamily: "'DM Mono'", fontSize: 10, letterSpacing: "0.06em",
                }}>{t}</button>
              ))}
            </div>

            {/* OVERVIEW */}
            {(selected._tab||"OVERVIEW") === "OVERVIEW" && (
              <div className="two-col-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                  {c.summary && (
                    <Card style={{ padding: 20, borderLeft: `3px solid ${verdictColor(c.vci_verdict)}` }}>
                      <SectionLabel>STORY SUMMARY</SectionLabel>
                      <p style={{ fontSize: 14, color: T.text, lineHeight: 1.7, margin: 0 }}>
                        {c.summary}
                      </p>
                    </Card>
                  )}
                  <Card style={{ padding: 20 }}>
                    <SectionLabel>CREDIBILITY RANGE</SectionLabel>
                    <div style={{ marginBottom: 8 }}>
                      <div style={{ display: "flex", justifyContent: "space-between",
                        marginBottom: 6 }}>
                        <Mono style={{ fontSize: 9, color: T.red }}>MIN {c.min_vci}</Mono>
                        <Mono style={{ fontSize: 9, color: vciColor(c.avg_vci),
                          fontWeight: 700 }}>AVG {c.avg_vci}</Mono>
                        <Mono style={{ fontSize: 9, color: T.green }}>MAX {c.max_vci}</Mono>
                      </div>
                      <div style={{ height: 8, background: T.border,
                        borderRadius: 4, position: "relative" }}>
                        <div style={{ position: "absolute",
                          left: `${c.min_vci}%`,
                          width: `${c.max_vci - c.min_vci}%`,
                          height: "100%", borderRadius: 4,
                          background: `linear-gradient(90deg, ${T.red}, ${T.green})` }} />
                      </div>
                    </div>
                    <Mono style={{ fontSize: 10, color: T.muted }}>
                      {divergenceTooltip(c.divergence_score)}
                    </Mono>
                  </Card>
                </div>
                <Card style={{ padding: 20 }}>
                  <SectionLabel>OUTLET COVERAGE</SectionLabel>
                  {(c.outlet_detail || []).map((o, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center",
                      gap: 10, padding: "9px 0",
                      borderBottom: i < c.outlet_detail.length-1
                        ? `1px solid ${T.border}` : "none" }}>
                      <ScoreRing score={o.avg_vci} size={36} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, color: T.text,
                          fontWeight: 500 }}>{o.outlet}</div>
                        <Mono style={{ fontSize: 9, color: T.muted }}>
                          {o.articles?.length} articles
                        </Mono>
                      </div>
                      <Mono style={{ fontSize: 12, fontWeight: 700,
                        color: vciColor(o.avg_vci) }}>{o.avg_vci}</Mono>
                    </div>
                  ))}
                </Card>
              </div>
            )}

            {/* ARTICLES */}
            {(selected._tab||"OVERVIEW") === "ARTICLES" && (
              <Card style={{ padding: 20 }}>
                <SectionLabel>ALL ARTICLES ({(c.articles||[]).length})</SectionLabel>
                {(c.articles || []).map((a, i) => (
                  <div key={i}
                    onClick={() => a.url && window.open(a.url, "_blank")}
                    style={{ display: "flex", alignItems: "flex-start",
                      gap: 12, padding: "12px 0",
                      borderBottom: i < c.articles.length-1
                        ? `1px solid ${T.border}` : "none",
                      cursor: a.url ? "pointer" : "default" }}
                    onMouseOver={e => e.currentTarget.style.opacity="0.75"}
                    onMouseOut={e => e.currentTarget.style.opacity="1"}>
                    <div style={{ width: 44, height: 44, borderRadius: 6,
                      flexShrink: 0, background: `${vciColor(a.vci_score)}15`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      border: `1px solid ${vciColor(a.vci_score)}30` }}>
                      <div style={{ fontFamily: "'Bebas Neue'", fontSize: 18,
                        color: vciColor(a.vci_score) }}>
                        {Math.round(a.vci_score)}
                      </div>
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 500, color: T.text,
                        lineHeight: 1.4, marginBottom: 4 }}>{a.title}</div>
                      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                        <Mono style={{ fontSize: 9, color: T.blue }}>{a.outlet}</Mono>
                        {a.layer4_triggered && (
                          <Mono style={{ fontSize: 8, color: T.yellow,
                            background: `${T.yellow}15`, padding: "1px 5px",
                            borderRadius: 2 }}>L4 VERIFIED</Mono>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </Card>
            )}

            {/* OUTLETS */}
            {(selected._tab||"OVERVIEW") === "OUTLETS" && (
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {(c.outlet_detail || []).map((o, i) => (
                  <Card key={i} style={{ padding: 20,
                    borderLeft: `3px solid ${vciColor(o.avg_vci)}` }}>
                    <div style={{ display: "flex", alignItems: "center",
                      gap: 16, marginBottom: 16 }}>
                      <ScoreRing score={o.avg_vci} size={52} />
                      <div>
                        <div style={{ fontSize: 16, fontWeight: 600,
                          color: T.text, marginBottom: 4 }}>{o.outlet}</div>
                        <Mono style={{ fontSize: 10, color: T.muted }}>
                          {o.articles?.length} articles on this story
                        </Mono>
                      </div>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      {(o.articles || []).map((a, j) => (
                        <div key={j}
                          onClick={() => a.url && window.open(a.url, "_blank")}
                          style={{ fontSize: 12, color: T.muted, padding: "6px 10px",
                            background: T.surface, borderRadius: 4,
                            cursor: a.url ? "pointer" : "default",
                            display: "flex", justifyContent: "space-between",
                            alignItems: "center", gap: 10 }}>
                          <span style={{ flex: 1, overflow: "hidden",
                            textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {a.title}
                          </span>
                          <Mono style={{ fontSize: 10, color: vciColor(a.vci_score),
                            fontWeight: 700, flexShrink: 0 }}>
                            {Math.round(a.vci_score)}
                          </Mono>
                        </div>
                      ))}
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        ) : <Mono style={{ fontSize: 11, color: T.muted }}>Loading story details...</Mono>}
      </div>
    );
  }
}

function ClusterCard({ cluster: c, selected, onSelect, verdictColor, verdictLabel, vciColor, divergenceTooltip }) {
  const totalCiting = (c.reliable_count || 0) + (c.at_risk_count || 0) + (c.blacklisted_count || 0);
  const reliablePct = totalCiting > 0 ? Math.round((c.reliable_count / totalCiting) * 100) : 0;

  return (
    <div onClick={onSelect} style={{
      padding: "16px 20px", marginBottom: 10, borderRadius: 6, cursor: "pointer",
      background: selected ? `${T.blue}10` : T.surface,
      border: `1px solid ${selected ? T.blue : T.border}`,
      transition: "all 0.15s",
    }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6,
            flexWrap: "wrap" }}>
            <Mono style={{
              fontSize: 9, padding: "2px 8px", borderRadius: 3,
              color: verdictColor(c.vci_verdict),
              background: `${verdictColor(c.vci_verdict)}15`,
              border: `1px solid ${verdictColor(c.vci_verdict)}30`,
            }}>{verdictLabel(c.vci_verdict)}</Mono>
            <Mono style={{ fontSize: 9, color: T.muted }}>{c.category}</Mono>
            {c.divergence_score >= 5 && (
              <Mono
                title={divergenceTooltip ? divergenceTooltip(c.divergence_score) : ""}
                style={{
                  fontSize: 9,
                  color: c.divergence_score >= 10 ? T.red : T.yellow,
                  background: c.divergence_score >= 10 ? `${T.red}10` : `${T.yellow}10`,
                  padding: "2px 6px", borderRadius: 3, cursor: "help",
                }}>
                ⚡ {c.divergence_score >= 10 ? "HIGH" : "MOD"} DIVERGENCE {c.divergence_score}
              </Mono>
            )}
          </div>
          <div style={{ fontSize: 14, fontWeight: 600, lineHeight: 1.4,
            marginBottom: 6, color: T.text }}>{c.headline}</div>
          {c.summary && (
            <p style={{ fontSize: 12, color: T.muted, lineHeight: 1.5,
              margin: "0 0 8px 0" }}>{c.summary}</p>
          )}
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
            <Mono style={{ fontSize: 9, color: T.muted }}>
              {c.article_count} articles
            </Mono>
            <Mono style={{ fontSize: 9, color: T.muted }}>
              {c.outlet_count} outlets
            </Mono>
            <Mono style={{ fontSize: 9, color: vciColor(c.avg_vci) }}>
              VCI {c.avg_vci}
            </Mono>
            {c.divergence_score > 0 && (
              <Mono style={{ fontSize: 9, color: T.muted }}>
                divergence {c.divergence_score}
              </Mono>
            )}
            {c.keywords?.length > 0 && (
              <Mono style={{ fontSize: 9, color: T.muted }}>
                {c.keywords.slice(0, 3).join(" · ")}
              </Mono>
            )}
          </div>
        </div>

        {/* VCI gauge */}
        <div style={{ textAlign: "center", flexShrink: 0 }}>
          <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 28,
            color: vciColor(c.avg_vci), lineHeight: 1 }}>{c.avg_vci}</div>
          <Mono style={{ fontSize: 8, color: T.muted, letterSpacing: "0.1em" }}>VCI</Mono>
          <div style={{ marginTop: 4 }}>
            <Mono style={{ fontSize: 8, color: T.green }}>{c.reliable_count}R</Mono>
            <Mono style={{ fontSize: 8, color: T.muted }}> · </Mono>
            <Mono style={{ fontSize: 8, color: T.yellow }}>{c.at_risk_count}A</Mono>
            <Mono style={{ fontSize: 8, color: T.muted }}> · </Mono>
            <Mono style={{ fontSize: 8, color: T.red }}>{c.blacklisted_count}B</Mono>
          </div>
        </div>
      </div>
    </div>
  );
}

function NarrativesTab() {
  const [risk, setRisk]       = useState("");
  const [type, setType]       = useState("");
  const [selected, setSelected] = useState(null);
  const [narrativeTab, setNarrativeTab] = useState("OVERVIEW");
  const path = `/narratives?per_page=50${risk ? `&risk=${risk}` : ""}${type ? `&type=${type}` : ""}`;
  const { data, loading, error, refetch } = useAPI(path, [risk, type]);

  const nRiskColor = r => r === "HIGH" ? T.red : r === "MEDIUM" ? T.yellow : T.green;
  const nTypeColor = t => t === "COORDINATED" ? T.red : t === "BREAKING" ? T.blue : T.muted;

  // Detail view
  if (selected) {
    const n = selected;
    const tabs = ["OVERVIEW", "OUTLETS", "KEYWORDS"];
    const rc = nRiskColor(n.risk_level);
    const highOutlets = n.high_vci_outlets || [];
    const lowOutlets  = n.low_vci_outlets  || [];
    const midOutlets  = (n.all_outlets || []).filter(o => !highOutlets.includes(o) && !lowOutlets.includes(o));

    return (
      <div style={{ animation: "fadeIn 0.2s ease" }}>
        <button onClick={() => { setSelected(null); setNarrativeTab("OVERVIEW"); }} style={{
          background: "none", border: `1px solid ${T.border}`, color: T.muted,
          borderRadius: 4, padding: "6px 14px", cursor: "pointer", marginBottom: 20,
          fontFamily: "'DM Mono'", fontSize: 10,
        }}>← BACK TO NARRATIVES</button>

        {/* Header */}
        <Card style={{ padding: 24, marginBottom: 16 }}>
          <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
            <Mono style={{ fontSize: 9, color: rc, background: `${rc}15`,
              border: `1px solid ${rc}30`, padding: "3px 10px", borderRadius: 3 }}>
              {n.risk_level} RISK
            </Mono>
            <Mono style={{ fontSize: 9, color: nTypeColor(n.narrative_type),
              background: `${nTypeColor(n.narrative_type)}15`,
              border: `1px solid ${nTypeColor(n.narrative_type)}30`,
              padding: "3px 10px", borderRadius: 3 }}>
              {n.narrative_type}
            </Mono>
            <Mono style={{ fontSize: 9, color: T.muted,
              background: T.surface, padding: "3px 10px", borderRadius: 3 }}>
              {n.category}
            </Mono>
            <Mono style={{ fontSize: 9, color: T.muted, marginLeft: "auto" }}>
              {n.detected_at ? new Date(n.detected_at).toLocaleString() : ""}
            </Mono>
          </div>
          <div style={{ fontSize: 20, fontWeight: 700, color: T.text,
            lineHeight: 1.4, marginBottom: 8 }}>{n.sample_title}</div>

          {/* KPIs */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)",
            gap: 8, marginTop: 20, paddingTop: 20,
            borderTop: `1px solid ${T.border}` }}>
            {[
              { label: "AVG VCI",       value: n.avg_vci,      color: vciColor(n.avg_vci) },
              { label: "OUTLETS",       value: n.outlet_count, color: T.blue },
              { label: "HIGH CRED",     value: highOutlets.length, color: T.green },
              { label: "LOW CRED",      value: lowOutlets.length,  color: T.red },
            ].map(k => (
              <div key={k.label} style={{ textAlign: "center" }}>
                <Mono style={{ fontSize: 8, color: T.muted, display: "block",
                  marginBottom: 4 }}>{k.label}</Mono>
                <div style={{ fontFamily: "'Bebas Neue'", fontSize: 28,
                  color: k.color, lineHeight: 1 }}>{k.value ?? "—"}</div>
              </div>
            ))}
          </div>
        </Card>

        {/* Tabs */}
        <div style={{ display: "flex", gap: 0, marginBottom: 16,
          borderBottom: `1px solid ${T.border}` }}>
          {tabs.map(t => (
            <button key={t} onClick={() => setNarrativeTab(t)} style={{
              background: "none", border: "none",
              borderBottom: narrativeTab === t ? `2px solid ${T.blue}` : "2px solid transparent",
              color: narrativeTab === t ? T.blue : T.muted,
              padding: "8px 16px", cursor: "pointer",
              fontFamily: "'DM Mono'", fontSize: 10, letterSpacing: "0.06em",
            }}>{t}</button>
          ))}
        </div>

        {/* OVERVIEW TAB */}
        {narrativeTab === "OVERVIEW" && (
          <div className="two-col-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <Card style={{ padding: 20, borderLeft: `3px solid ${rc}` }}>
                <SectionLabel>NARRATIVE SUMMARY</SectionLabel>
                <p style={{ fontSize: 14, color: T.text, lineHeight: 1.7, margin: 0 }}>
                  {n.summary || "No summary available."}
                </p>
              </Card>
              {n.risk_level === "HIGH" && (
                <Card style={{ padding: 20, background: `${T.red}08`,
                  border: `1px solid ${T.red}20` }}>
                  <SectionLabel>⚠ HIGH RISK ALERT</SectionLabel>
                  <p style={{ fontSize: 13, color: T.muted, lineHeight: 1.6, margin: 0 }}>
                    This narrative is being amplified by {lowOutlets.length} low-credibility
                    outlet{lowOutlets.length !== 1 ? "s" : ""} alongside {highOutlets.length} reliable
                    source{highOutlets.length !== 1 ? "s" : ""}. The divergence in credibility
                    suggests this story may be contested or selectively framed.
                  </p>
                </Card>
              )}
              <Card style={{ padding: 20 }}>
                <SectionLabel>CREDIBILITY SPREAD</SectionLabel>
                {[
                  { label: "High credibility outlets", outlets: highOutlets, color: T.green },
                  { label: "Mid credibility outlets",  outlets: midOutlets,  color: T.yellow },
                  { label: "Low credibility outlets",  outlets: lowOutlets,  color: T.red },
                ].map(row => row.outlets.length > 0 ? (
                  <div key={row.label} style={{ marginBottom: 14 }}>
                    <div style={{ display: "flex", justifyContent: "space-between",
                      marginBottom: 6 }}>
                      <Mono style={{ fontSize: 10, color: T.muted }}>{row.label}</Mono>
                      <Mono style={{ fontSize: 10, color: row.color }}>{row.outlets.length}</Mono>
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                      {row.outlets.map(o => (
                        <Mono key={o} style={{ fontSize: 8, color: row.color,
                          background: `${row.color}10`,
                          border: `1px solid ${row.color}20`,
                          padding: "2px 6px", borderRadius: 2 }}>{o}</Mono>
                      ))}
                    </div>
                  </div>
                ) : null)}
              </Card>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <Card style={{ padding: 20 }}>
                <SectionLabel>KEYWORDS</SectionLabel>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {(n.keywords || []).map(kw => (
                    <div key={kw} style={{ padding: "6px 12px",
                      background: `${T.blue}10`,
                      border: `1px solid ${T.blue}20`, borderRadius: 4 }}>
                      <Mono style={{ fontSize: 10, color: T.blue }}>{kw}</Mono>
                    </div>
                  ))}
                </div>
              </Card>
              <Card style={{ padding: 20 }}>
                <SectionLabel>ALL OUTLETS COVERING THIS STORY</SectionLabel>
                {(n.all_outlets || []).map((o, i) => {
                  const isHigh = highOutlets.includes(o);
                  const isLow  = lowOutlets.includes(o);
                  const color  = isHigh ? T.green : isLow ? T.red : T.muted;
                  return (
                    <div key={o} style={{ display: "flex", alignItems: "center",
                      gap: 10, padding: "8px 0",
                      borderBottom: i < n.all_outlets.length-1 ? `1px solid ${T.border}` : "none" }}>
                      <div style={{ width: 8, height: 8, borderRadius: "50%",
                        background: color, flexShrink: 0 }} />
                      <Mono style={{ fontSize: 11, color: T.text, flex: 1 }}>{o}</Mono>
                      <Mono style={{ fontSize: 9, color }}>
                        {isHigh ? "HIGH CRED" : isLow ? "LOW CRED" : "MID CRED"}
                      </Mono>
                    </div>
                  );
                })}
              </Card>
            </div>
          </div>
        )}

        {/* OUTLETS TAB */}
        {narrativeTab === "OUTLETS" && (
          <div className="three-col-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
            {[
              { label: "HIGH CREDIBILITY", outlets: highOutlets, color: T.green,
                desc: "These outlets are reporting this story with high credibility scores." },
              { label: "MID CREDIBILITY",  outlets: midOutlets,  color: T.yellow,
                desc: "These outlets show mixed credibility on this narrative." },
              { label: "LOW CREDIBILITY",  outlets: lowOutlets,  color: T.red,
                desc: "These outlets are amplifying this story with low credibility scores." },
            ].map(col => (
              <Card key={col.label} style={{ padding: 20,
                borderTop: `2px solid ${col.color}` }}>
                <Mono style={{ fontSize: 9, color: col.color, letterSpacing: "0.1em",
                  display: "block", marginBottom: 8 }}>{col.label}</Mono>
                <div style={{ fontSize: 11, color: T.muted, marginBottom: 16,
                  lineHeight: 1.5 }}>{col.desc}</div>
                {col.outlets.length > 0 ? col.outlets.map((o, i) => (
                  <div key={o} style={{ padding: "8px 0",
                    borderBottom: i < col.outlets.length-1 ? `1px solid ${T.border}` : "none" }}>
                    <Mono style={{ fontSize: 11, color: T.text }}>{o}</Mono>
                  </div>
                )) : (
                  <Mono style={{ fontSize: 10, color: T.muted }}>None detected</Mono>
                )}
              </Card>
            ))}
          </div>
        )}

        {/* KEYWORDS TAB */}
        {narrativeTab === "KEYWORDS" && (
          <Card style={{ padding: 24 }}>
            <SectionLabel>NARRATIVE KEYWORDS & SIGNALS</SectionLabel>
            <div style={{ display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
              gap: 10, marginTop: 8 }}>
              {(n.keywords || []).map((kw, i) => (
                <div key={kw} style={{ padding: "12px 16px",
                  background: `${T.blue}08`,
                  border: `1px solid ${T.blue}20`,
                  borderRadius: 6, textAlign: "center" }}>
                  <Mono style={{ fontSize: 11, color: T.blue }}>{kw}</Mono>
                  <Mono style={{ fontSize: 8, color: T.muted, display: "block",
                    marginTop: 4 }}>#{i+1}</Mono>
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: "flex", gap: 10, marginBottom: 18,
        alignItems: "center", flexWrap: "wrap" }}>
        <SectionLabel>NARRATIVE CORRELATION ENGINE</SectionLabel>
        <div style={{ display: "flex", gap: 8, marginLeft: "auto" }}>
          {[["Risk",  ["","HIGH","MEDIUM","LOW"],               risk, setRisk],
            ["Type",  ["","COORDINATED","TRENDING","BREAKING"], type, setType]
          ].map(([label, opts, val, set]) => (
            <select key={label} value={val} onChange={e => set(e.target.value)} style={{
              background: T.surface, border: `1px solid ${T.border}`,
              color: T.text, borderRadius: 4, padding: "5px 10px",
              fontFamily: "'DM Mono', monospace", fontSize: 10, cursor: "pointer",
            }}>
              {opts.map(o => <option key={o} value={o}>{o || `All ${label}`}</option>)}
            </select>
          ))}
          <button onClick={refetch} style={{
            background: "none", border: `1px solid ${T.border}`, color: T.muted,
            borderRadius: 4, padding: "5px 12px", cursor: "pointer",
            fontFamily: "'DM Mono', monospace", fontSize: 10,
          }}>↺</button>
        </div>
      </div>

      {/* Summary stats */}
      {data && (
        <div style={{ display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
          gap: 10, marginBottom: 18 }}>
          {[
            { label: "HIGH RISK",   value: data.narratives?.filter(n=>n.risk_level==="HIGH").length||0,   color: T.red },
            { label: "COORDINATED", value: data.narratives?.filter(n=>n.narrative_type==="COORDINATED").length||0, color: T.yellow },
            { label: "TOTAL",       value: data.total || 0, color: T.blue },
          ].map(({ label, value, color }) => (
            <Card key={label} style={{ padding: "14px 18px", textAlign: "center" }}>
              <div style={{ fontFamily: "'Bebas Neue', sans-serif",
                fontSize: 32, color }}>{value}</div>
              <Mono style={{ fontSize: 8, color: T.muted,
                letterSpacing: "0.1em" }}>{label}</Mono>
            </Card>
          ))}
        </div>
      )}

      {loading ? <Spinner /> : error ? <ErrorBox message={error} onRetry={refetch} /> : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {(data?.narratives || []).length === 0 ? (
            <Card style={{ padding: 40, textAlign: "center" }}>
              <Mono style={{ fontSize: 11, color: T.muted }}>NO NARRATIVES DETECTED</Mono>
            </Card>
          ) : (data?.narratives || []).map((n, i) => (
            <Card key={n.id || i}
              onClick={() => { setSelected(n); setNarrativeTab("OVERVIEW"); }}
              style={{ padding: 18, cursor: "pointer", transition: "all 0.15s" }}
              onMouseOver={e => e.currentTarget.style.borderColor = T.blue}
              onMouseOut={e => e.currentTarget.style.borderColor = T.border}>
              <div style={{ display: "flex", gap: 12, alignItems: "flex-start",
                flexWrap: "wrap" }}>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <div style={{ display: "flex", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
                    <Mono style={{ fontSize: 9, color: nRiskColor(n.risk_level),
                      background: `${nRiskColor(n.risk_level)}15`,
                      border: `1px solid ${nRiskColor(n.risk_level)}40`,
                      padding: "2px 8px", borderRadius: 3 }}>
                      {n.risk_level} RISK
                    </Mono>
                    <Mono style={{ fontSize: 9, color: nTypeColor(n.narrative_type),
                      background: `${nTypeColor(n.narrative_type)}15`,
                      border: `1px solid ${nTypeColor(n.narrative_type)}40`,
                      padding: "2px 8px", borderRadius: 3 }}>
                      {n.narrative_type}
                    </Mono>
                    <Mono style={{ fontSize: 9, color: T.muted }}>{n.category}</Mono>
                    <Mono style={{ fontSize: 9, color: T.muted, marginLeft: "auto" }}>
                      {n.detected_at ? new Date(n.detected_at).toLocaleString() : ""}
                    </Mono>
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 600,
                    marginBottom: 6 }}>{n.sample_title}</div>
                  <div style={{ fontSize: 11, color: T.muted,
                    marginBottom: 10 }}>{n.summary}</div>
                  <div style={{ display: "flex", gap: 4, flexWrap: "wrap",
                    marginBottom: 10 }}>
                    {(n.keywords || []).slice(0,6).map(kw => (
                      <Mono key={kw} style={{ fontSize: 8, color: T.blue,
                        background: `${T.blue}15`, border: `1px solid ${T.blue}30`,
                        padding: "1px 6px", borderRadius: 2 }}>{kw}</Mono>
                    ))}
                  </div>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {(n.all_outlets || []).map(o => {
                      const isLow  = (n.low_vci_outlets  || []).includes(o);
                      const isHigh = (n.high_vci_outlets || []).includes(o);
                      const color  = isLow ? T.red : isHigh ? T.green : T.muted;
                      return (
                        <Mono key={o} style={{ fontSize: 8, color,
                          background: `${color}10`, border: `1px solid ${color}30`,
                          padding: "2px 6px", borderRadius: 2 }}>{o}</Mono>
                      );
                    })}
                  </div>
                </div>
                <div style={{ textAlign: "center", minWidth: 70 }}>
                  <ScoreRing score={n.avg_vci} size={56} />
                  <Mono style={{ fontSize: 8, color: T.muted, display: "block",
                    marginTop: 4 }}>AVG VCI</Mono>
                  <Mono style={{ fontSize: 8, color: T.muted,
                    display: "block" }}>{n.outlet_count} OUTLETS</Mono>
                  <Mono style={{ fontSize: 8, color: T.blue,
                    display: "block", marginTop: 4 }}>CLICK FOR DETAIL →</Mono>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}


function DemoTab() {
  const { data: stats }   = useAPI("/stats");
  const { data: outlets } = useAPI("/outlets?sort_by=oci_score&order=desc&per_page=50");

  const rt      = outlets?.outlets?.find(o => o.name === "RT English");
  const reuters = outlets?.outlets?.find(o => o.name === "Reuters");
  const nature  = outlets?.outlets?.find(o => o.name === "Nature News");
  const breit   = outlets?.outlets?.find(o => o.name === "Breitbart");
  const blacklisted = stats?.by_status?.Blacklisted || 435;
  const atRisk      = stats?.by_status?.["At Risk"]  || 3246;
  const reliable    = stats?.by_status?.Reliable     || 2458;

  const metrics = [
    { label: "ARTICLES SCORED",    value: stats?.total_articles?.toLocaleString()  || "6,000+", icon: "◈" },
    { label: "OUTLETS TRACKED",    value: stats?.total_outlets?.toLocaleString()   || "55+",    icon: "◉" },
    { label: "AUTHORS TRACKED",    value: "1,000+",                                             icon: "◎" },
    { label: "FACT-CHECKERS USED", value: "6",                                                  icon: "◆" },
  ];

  const steps = [
    {
      num: "01",
      title: "INGEST",
      desc: "64 RSS feeds and news APIs scraped every 6 hours — from Nature News to RT English. No source is excluded.",
      color: T.blue,
    },
    {
      num: "02",
      title: "SCORE",
      desc: "Each article passes through a 4-layer VCI engine: source registry, signal detection, sentiment analysis, and external fact-check verification.",
      color: T.purple,
    },
    {
      num: "03",
      title: "RANK",
      desc: "Outlets accumulate scores across all their articles. A single verified lie costs more than a thousand accurate stories earn back.",
      color: T.green,
    },
    {
      num: "04",
      title: "VERIFY",
      desc: "Articles below 70 VCI trigger Layer 4: automated cross-referencing against Google Fact Check, Full Fact, PolitiFact, Snopes, Africa Check and AFP — with AI relevance filtering.",
      color: T.yellow,
    },
  ];

  const comparisons = [
    { outlet: rt,      fallback: { name: "RT English", avg_vci: 26.8, country: "RU", status: "Blacklisted" },
      label: "STATE PROPAGANDA", desc: "Russian state media. Consistently publishes misleading narratives, fabricated claims, and war disinformation. Lowest VCI in our registry." },
    { outlet: breit,   fallback: { name: "Breitbart", avg_vci: 35.0, country: "US", status: "Blacklisted" },
      label: "EXTREME BIAS",     desc: "Far-right outlet with documented history of misleading headlines, selective omission of facts, and amplifying unverified claims." },
    { outlet: reuters, fallback: { name: "Reuters", avg_vci: 91.0, country: "GB", status: "Reliable" },
      label: "GOLD STANDARD",    desc: "Global wire service with rigorous editorial standards, transparent corrections policy, and consistent factual accuracy across decades." },
    { outlet: nature,  fallback: { name: "Nature News", avg_vci: 95.7, country: "GB", status: "Reliable" },
      label: "PEER REVIEWED",    desc: "World's leading scientific journal. Every claim is peer-reviewed. Retractions are published prominently. Highest VCI in our registry." },
  ];

  return (
    <div style={{ animation: "fadeIn 0.4s ease" }}>

      {/* HERO */}
      <div style={{
        background: `linear-gradient(135deg, #0a1628 0%, #0d1f3c 50%, #0a1628 100%)`,
        border: `1px solid ${T.border}`, borderRadius: 12,
        padding: "64px 48px", marginBottom: 24, position: "relative", overflow: "hidden",
      }}>
        {/* Background grid */}
        <div style={{
          position: "absolute", inset: 0, opacity: 0.04,
          backgroundImage: "linear-gradient(#3b82f6 1px, transparent 1px), linear-gradient(90deg, #3b82f6 1px, transparent 1px)",
          backgroundSize: "40px 40px",
        }} />
        {/* Glow */}
        <div style={{
          position: "absolute", top: -100, right: -100,
          width: 400, height: 400, borderRadius: "50%",
          background: "radial-gradient(circle, #3b82f620 0%, transparent 70%)",
        }} />
        <div style={{ position: "relative", zIndex: 1 }}>
          <Mono style={{ fontSize: 10, color: T.blue, letterSpacing: "0.2em", display: "block", marginBottom: 16 }}>
            VERDAD SCORE · INVESTOR BRIEFING · CONFIDENTIAL
          </Mono>
          <div style={{
            fontFamily: "'Bebas Neue', sans-serif", fontSize: 64,
            letterSpacing: "0.05em", lineHeight: 1, marginBottom: 16,
            background: "linear-gradient(135deg, #E2E8F0 0%, #94A3B8 100%)",
            WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
          }}>
            THE CREDIT SCORE<br/>FOR NEWS
          </div>
          <div style={{ fontSize: 18, color: "#94A3B8", maxWidth: 600, lineHeight: 1.6, marginBottom: 32 }}>
            Verdad Score assigns every news outlet a real-time credibility score —
            powered by a 4-layer AI verification engine that never sleeps.
          </div>
          <div style={{ display: "flex", gap: 12 }}>
            <div style={{
              background: T.blue, color: "#fff", padding: "12px 28px",
              borderRadius: 6, fontSize: 13, fontWeight: 600, letterSpacing: "0.04em",
            }}>REQUEST FULL DECK →</div>
            <div style={{
              border: `1px solid ${T.border}`, color: T.text, padding: "12px 28px",
              borderRadius: 6, fontSize: 13, fontWeight: 600, letterSpacing: "0.04em",
            }}>EXPLORE LIVE DATA ↓</div>
          </div>
        </div>
      </div>

      {/* METRICS */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 12, marginBottom: 24 }}>
        {metrics.map(m => (
          <Card key={m.label} style={{ padding: "24px", textAlign: "center" }}>
            <div style={{ fontSize: 28, marginBottom: 4, color: T.blue }}>{m.icon}</div>
            <div style={{
              fontFamily: "'Bebas Neue', sans-serif", fontSize: 42,
              letterSpacing: "0.04em", color: T.text, lineHeight: 1,
            }}>{m.value}</div>
            <Mono style={{ fontSize: 9, color: T.muted, letterSpacing: "0.12em", display: "block", marginTop: 6 }}>
              {m.label}
            </Mono>
          </Card>
        ))}
      </div>

      {/* HOW IT WORKS */}
      <Card style={{ padding: 32, marginBottom: 24 }}>
        <Mono style={{ fontSize: 10, color: T.muted, letterSpacing: "0.15em", display: "block", marginBottom: 24 }}>
          HOW IT WORKS
        </Mono>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 16 }}>
          {steps.map((s, i) => (
            <div key={s.num} style={{ position: "relative" }}>
              {i < steps.length - 1 && (
                <div style={{
                  position: "absolute", top: 20, left: "60%", right: "-10%",
                  height: 1, background: `linear-gradient(90deg, ${s.color}60, transparent)`,
                }} />
              )}
              <div style={{
                fontFamily: "'Bebas Neue', sans-serif", fontSize: 48,
                color: s.color, opacity: 0.3, lineHeight: 1, marginBottom: 4,
              }}>{s.num}</div>
              <div style={{
                fontFamily: "'Bebas Neue', sans-serif", fontSize: 22,
                color: s.color, letterSpacing: "0.08em", marginBottom: 8,
              }}>{s.title}</div>
              <div style={{ fontSize: 12, color: "#94A3B8", lineHeight: 1.6 }}>{s.desc}</div>
            </div>
          ))}
        </div>
      </Card>

      {/* CASE STUDY */}
      <Card style={{ padding: 32, marginBottom: 24 }}>
        <Mono style={{ fontSize: 10, color: T.muted, letterSpacing: "0.15em", display: "block", marginBottom: 8 }}>
          LIVE CASE STUDY — THE CREDIBILITY SPECTRUM
        </Mono>
        <div style={{ fontSize: 13, color: "#94A3B8", marginBottom: 24 }}>
          Real scores from our live database. Updated every 6 hours.
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 16 }}>
          {comparisons.map(({ outlet, fallback, label, desc }) => {
            const o = outlet || fallback;
            const vci = o.avg_vci || o.base_vci || 0;
            const c = vciColor(vci);
            return (
              <div key={o.name} style={{
                background: T.surfaceAlt, border: `1px solid ${T.border}`,
                borderRadius: 8, padding: 20,
                borderTop: `3px solid ${c}`,
              }}>
                <Mono style={{ fontSize: 8, color: c, letterSpacing: "0.15em", display: "block", marginBottom: 8 }}>
                  {label}
                </Mono>
                <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 2 }}>{o.name}</div>
                <Mono style={{ fontSize: 9, color: T.muted, display: "block", marginBottom: 16 }}>
                  {o.country} · {o.status?.toUpperCase() || "TRACKED"}
                </Mono>
                <div style={{
                  fontFamily: "'Bebas Neue', sans-serif", fontSize: 52,
                  color: c, lineHeight: 1, marginBottom: 4,
                }}>{typeof vci === "number" ? vci.toFixed(1) : vci}</div>
                <Mono style={{ fontSize: 8, color: T.muted, letterSpacing: "0.1em", display: "block", marginBottom: 12 }}>
                  VCI SCORE / 100
                </Mono>
                {/* Score bar */}
                <div style={{ background: T.border, borderRadius: 2, height: 3, marginBottom: 12 }}>
                  <div style={{ width: `${vci}%`, background: c, height: 3, borderRadius: 2, transition: "width 1s ease" }} />
                </div>
                <div style={{ fontSize: 11, color: "#64748B", lineHeight: 1.5 }}>{desc}</div>
              </div>
            );
          })}
        </div>
      </Card>

      {/* MARKET OPPORTUNITY */}
      <div className="two-col-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 24 }}>
        <Card style={{ padding: 32 }}>
          <Mono style={{ fontSize: 10, color: T.muted, letterSpacing: "0.15em", display: "block", marginBottom: 20 }}>
            THE PROBLEM
          </Mono>
          {[
            { stat: "6.6B", desc: "Social media users exposed to misinformation daily" },
            { stat: "$78B", desc: "Annual economic damage from fake news globally" },
            { stat: "56%", desc: "Of people struggle to identify credible news sources" },
            { stat: "0",   desc: "Universal credibility standard exists today" },
          ].map(item => (
            <div key={item.stat} style={{ display: "flex", gap: 16, alignItems: "flex-start", marginBottom: 16 }}>
              <div style={{
                fontFamily: "'Bebas Neue', sans-serif", fontSize: 28,
                color: T.red, minWidth: 70, lineHeight: 1,
              }}>{item.stat}</div>
              <div style={{ fontSize: 12, color: "#94A3B8", lineHeight: 1.5, paddingTop: 4 }}>{item.desc}</div>
            </div>
          ))}
        </Card>
        <Card style={{ padding: 32 }}>
          <Mono style={{ fontSize: 10, color: T.muted, letterSpacing: "0.15em", display: "block", marginBottom: 20 }}>
            THE OPPORTUNITY
          </Mono>
          {[
            { stat: "API",  color: T.blue,   desc: "License VCI scores to social platforms, search engines, and browsers" },
            { stat: "B2B",  color: T.purple, desc: "Sell outlet credibility reports to media buyers, PR agencies, and advertisers" },
            { stat: "GOV",  color: T.green,  desc: "Supply credibility data to election commissions and regulatory bodies" },
            { stat: "SaaS", color: T.yellow, desc: "Consumer subscription for journalists, researchers, and fact-checkers" },
          ].map(item => (
            <div key={item.stat} style={{ display: "flex", gap: 16, alignItems: "flex-start", marginBottom: 16 }}>
              <div style={{
                fontFamily: "'Bebas Neue', sans-serif", fontSize: 28,
                color: item.color, minWidth: 70, lineHeight: 1,
              }}>{item.stat}</div>
              <div style={{ fontSize: 12, color: "#94A3B8", lineHeight: 1.5, paddingTop: 4 }}>{item.desc}</div>
            </div>
          ))}
        </Card>
      </div>

      {/* CTA */}
      <div style={{
        background: "linear-gradient(135deg, #1e3a5f 0%, #1a2d4a 100%)",
        border: `1px solid #2d4a6e`, borderRadius: 12,
        padding: "40px 48px", textAlign: "center",
      }}>
        <div style={{
          fontFamily: "'Bebas Neue', sans-serif", fontSize: 36,
          letterSpacing: "0.08em", marginBottom: 12,
        }}>INTERESTED IN VERDAD SCORE?</div>
        <div style={{ fontSize: 14, color: "#94A3B8", marginBottom: 24 }}>
          We are seeking seed investment to scale our data pipeline and build enterprise API infrastructure.
        </div>
        <Mono style={{ fontSize: 12, color: T.blue, letterSpacing: "0.08em" }}>
          contact@veritasprotocol.io · CONFIDENTIAL
        </Mono>
      </div>

    </div>
  );
}



// ── First Time Walkthrough ────────────────────────────────────────────────────
function FirstTimeWalkthrough({ onComplete, onSkip }) {
  const [step, setStep] = useState(0);

  const steps = [
    {
      title: "Welcome to Verdad Score",
      icon: "🛡",
      content: `Verdad Score was built after founder Matt Callcott-Stevens watched journalists give unchallenged platforms to information peddlers during a major South African policing scandal — allowing narratives to be pushed against honest officers doing their jobs. This platform exists to help you cut through falsehoods, clickbait, propaganda and biased news.`,
      cta: "Show me how it works →",
    },
    {
      title: "Scan any article",
      icon: "🔍",
      content: `Paste any article text directly into the scanner for an instant credibility report. You can also paste a URL — but if the site is paywalled or blocks automated access, paste the article text instead for the most accurate result.`,
      cta: "What does the score mean? →",
    },
    {
      title: "Understanding your VCI score",
      icon: "📊",
      content: `Every article gets a Verifiable Credibility Index (VCI) score from 0–100.\n\n82+ RELIABLE — Meets journalistic standards\n65–81 AT RISK — Some credibility concerns\nBelow 65 BLACKLISTED — Significant credibility issues\n\nThe score is built from framing analysis, source quality, fact-check signals and the outlet's track record.`,
      cta: "What else can I do? →",
    },
    {
      title: "Explore the full platform",
      icon: "🧭",
      content: `Beyond scanning articles, you can:\n\n• LEADERBOARD — See how 105 outlets rank by credibility\n• AUTHORS — Track individual journalist credibility\n• SOURCES — See which sources get cited and how reliably\n• REPORTS — Generate Narrative Intelligence Reports on any subject or person`,
      cta: "Scan my first article →",
    },
  ];

  const step_data = steps[step];
  const isLast = step === steps.length - 1;

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(6,12,23,0.92)",
      zIndex: 9999, display: "flex", alignItems: "center",
      justifyContent: "center", padding: 24,
    }}>
      <div style={{
        background: "#0D1521", border: "1px solid #1E293B",
        borderRadius: 12, maxWidth: 520, width: "100%",
        overflow: "hidden", boxShadow: "0 25px 60px rgba(0,0,0,0.6)",
      }}>
        {/* Progress bar */}
        <div style={{ height: 3, background: "#1E293B" }}>
          <div style={{
            height: "100%", background: "#3B82F6",
            width: `${((step + 1) / steps.length) * 100}%`,
            transition: "width 0.3s ease",
          }} />
        </div>

        {/* Header */}
        <div style={{
          padding: "28px 32px 0",
          display: "flex", justifyContent: "space-between", alignItems: "flex-start"
        }}>
          <div>
            <div style={{ fontSize: 32, marginBottom: 12 }}>{step_data.icon}</div>
            <div style={{ fontSize: 10, color: "#3B82F6", fontFamily: "monospace",
                          letterSpacing: "0.15em", marginBottom: 8 }}>
              STEP {step + 1} OF {steps.length}
            </div>
            <div style={{ fontSize: 20, fontWeight: 700, color: "#E2E8F0" }}>
              {step_data.title}
            </div>
          </div>
          <button onClick={onSkip} style={{
            background: "none", border: "none", color: "#475569",
            cursor: "pointer", fontSize: 12, fontFamily: "monospace",
            letterSpacing: "0.05em", padding: "4px 8px",
          }}>SKIP</button>
        </div>

        {/* Content */}
        <div style={{ padding: "20px 32px 28px" }}>
          <p style={{
            color: "#94A3B8", fontSize: 14, lineHeight: 1.8,
            whiteSpace: "pre-line", margin: "0 0 28px",
          }}>
            {step_data.content}
          </p>

          {/* Step dots */}
          <div style={{ display: "flex", gap: 6, marginBottom: 24, justifyContent: "center" }}>
            {steps.map((_, i) => (
              <div key={i} style={{
                width: i === step ? 20 : 6, height: 6,
                borderRadius: 3, transition: "all 0.3s ease",
                background: i === step ? "#3B82F6" : "#1E293B",
              }} />
            ))}
          </div>

          {/* CTA Button */}
          <button
            onClick={() => isLast ? onComplete() : setStep(s => s + 1)}
            style={{
              width: "100%", padding: "14px", background: "#3B82F6",
              border: "none", borderRadius: 6, color: "white",
              fontFamily: "monospace", fontSize: 12, letterSpacing: "0.08em",
              cursor: "pointer", fontWeight: 600,
            }}>
            {step_data.cta}
          </button>
        </div>
      </div>
    </div>
  );
}

function LoginPage({ onLogin }) {
  const [mode, setMode]         = useState("signin");
  const [email, setEmail]       = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState("");
  const [message, setMessage]   = useState("");

  const handleEmail = async () => {
    setLoading(true); setError(""); setMessage("");
    try {
      const data = mode === "signin"
        ? await supabase.auth.signInWithPassword(email, password)
        : await supabase.auth.signUp(email, password);
      if (data.error) {
        setError(data.error.message || "Authentication failed");
      } else if (data.access_token) {
        localStorage.setItem("vs_token", data.access_token);
        localStorage.setItem("vs_user", JSON.stringify(data.user));
        onLogin(data.user);
        // Fire-and-forget welcome email — non-blocking
        fetch(`${API_BASE}/user/welcome`, { method: "POST", headers: { "Authorization": `Bearer ${data.access_token}`, "Content-Type": "application/json" } }).catch(() => {});
        // Cache timezone + name for personalised emails — non-blocking
        try {
          const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
          const fullName = data.user?.user_metadata?.full_name || data.user?.user_metadata?.name || "";
          fetch(`${API_BASE}/profile/update`, {
            method: "POST",
            headers: { "Authorization": `Bearer ${data.access_token}`, "Content-Type": "application/json" },
            body: JSON.stringify({ timezone: tz, full_name: fullName }),
          }).catch(() => {});
        } catch(e) {}
      } else if (mode === "signup") {
        setMessage("Check your email to confirm your account.");
        // Save email to waitlist — fire and forget
        fetch(`${API_BASE}/waitlist`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email, source: "signup_gate" })
        }).catch(() => {});
      }
    } catch(e) { setError(e.message); }
    setLoading(false);
  };

  return (
    <div style={{ minHeight: "100vh", background: T.bg,
      display: "flex", alignItems: "center", justifyContent: "center",
      fontFamily: "'DM Sans', system-ui, sans-serif" }}>
      <div style={{ width: "100%", maxWidth: 400, padding: "0 24px" }}>

        {/* Brand */}
        <div style={{ textAlign: "center", marginBottom: 40 }}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 16 }}>
            <svg width="52" height="52" viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
              <g transform="translate(25,28)">
                <rect x="0"   y="62" width="14" height="48"  rx="7" fill="#16a34a" opacity="0.25"/>
                <rect x="22"  y="48" width="14" height="76"  rx="7" fill="#16a34a" opacity="0.45"/>
                <rect x="44"  y="54" width="14" height="64"  rx="7" fill="#16a34a" opacity="0.35"/>
                <rect x="66"  y="28" width="14" height="116" rx="7" fill="#16a34a" opacity="0.65"/>
                <rect x="88"  y="4"  width="14" height="164" rx="7" fill="#16a34a" opacity="1"/>
                <rect x="110" y="28" width="14" height="116" rx="7" fill="#16a34a" opacity="0.65"/>
                <rect x="132" y="54" width="14" height="64"  rx="7" fill="#16a34a" opacity="0.35"/>
                <rect x="154" y="48" width="14" height="76"  rx="7" fill="#16a34a" opacity="0.45"/>
                <rect x="176" y="62" width="14" height="48"  rx="7" fill="#16a34a" opacity="0.25"/>
              </g>
            </svg>
          </div>
          <Mono style={{ fontSize: 11, color: "#16a34a", letterSpacing: "0.15em",
            display: "block", marginBottom: 8 }}>VERDAD SCORE</Mono>
          <div style={{ fontSize: 22, fontWeight: 700, color: T.text, marginBottom: 6 }}>
            {mode === "signin" ? "Welcome back" : "Create account"}
          </div>
          <div style={{ fontSize: 13, color: T.muted }}>
            {mode === "signin" ? "Sign in to your intelligence dashboard" : "Start your free trial today"}
          </div>
        </div>

        {/* OAuth */}
        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 24 }}>
          {[
            { provider: "google", label: "Continue with Google", icon: "G" },
            { provider: "github", label: "Continue with GitHub",  icon: "⌥" },
          ].map(({ provider, label, icon }) => (
            <button key={provider}
              onClick={() => supabase.auth.signInWithOAuth(provider)}
              style={{ display: "flex", alignItems: "center", justifyContent: "center",
                gap: 10, padding: "12px 20px", background: T.surface,
                border: `1px solid ${T.border}`, borderRadius: 6,
                color: T.text, cursor: "pointer", fontSize: 14, fontWeight: 500,
                fontFamily: "'DM Sans'", transition: "border-color 0.2s" }}>
              <span style={{ fontWeight: 700, fontSize: 16 }}>{icon}</span>
              {label}
            </button>
          ))}
        </div>

        {/* Divider */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24 }}>
          <div style={{ flex: 1, height: 1, background: T.border }} />
          <Mono style={{ fontSize: 10, color: T.muted }}>OR</Mono>
          <div style={{ flex: 1, height: 1, background: T.border }} />
        </div>

        {/* Email/Password */}
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <input type="email" placeholder="your@email.com"
            value={email} onChange={e => setEmail(e.target.value)}
            style={{ background: T.surface, border: `1px solid ${T.border}`,
              borderRadius: 6, padding: "12px 16px", color: T.text,
              fontFamily: "'DM Sans'", fontSize: 14, outline: "none",
              width: "100%", boxSizing: "border-box" }} />
          <input type="password" placeholder="Password"
            value={password} onChange={e => setPassword(e.target.value)}
            onKeyDown={e => e.key === "Enter" && handleEmail()}
            style={{ background: T.surface, border: `1px solid ${T.border}`,
              borderRadius: 6, padding: "12px 16px", color: T.text,
              fontFamily: "'DM Sans'", fontSize: 14, outline: "none",
              width: "100%", boxSizing: "border-box" }} />
          {error && <div style={{ fontSize: 12, color: T.red,
            fontFamily: "'DM Mono'" }}>{error}</div>}
          {message && <div style={{ fontSize: 12, color: T.green,
            fontFamily: "'DM Mono'" }}>{message}</div>}
          <button onClick={handleEmail}
            disabled={loading || !email || !password}
            style={{ padding: "13px", background: T.blue, border: "none",
              borderRadius: 6, color: "white", fontFamily: "'DM Sans'",
              fontSize: 15, fontWeight: 600, cursor: "pointer",
              opacity: loading || !email || !password ? 0.6 : 1 }}>
            {loading ? "Please wait..." : mode === "signin" ? "Sign In" : "Create Account"}
          </button>
        </div>

        {/* Toggle */}
        <div style={{ textAlign: "center", marginTop: 24 }}>
          <span style={{ fontSize: 13, color: T.muted }}>
            {mode === "signin" ? "No account? " : "Already registered? "}
          </span>
          <button onClick={() => { setMode(mode === "signin" ? "signup" : "signin"); setError(""); }}
            style={{ background: "none", border: "none", color: T.blue,
              fontSize: 13, cursor: "pointer", fontWeight: 600 }}>
            {mode === "signin" ? "Sign up free" : "Sign in"}
          </button>
        </div>

        <div style={{ textAlign: "center", marginTop: 16 }}>
          <a href="https://verdadscore.com"
            style={{ fontSize: 12, color: T.muted, fontFamily: "'DM Mono'" }}>
            back to verdadscore.com
          </a>
        </div>
      </div>
    </div>
  );
}

export default function App() {
  const [tab, setTab]           = useState("Scan");
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [user, setUser]         = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen]           = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [globalArticleId, setGlobalArticleId]   = useState(null);
  const { sub, subLoading } = useSubscription(user?.id);
  const [scanResult, setScanResult] = useState(null);
  const [showWalkthrough, setShowWalkthrough] = useState(false);

  // PayFast — no client-side initialization needed

  // Open any article from anywhere in the app
  const openArticle = (id) => {
    setGlobalArticleId(id);
    setTab("Articles");
  };

  useEffect(() => {
    // Save ?article= and ?scan= params to sessionStorage before any login redirect clears it
    const urlParams = new URLSearchParams(window.location.search);
    const articleParam = urlParams.get("article");
    if (articleParam) {
      sessionStorage.setItem("vs_pending_article", articleParam);
    }
    const scanParam = urlParams.get("scan");
    if (scanParam) {
      sessionStorage.setItem("vs_pending_scan_id", scanParam);
    }
    // Handle OAuth callback
    const hash = new URLSearchParams(window.location.hash.slice(1));
    const accessToken = hash.get("access_token");
    if (accessToken) {
      fetch(`${SUPABASE_URL}/auth/v1/user`, {
        headers: { "apikey": SUPABASE_ANON, "Authorization": `Bearer ${accessToken}` }
      }).then(r => r.json()).then(u => {
        localStorage.setItem("vs_token", accessToken);
        localStorage.setItem("vs_user", JSON.stringify(u));
        setUser(u);
        // Fire-and-forget welcome email — non-blocking
        fetch(`${API_BASE}/user/welcome`, { method: "POST", headers: { "Authorization": `Bearer ${accessToken}`, "Content-Type": "application/json" } }).catch(() => {});
        // Cache timezone + name for personalised emails — non-blocking
        try {
          const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
          const fullName = u?.user_metadata?.full_name || u?.user_metadata?.name || "";
          fetch(`${API_BASE}/profile/update`, {
            method: "POST",
            headers: { "Authorization": `Bearer ${accessToken}`, "Content-Type": "application/json" },
            body: JSON.stringify({ timezone: tz, full_name: fullName }),
          }).catch(() => {});
        } catch(e) {}
        window.history.replaceState({}, "", window.location.pathname);
        // Restore pending article from email digest link
        const pendingArticle = sessionStorage.getItem("vs_pending_article");
        if (pendingArticle) {
          sessionStorage.removeItem("vs_pending_article");
          setGlobalArticleId(pendingArticle);
          setTab("Articles");
        }
        // Restore scan from weekly email link
        const pendingScanId = sessionStorage.getItem("vs_pending_scan_id");
        if (pendingScanId) {
          sessionStorage.removeItem("vs_pending_scan_id");
          fetch(`${API_BASE}/scan/history`, {
            headers: { "Authorization": `Bearer ${accessToken}` }
          }).then(r => r.json()).then(d => {
            const match = (d.history || []).find(s => s.id === pendingScanId);
            if (match && match.result_json) {
              setScanResult(match.result_json);
              setTab("Scan");
            }
          }).catch(() => {});
        }
        // Restore pending scan from landing page (survives OAuth redirect)
        const pending = sessionStorage.getItem("vs_pending_scan");
        if (pending) {
          try {
            setScanResult(JSON.parse(pending));
            setTab("Scan");
          } catch (_) {}
          sessionStorage.removeItem("vs_pending_scan");
        }
      }).finally(() => setAuthLoading(false));
      return;
    }
    // Check existing session
    const token = localStorage.getItem("vs_token");
    const savedUser = localStorage.getItem("vs_user");
    if (token && savedUser) {
      fetch(`${SUPABASE_URL}/auth/v1/user`, {
        headers: { "apikey": SUPABASE_ANON, "Authorization": `Bearer ${token}` }
      }).then(r => {
        if (r.ok) return r.json();
        throw new Error("expired");
      }).then(u => {
        setUser(u);
        // Restore pending article from email digest link
        const pendingArticle = sessionStorage.getItem("vs_pending_article");
        if (pendingArticle) {
          sessionStorage.removeItem("vs_pending_article");
          setGlobalArticleId(pendingArticle);
          setTab("Articles");
        }
        // Restore scan from weekly email link
        const pendingScanId = sessionStorage.getItem("vs_pending_scan_id");
        if (pendingScanId) {
          sessionStorage.removeItem("vs_pending_scan_id");
          fetch(`${API_BASE}/scan/history`, {
            headers: { "Authorization": `Bearer ${token}` }
          }).then(r => r.json()).then(d => {
            const match = (d.history || []).find(s => s.id === pendingScanId);
            if (match && match.result_json) {
              setScanResult(match.result_json);
              setTab("Scan");
            }
          }).catch(() => {});
        }
        // Restore pending scan from landing page (survives redirect)
        const pending = sessionStorage.getItem("vs_pending_scan");
        if (pending) {
          try {
            setScanResult(JSON.parse(pending));
            setTab("Scan");
          } catch (_) {}
          sessionStorage.removeItem("vs_pending_scan");
        }
      }).catch(() => {
        localStorage.removeItem("vs_token");
        localStorage.removeItem("vs_user");
      }).finally(() => setAuthLoading(false));
    } else {
      setAuthLoading(false);
    }
  }, []);

  // Show walkthrough on first login — fires when auth is fully complete
  useEffect(() => {
    if (!user || authLoading) return;
    const seen = localStorage.getItem("vs_walkthrough_done");
    if (!seen) setShowWalkthrough(true);
  }, [user, authLoading]);

  // Handle ?scan= param from weekly email links — fires when user is confirmed logged in
  useEffect(() => {
    if (!user) return;
    const pendingScanId = sessionStorage.getItem("vs_pending_scan_id");
    if (!pendingScanId) return;
    sessionStorage.removeItem("vs_pending_scan_id");
    const token = localStorage.getItem("vs_token");
    fetch(`${API_BASE}/scan/history`, {
      headers: { "Authorization": `Bearer ${token}` }
    }).then(r => r.json()).then(d => {
      const match = (d.history || []).find(s => s.id === pendingScanId);
      if (match && match.result_json) {
        setScanResult(match.result_json);
        setTab("Scan");
      }
    }).catch(() => {});
  }, [user]);

  if (authLoading || (user && subLoading)) return (
    <div style={{ minHeight: "100vh", background: T.bg,
      display: "flex", alignItems: "center", justifyContent: "center",
      flexDirection: "column", gap: 16 }}>
      <Spinner />
      <Mono style={{ fontSize: 10, color: T.muted, letterSpacing: "0.1em" }}>
        {authLoading ? "AUTHENTICATING..." : "LOADING ACCOUNT..."}
      </Mono>
    </div>
  );

  if (!user) return <LoginPage onLogin={u => {
    setUser(u);
    // Show walkthrough only if first time
    const seen = localStorage.getItem("vs_walkthrough_done");
    if (!seen) setShowWalkthrough(true);
  }} />;

  return (
    <div style={{ minHeight: "100vh", background: T.bg, color: T.text,
      fontFamily: "'DM Sans', system-ui, sans-serif" }}>
      {showUpgrade && <UpgradeModal onClose={() => setShowUpgrade(false)} />}
      {showWalkthrough && <FirstTimeWalkthrough
        onComplete={() => {
          localStorage.setItem("vs_walkthrough_done", "1");
          setShowWalkthrough(false);
          setTab("Scan");
        }}
        onSkip={() => {
          localStorage.setItem("vs_walkthrough_done", "1");
          setShowWalkthrough(false);
        }}
      />}
      {/* TRIAL BANNER */}
      {sub?.on_trial && sub?.trial_end && (() => {
        const trialEnd = new Date(sub.trial_end);
        const now      = new Date();
        const daysLeft = Math.ceil((trialEnd - now) / (1000 * 60 * 60 * 24));
        const urgent   = daysLeft <= 3;
        return (
          <div style={{
            background: urgent ? "#7c1d1d" : "#1a2a1a",
            borderBottom: `1px solid ${urgent ? "#ef4444" : "#22c55e"}`,
            padding: "8px 24px",
            display: "flex", alignItems: "center", justifyContent: "space-between",
            gap: 12,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 14 }}>{urgent ? "⚠️" : "🟢"}</span>
              <span style={{
                fontFamily: "'DM Mono'", fontSize: 11,
                color: urgent ? "#fca5a5" : "#86efac",
                letterSpacing: "0.05em",
              }}>
                {daysLeft > 0
                  ? `FREE TRIAL — ${daysLeft} DAY${daysLeft === 1 ? "" : "S"} REMAINING — Full professional access until ${trialEnd.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}`
                  : "YOUR TRIAL HAS EXPIRED — Upgrade to keep your access"
                }
              </span>
            </div>
            <button onClick={() => setShowUpgrade(true)} style={{
              background: urgent ? "#ef4444" : "#22c55e",
              border: "none", color: "#fff", borderRadius: 4,
              padding: "4px 14px", cursor: "pointer",
              fontFamily: "'DM Mono'", fontSize: 10,
              letterSpacing: "0.08em", whiteSpace: "nowrap",
            }}>UPGRADE NOW</button>
          </div>
        );
      })()}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Mono:wght@400;500&family=DM+Sans:wght@300;400;500;600;700&family=Bebas+Neue&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        html, body { margin: 0; padding: 0; overflow-x: hidden; }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-track { background: #0f172a; }
        ::-webkit-scrollbar-thumb { background: #1e293b; border-radius: 2px; }
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes fadeIn { from { opacity:0; transform:translateY(6px); } to { opacity:1; transform:none; } }
        select option { background: #0d1521; }
      `}</style>

      {/* TOPBAR */}
      <style>{`
        @media (min-width: 769px) {
          .hamburger-btn { display: none !important; }
          .mobile-sidebar-overlay { display: none !important; }
        }
        @media (max-width: 768px) {
          .desktop-sidebar { display: none !important; }
          .mobile-sidebar-open { display: flex !important; }

          /* KPI cards — 2x2 grid on mobile */
          .kpi-grid { grid-template-columns: repeat(2,1fr) !important; }

          /* All 2-column grids collapse to 1 column on mobile */
          .two-col-grid { grid-template-columns: 1fr !important; }

          /* 3-column grids collapse to 1 column on mobile */
          .three-col-grid { grid-template-columns: 1fr !important; }

          /* Article rows — stack on mobile */
          .article-row { grid-template-columns: 1fr !important; gap: 6px !important; padding: 12px !important; }
          .article-row-badges { display: flex; gap: 6px; flex-wrap: wrap; }

          /* Overview category columns — single column */
          .category-grid { grid-template-columns: 1fr !important; }

          /* Leaderboard — hide less important columns */
          .leaderboard-row { grid-template-columns: 28px 1fr 60px 80px !important; }
          .leaderboard-hide { display: none !important; }

          /* General padding reduction */
          .tab-content { padding: 12px !important; }

          /* Filters wrap nicely */
          .filter-bar { flex-wrap: wrap !important; gap: 6px !important; }

          /* Score ring smaller on mobile */
          .mobile-score { transform: scale(0.85); transform-origin: left center; }
        }
      `}</style>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "0 16px", height: 52, borderBottom: `1px solid ${T.border}`,
        background: T.surfaceAlt, position: "sticky", top: 0, zIndex: 100 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <button className="hamburger-btn" onClick={() => setSidebarOpen(o => !o)} style={{
            background: "transparent", border: `1px solid ${T.border}`,
            borderRadius: 4, padding: "4px 8px", cursor: "pointer",
            color: T.muted, fontSize: 16, lineHeight: 1,
          }}>☰</button>
          <svg width="26" height="26" viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
            <g transform="translate(25,28)">
              <rect x="0"   y="62" width="14" height="48"  rx="7" fill="#16a34a" opacity="0.25"/>
              <rect x="22"  y="48" width="14" height="76"  rx="7" fill="#16a34a" opacity="0.45"/>
              <rect x="44"  y="54" width="14" height="64"  rx="7" fill="#16a34a" opacity="0.35"/>
              <rect x="66"  y="28" width="14" height="116" rx="7" fill="#16a34a" opacity="0.65"/>
              <rect x="88"  y="4"  width="14" height="164" rx="7" fill="#16a34a" opacity="1"/>
              <rect x="110" y="28" width="14" height="116" rx="7" fill="#16a34a" opacity="0.65"/>
              <rect x="132" y="54" width="14" height="64"  rx="7" fill="#16a34a" opacity="0.35"/>
              <rect x="154" y="48" width="14" height="76"  rx="7" fill="#16a34a" opacity="0.45"/>
              <rect x="176" y="62" width="14" height="48"  rx="7" fill="#16a34a" opacity="0.25"/>
            </g>
          </svg>
          <span style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 17,
            letterSpacing: "0.12em", color: "#16a34a" }}>VERDAD SCORE</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ width: 6, height: 6, borderRadius: "50%", background: T.green,
            boxShadow: `0 0 6px ${T.green}` }} />
          <Mono style={{ fontSize: 8, color: T.muted }}>LIVE</Mono>
          {sub && (
            <Mono style={{ fontSize: 8,
              color: sub.tier === "free" ? T.muted : sub.tier === "reader" ? T.blue :
                     sub.tier === "analyst" ? T.green : T.yellow,
              background: sub.tier === "free" ? `${T.muted}15` : sub.tier === "reader" ? `${T.blue}15` :
                           sub.tier === "analyst" ? `${T.green}15` : `${T.yellow}15`,
              padding: "2px 6px", borderRadius: 3,
            }}>{sub.tier.toUpperCase()}</Mono>
          )}
          {sub?.tier === "free" && (
            <button onClick={() => setShowUpgrade(true)} style={{
              background: T.blue, border: "none", color: "#fff",
              borderRadius: 4, padding: "4px 10px", cursor: "pointer",
              fontFamily: "'DM Mono'", fontSize: 8,
            }}>UPGRADE</button>
          )}
        </div>
      </div>

      {/* SIDEBAR + CONTENT LAYOUT */}
      <div style={{ display: "flex", position: "relative" }}>

        {/* MOBILE OVERLAY */}
        {sidebarOpen && (
          <div className="mobile-sidebar-overlay"
            onClick={() => setSidebarOpen(false)} style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)",
            zIndex: 90,
          }} />
        )}

        {/* DESKTOP SIDEBAR — always visible, collapsible */}
        <div className="desktop-sidebar" style={{
          width: sidebarCollapsed ? 48 : 200,
          flexShrink: 0,
          background: T.surface,
          borderRight: `1px solid ${T.border}`,
          height: "calc(100vh - 52px)",
          position: "sticky", top: 52,
          overflowY: "auto",
          display: "flex", flexDirection: "column",
          transition: "width 0.2s ease",
          overflow: "hidden",
        }}>
          {/* Collapse toggle button */}
          <div style={{ padding: "8px 8px 0", display: "flex",
            justifyContent: sidebarCollapsed ? "center" : "flex-end" }}>
            <button onClick={() => setSidebarCollapsed(c => !c)} style={{
              background: "transparent", border: `1px solid ${T.border}`,
              borderRadius: 4, padding: "4px 7px", cursor: "pointer",
              color: T.muted, fontSize: 12, lineHeight: 1,
              transition: "all 0.2s",
            }} title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}>
              {sidebarCollapsed ? "»" : "«"}
            </button>
          </div>

          {/* Nav items */}
          <div style={{ flex: 1, padding: "12px 0" }}>
            {[
              { group: "TOOLS", tabs: ["Scan", "History", "Articles", "Leaderboard", "Authors", "Sources", "Reports"] },
              { group: "SETTINGS", tabs: ["Account", ...(user?.id === ADMIN_ID ? ["Blog", "Review"] : [])] },
            ].map(({ group, tabs: groupTabs }) => (
              <div key={group} style={{ marginBottom: 8 }}>
                {!sidebarCollapsed && (
                  <Mono style={{ fontSize: 8, color: T.muted, letterSpacing: "0.12em",
                    padding: "6px 16px 4px", display: "block" }}>{group}</Mono>
                )}
                {groupTabs.map(t => {
                  const tier = sub?.tier || "free";
                  const locked = !canAccessTab(t, tier);
                  const active = tab === t;
                  const icons = {
                    "Overview": "◎", "Narratives": "⊞", "Digest": "◈",
                    "Articles": "≡", "Leaderboard": "▲", "Sources": "◉", "Authors": "◷",
                    "Scan": "⌕", "Watchlist": "◬", "Map": "◫", "Simulator": "⊡", "YouTube": "▶",
                    "Account": "◯", "Review": "⊘", "Markets": "₿",
                  };
                  return (
                    <button key={t} onClick={() => { setTab(t); setSidebarOpen(false); }}
                      title={sidebarCollapsed ? t : ""}
                      style={{
                        width: "100%", textAlign: sidebarCollapsed ? "center" : "left",
                        background: active ? `${T.blue}15` : "transparent",
                        border: "none", borderLeft: active ? `2px solid ${T.blue}` : "2px solid transparent",
                        padding: sidebarCollapsed ? "10px 0" : "9px 16px",
                        cursor: "pointer",
                        fontFamily: "'DM Mono', monospace", fontSize: 10,
                        letterSpacing: "0.06em",
                        color: active ? T.blue : locked ? T.muted + "60" : T.muted,
                        display: "flex", alignItems: "center",
                        justifyContent: sidebarCollapsed ? "center" : "space-between",
                        transition: "all 0.15s",
                      }}>
                      {sidebarCollapsed ? (
                        <span style={{ fontSize: 14 }}>{icons[t] || "·"}</span>
                      ) : (
                        <>
                          <span>{t.toUpperCase()}</span>
                          {locked && <span style={{ fontSize: 10 }}>🔒</span>}
                        </>
                      )}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>

          {/* Sidebar footer */}
          <div style={{ padding: "12px 8px", borderTop: `1px solid ${T.border}` }}>
            {sub?.tier === "free" && !sidebarCollapsed && (
              <button onClick={() => setShowUpgrade(true)} style={{
                width: "100%", background: T.blue, border: "none",
                color: "white", borderRadius: 4, padding: "8px",
                cursor: "pointer", fontFamily: "'DM Mono'", fontSize: 9,
                letterSpacing: "0.08em", marginBottom: 8,
              }}>UPGRADE PLAN</button>
            )}
            {!sidebarCollapsed && (
              <Mono style={{ fontSize: 8, color: T.muted, display: "block",
                textAlign: "center" }}>
                {sub?.tier?.toUpperCase() || "FREE"} · VERDAD SCORE
              </Mono>
            )}
          </div>
        </div>

        {/* MOBILE SIDEBAR — slides in/out */}
        <div style={{
          position: "fixed", top: 52, left: 0, bottom: 0,
          zIndex: 95, width: 220,
          background: T.surface,
          borderRight: `1px solid ${T.border}`,
          overflowY: "auto",
          display: "flex", flexDirection: "column",
          transform: sidebarOpen ? "translateX(0)" : "translateX(-100%)",
          transition: "transform 0.25s ease",
        }}>
          {/* Nav items — same as desktop */}
          <div style={{ flex: 1, padding: "12px 0" }}>
            {[
              { group: "TOOLS", tabs: ["Scan", "History", "Articles", "Leaderboard", "Authors", "Sources", "Reports"] },
              { group: "SETTINGS", tabs: ["Account", ...(user?.id === ADMIN_ID ? ["Review"] : [])] },
            ].map(({ group, tabs: groupTabs }) => (
              <div key={group} style={{ marginBottom: 8 }}>
                <Mono style={{ fontSize: 8, color: T.muted, letterSpacing: "0.12em",
                  padding: "6px 16px 4px", display: "block" }}>{group}</Mono>
                {groupTabs.map(t => {
                  const tier = sub?.tier || "free";
                  const locked = !canAccessTab(t, tier);
                  const active = tab === t;
                  return (
                    <button key={t} onClick={() => { setTab(t); setSidebarOpen(false); }}
                      style={{
                        width: "100%", textAlign: "left",
                        background: active ? `${T.blue}15` : "transparent",
                        border: "none", borderLeft: active ? `2px solid ${T.blue}` : "2px solid transparent",
                        padding: "9px 16px", cursor: "pointer",
                        fontFamily: "'DM Mono', monospace", fontSize: 10,
                        letterSpacing: "0.06em",
                        color: active ? T.blue : locked ? T.muted + "60" : T.muted,
                        display: "flex", alignItems: "center", justifyContent: "space-between",
                        transition: "all 0.15s",
                      }}>
                      <span>{t.toUpperCase()}</span>
                      {locked && <span style={{ fontSize: 10 }}>🔒</span>}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
          <div style={{ padding: "12px 16px", borderTop: `1px solid ${T.border}` }}>
            {sub?.tier === "free" && (
              <button onClick={() => { setShowUpgrade(true); setSidebarOpen(false); }} style={{
                width: "100%", background: T.blue, border: "none",
                color: "white", borderRadius: 4, padding: "8px",
                cursor: "pointer", fontFamily: "'DM Mono'", fontSize: 9,
                letterSpacing: "0.08em", marginBottom: 8,
              }}>UPGRADE PLAN</button>
            )}
            <Mono style={{ fontSize: 8, color: T.muted, display: "block", textAlign: "center" }}>
              {sub?.tier?.toUpperCase() || "FREE"} · VERDAD SCORE
            </Mono>
          </div>
        </div>

        {/* MAIN CONTENT */}
        <div style={{ flex: 1, minWidth: 0, padding: "20px 16px",
          animation: "fadeIn 0.25s ease" }}>
          {(() => {
            const tier = sub?.tier || "free";
            const locked = !canAccessTab(tab, tier);
            if (locked) return <LockedTab tab={tab} tier={tier} onUpgrade={() => setShowUpgrade(true)} />;
            return null;
          })()}
          {tab === "Overview"    && canAccessTab("Overview",    sub?.tier||"free") && sub !== null && <OverviewTab user={user} sub={sub} onUpgrade={() => setShowUpgrade(true)} adminId={ADMIN_ID} onArticleClick={openArticle} />}
          {tab === "Leaderboard" && canAccessTab("Leaderboard", sub?.tier||"free") && <LeaderboardTab />}
          {tab === "Map"         && canAccessTab("Map",         sub?.tier||"free") && <MapTab />}
          {tab === "Articles"    && canAccessTab("Articles",    sub?.tier||"free") && <ArticlesTab initialArticleId={globalArticleId} onClose={() => setGlobalArticleId(null)} />}
          {tab === "Simulator"   && canAccessTab("Simulator",   sub?.tier||"free") && <SimulatorTab />}
          {tab === "Watchlist"   && canAccessTab("Watchlist",   sub?.tier||"free") && <WatchlistTab />}
          {tab === "Authors"     && canAccessTab("Authors",     sub?.tier||"free") && <AuthorsTab />}
          {tab === "YouTube"     && canAccessTab("YouTube",     sub?.tier||"free") && <YouTubeTab />}
          {tab === "Narratives"  && canAccessTab("Narratives",  sub?.tier||"free") && <NarrativesTab />}
          {tab === "Digest"      && canAccessTab("Digest",      sub?.tier||"free") && <DigestTab />}
          {tab === "Scan"        && canAccessTab("Scan",        sub?.tier||"free") && <ScanTab externalResult={scanResult} clearExternalResult={() => setScanResult(null)} />}
          {tab === "Sources"     && canAccessTab("Sources",     sub?.tier||"free") && <SourcesTab />}
          {tab === "Review"      && user?.id === ADMIN_ID && <ReviewTab />}
          {tab === "Blog"        && user?.id === ADMIN_ID && <BlogTab />}
        {tab === "Markets"     && canAccessTab("Markets", sub?.tier||"free") && <MarketsTab />}
          {tab === "History"  && <HistoryTab onSelect={(result) => { setScanResult(result); setTab("Scan"); }} />}
          {tab === "Reports"  && <ReportsTab />}
        {tab === "Account"     && <AccountTab user={user} sub={sub} onUpgrade={() => setShowUpgrade(true)} />}
        </div>
      </div>

      {/* FOOTER */}
      <div style={{ borderTop: `1px solid ${T.border}`, padding: "14px 24px",
        display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
        <Mono style={{ fontSize: 9, color: "#334155" }}>VERDAD SCORE v0.1 · CONFIDENTIAL</Mono>
        <div style={{ display: "flex", gap: 16 }}>
          <a href="https://verdadscore.com/legal#privacy" target="_blank" rel="noreferrer"
            style={{ fontSize: 9, color: "#334155", fontFamily: "monospace", textDecoration: "none" }}>PRIVACY</a>
          <a href="https://verdadscore.com/legal#tos" target="_blank" rel="noreferrer"
            style={{ fontSize: 9, color: "#334155", fontFamily: "monospace", textDecoration: "none" }}>TERMS</a>
          <a href="https://verdadscore.com/legal#refund" target="_blank" rel="noreferrer"
            style={{ fontSize: 9, color: "#334155", fontFamily: "monospace", textDecoration: "none" }}>REFUND</a>
        </div>
        <Mono style={{ fontSize: 9, color: "#334155" }}>Credit Score for the Information Age™</Mono>
      </div>
    </div>
  );
}
